import { costProvisionsEn, costProvisionsPtBr } from '@/lib/i18n/cost-provisions';
import { costServicesEn, costServicesPtBr } from '@/lib/i18n/cost-services';
import { revenueCyclesEn, revenueCyclesPtBr } from '@/lib/i18n/revenue-cycles';

export const billingPtBr = {
  ...costServicesPtBr,
  ...costProvisionsPtBr,
  ...revenueCyclesPtBr,
  list: {
    title: 'Serviços de faturamento',
    subtitle: 'Cadastre e gerencie produtos e serviços para faturamento',
    searchPlaceholder: 'Buscar serviços...',
    createButton: 'Novo serviço',
    stats: {
      services: 'serviços',
    },
    price: {
      usage: 'Preço por uso',
    },
    filters: {
      status: {
        label: 'Status',
        all: 'Todos',
        active: 'Ativo',
        inactive: 'Inativo',
        draft: 'Rascunho',
      },
    },
    tabs: {
      overview: 'Visão geral',
      active: 'Ativos',
      inactive: 'Inativos',
      draft: 'Rascunhos',
    },
  },
  form: {
    titleCreate: 'Novo serviço',
    // Proposed copy: the toast after a page save.
    proposedToasts: {
      created: 'Serviço criado com sucesso',
      updated: 'Serviço atualizado com sucesso',
      draftSaved: 'Rascunho do serviço salvo',
    },
    titleEdit: 'Editar serviço',
    sections: {
      entity: {
        title: 'Entidade',
        subtitle: 'Selecione a entidade do serviço',
      },
      serviceInfo: {
        title: 'Informações do serviço',
        subtitle: 'Dados principais do serviço',
      },
      pricing: {
        title: 'Precificação',
        subtitle: 'Modelo e regras de preço',
      },
      fixedPricing: {
        title: 'Preço base',
        subtitle: 'Valores base do contrato',
      },
      pricingLimits: {
        title: 'Limites de cobrança',
        subtitle: 'Travas mínimas e máximas da cobrança',
      },
      billingRevenue: {
        title: 'Faturamento & receita',
        subtitle: 'Momento de cobrança e reconhecimento contábil',
      },
      usagePricing: {
        title: 'Preço variável por uso',
        subtitle: 'Ative para cobrar por unidade consumida além (ou no lugar) da taxa base',
        modeCards: {
          PER_UNIT: 'Uma única tarifa por unidade',
          GRADUATED: 'Tarifas progressivas por faixa',
          VOLUME: 'Uma tarifa para todo o volume',
          STAIRSTEP: 'Valor fixo por faixa selecionada',
          PERCENT_OF_GTV: 'Percentual sobre o valor bruto transacionado',
        },
      },
      fiscal: {
        title: 'Configuração fiscal',
        subtitle: 'ISS e códigos fiscais',
      },
      ibs: {
        title: 'IBS/CBS - Reforma tributária',
        subtitle: 'Códigos exigidos na emissão de NFS-e (layout Reforma / NT-002)',
      },
      accounting: {
        title: 'Classificação contábil',
        subtitle: 'Contas e tags gerenciais',
      },
      cfdi: {
        title: 'CFDI (México)',
        subtitle: 'Chaves do SAT usadas na emissão do CFDI 4.0',
        claveProdServ: 'Chave de produto/serviço (SAT)',
        claveProdServHint: 'Chave do catálogo c_ClaveProdServ do SAT, usada no CFDI.',
        satUnitCode: 'Chave de unidade (SAT)',
        satUnitCodeHint: 'Chave do catálogo c_ClaveUnidad do SAT. Ex.: E48, unidade de serviço.',
      },
    },
    fields: {
      name: {
        label: 'Nome',
        hint: 'Campo obrigatório',
        placeholder: 'Ex: Assinatura mensal',
      },
      code: {
        label: 'Código',
        hint: 'Deixe vazio para gerar automaticamente',
        placeholder: 'SRV-001',
      },
      description: {
        label: 'Descrição',
        hint: 'Texto opcional para detalhar o serviço',
        placeholder: 'Descreva o serviço e sua aplicação...',
      },
      entity: {
        label: 'Entidade',
        placeholder: 'Selecione a entidade',
      },
      currency: {
        label: 'Moeda',
        placeholder: 'Ex: BRL',
        derivedHint:
          'Moeda comercial padrão do serviço. Pode ser diferente da moeda funcional da entidade.',
        unresolvedHint: 'Selecione uma moeda comercial suportada para o serviço.',
        noEntitySelected: 'Selecione uma entidade para ver a moeda',
        noEntitySelectedBlock: 'Selecione a entidade do serviço antes de salvar.',
        unresolvedBlock: 'Selecione uma moeda comercial suportada antes de salvar.',
      },
      billingMode: {
        label: 'Modo de faturamento',
        placeholder: 'Selecione o modo',
        options: {
          recurring: 'Recorrente',
          oneTime: 'Pontual',
        },
      },
      invoicing: {
        label: 'Faturamento',
        hint: 'Individual: cada cobrança é uma fatura com seu documento fiscal. Acumulado do período: as cobranças viram transações e o pré-faturamento junta tudo numa agregadora por parceiro, moeda e janela.',
        options: {
          individual: 'Individual (uma fatura por cobrança)',
          periodAggregate: 'Acumulado do período (transações → agregadora)',
        },
      },
      usageUnitName: {
        label: 'Unidade de uso',
        hint: 'Nome da unidade cobrada por uso (ex.: "assento", "GB", "chamada de API")',
        placeholder: 'Ex.: assento',
      },
      baseFee: {
        label: 'Taxa base',
        hint: 'Valor fixo cobrado todo período, mesmo sem nenhum uso. Ex.: R$ 200/mês de assinatura.',
      },
      includedUnits: {
        label: 'Unidades incluídas',
        hint: 'Unidades de uso incluídas na taxa base antes de cobranças variáveis',
      },
      minimumCommitment: {
        label: 'Comprometimento mínimo',
        hint: 'Faturamento mínimo por período, independente do consumo. Ex.: mínimo de R$ 500/mês mesmo usando pouco.',
      },
      cap: {
        label: 'Teto de cobrança',
        hint: 'Limite máximo por período. O uso acima disso não é cobrado. Ex.: teto de R$ 5.000/mês.',
      },
      floorPrice: {
        label: 'Preço piso',
        hint: 'Preço unitário mínimo, mesmo com desconto negociado. Ex.: nunca cobrar menos de R$ 2,00 por ticket.',
      },
      defaultStandaloneSellingPrice: {
        label: 'Preço de venda avulso padrão',
        hint: 'Preço de referência quando o serviço é vendido em pacote. Usado para ratear a receita entre os itens.',
      },
      calculationMode: {
        label: 'Modo de cálculo',
        hint: 'Como o uso variável é calculado',
        placeholder: 'Selecione o modo',
      },
      defaultBillingTiming: {
        label: 'Momento de cobrança padrão',
        hint: 'Antecipado = cobra no início do período; postecipado = cobra ao final, após o consumo.',
        placeholder: 'Selecione o momento',
        options: {
          advance: 'Antecipado',
          arrears: 'Postecipado',
        },
      },
      defaultRecognitionMethod: {
        label: 'Método de reconhecimento de receita padrão',
        hint: 'Quando a receita é contabilizada, no regime de competência, independentemente de quando o dinheiro entra.',
        placeholder: 'Selecione o método',
        options: {
          POINT_IN_TIME_AT_DELIVERY: 'No momento da entrega',
          STRAIGHT_LINE_OVER_TIME: 'Linear ao longo do tempo',
          USAGE_AS_CONSUMED: 'Conforme consumido',
          PROGRESS_MILESTONES: 'Por marcos de progresso',
        },
        optionDescriptions: {
          POINT_IN_TIME_AT_DELIVERY: 'Receita reconhecida de uma vez, na entrega.',
          STRAIGHT_LINE_OVER_TIME:
            'Distribuída igualmente pela duração do contrato. Ex.: anual = 1/12 por mês.',
          USAGE_AS_CONSUMED: 'Reconhecida à medida que o cliente usa.',
          PROGRESS_MILESTONES: 'Reconhecida ao atingir etapas definidas.',
        },
      },
      gtvRate: {
        label: 'Percentual sobre GTV',
        hint: 'A fee é este percentual do valor bruto transacionado (GTV) de cada transação. Ex.: 4,23.',
      },
      usageTiers: {
        label: 'Faixas de uso',
        empty: 'Nenhuma faixa adicionada',
        autoFillHint:
          'O "De" de cada faixa é preenchido automaticamente com base na faixa anterior. A última faixa sem limite superior cobre todo o excedente.',
        headers: {
          from: 'De',
          to: 'Até',
          unitPrice: 'Preço unitário',
          flatPrice: 'Preço da faixa',
          floorPrice: 'Piso',
        },
        placeholders: {
          from: '0',
          to: 'Sem limite',
        },
      },
      cnaeCode: {
        label: 'CNAE',
        placeholder: 'Digite para buscar',
      },
      serviceCode: {
        label: 'Serviço fiscal',
        placeholder: 'Digite para buscar',
      },
      externalProductId: {
        label: 'ID externo do produto',
        hint: 'Usado para casar imports externos com este serviço',
        placeholder: 'Ex.: PROD-123',
      },
      fiscalServiceTypeId: {
        label: 'Serviço fiscal (LC 116)',
        placeholder: 'Digite para buscar',
        optionalHint: 'Opcional. Quando selecionado, preenche retenções federais automaticamente.',
        clearLabel: 'Nenhum',
        clearAria: 'Limpar seleção do serviço fiscal',
      },
      municipalServiceCode: {
        label: 'Código municipal (NFS-e)',
        placeholder: 'Buscar código da prefeitura',
        hint: 'Código proprietário do município. A alíquota ISS pode ser preenchida automaticamente.',
        // Proposed copy, not yet approved by the client.
        proposedSelectMunicipalityFirst: 'Selecione o município do ISS primeiro.',
      },
      irrfNature: {
        label: 'Natureza IRRF',
        placeholder: 'Selecione a natureza',
        derivedHint:
          'Sempre editável. O serviço fiscal (LC 116) só sugere um padrão quando este campo está vazio.',
        options: {
          NONE: 'Sem retenção de IRRF',
          PROFESSIONAL_1_5: 'Serviços profissionais (1,5%)',
          COMMISSION_1_5: 'Comissões e corretagem (1,5%)',
          CLEANING_SECURITY_1_0: 'Limpeza, conservação e segurança (1,0%)',
        },
      },
      isLaborProvisionOverride: {
        label: 'Cessão de mão de obra / empreitada',
        hint: 'Quando ativo, aplica retenção de INSS (11%) nas notas fiscais deste serviço.',
        defaultYes: 'Padrão do tipo fiscal: Sim',
        defaultNo: 'Padrão do tipo fiscal: Não',
      },
      issRate: {
        label: 'Alíquota ISS',
      },
      issMunicipalityIbgeCode: {
        label: 'Município ISS',
        hint: 'Por padrão vem da configuração fiscal da empresa, mas pode ser alterado para este serviço.',
        placeholder: 'Selecione um município',
      },
      issWithheldByDefault: {
        label: 'ISS retido por padrão',
        placeholder: 'Selecione uma opção',
      },
      nfseIssTaxation: {
        label: 'Tratamento do ISS na NFS-e',
        hint: 'Classificação fiscal explícita. Alíquota zero não caracteriza exportação.',
        options: {
          TAXABLE: 'Operação tributável',
          IMMUNITY: 'Imunidade',
          EXPORT: 'Exportação de serviço',
          NON_INCIDENCE: 'Não incidência',
        },
      },
      serviceResultCountryCode: {
        label: 'País do resultado do serviço',
        hint: 'Na NFS-e municipal, este país também será enviado como local da prestação. Se forem países diferentes, revise a configuração antes de emitir.',
        placeholder: 'Selecione o país',
        loading: 'Carregando países...',
        unavailable: 'Não foi possível carregar os países. Tente novamente.',
      },
      nbsCode: {
        label: 'Classificação do serviço',
        placeholder: '1.0401.10',
        tooltip:
          'A NBS (Nomenclatura Brasileira de Serviços) classifica o tipo de serviço vendido, similar ao NCM para produtos. Ex.: 1.0401.10 - Serviços de TI',
      },
      cstIbsCbs: {
        label: 'Situação tributária',
        placeholder: 'Selecione o CST',
        tooltip:
          'Define como IBS/CBS se aplica a este item — tributação integral, isenta, suspensa, etc. Ex.: 010 — Tributação normal',
        options: {
          '000': '000 - Tributação integral',
          '010': '010 - Alíquotas uniformes setoriais',
          '011': '011 - Alíquotas uniformes reduzidas',
          '200': '200 - Alíquota reduzida',
          '220': '220 - Alíquota fixa',
          '221': '221 - Alíquota fixa proporcional',
          '222': '222 - Redução de base de cálculo',
          '400': '400 - Isenção',
          '410': '410 - Imunidade / não incidência',
          '510': '510 - Diferimento',
          '515': '515 - Diferimento + redução',
          '550': '550 - Suspensão',
          '620': '620 - Tributação monofásica',
          '800': '800 - Transferência de crédito',
          '810': '810 - Ajuste IBS ZFM',
          '811': '811 - Ajustes gerais',
          '820': '820 - Regime específico',
          '830': '830 - Exclusão de base de cálculo',
        },
      },
      cClassTrib: {
        label: 'Classificação tributária',
        placeholder: 'Informe a classificação',
        tooltip:
          'Agrupa itens por tratamento tributário na reforma. Ainda em definição pela RFB — campo avançado/opcional.',
      },
      cIndOp: {
        label: 'Tipo de operação',
        placeholder: 'Selecione o tipo',
        tooltip: 'Classifica a natureza do fornecimento conforme o catálogo vigente da NFS-e.',
        inactiveOption: 'Código não vigente; selecione outro',
        options: {
          '100501': '100501 - Bens imateriais e direitos, operação onerosa',
          '100502': '100502 - Bens imateriais e direitos, operação onerosa',
        },
      },
      ibsCbsBaseReductionPct: {
        label: 'Redução da base de cálculo',
        hint: 'Percentual de redução aplicável conforme LC 214/2025',
        tooltip: 'Desconto percentual na base tributável — alguns serviços têm alíquota reduzida.',
      },
      glRevenueAccountId: {
        label: 'Conta receita (GL)',
        placeholder: 'Selecione a conta',
      },
      managerialAccountId: {
        label: 'Conta gerencial',
        placeholder: 'Selecione a conta',
      },
      costCenterId: {
        label: 'Centro de custo',
        placeholder: 'Selecione o centro de custo',
      },
      segments: {
        label: 'Segmentos personalizados',
        hint: 'Selecione um valor por segmento',
        selectEntity: 'Selecione uma entidade para carregar segmentos.',
        empty: 'Nenhum segmento disponível.',
        placeholder: 'Selecione um valor',
        emptyValues: 'Nenhum valor disponível',
      },
      nfseDescriptionTemplate: {
        label: 'Template NFS-e',
        hint: 'Texto padrão para a nota fiscal de serviço',
        placeholder: 'Ex.: Serviços prestados para {{customer_name}} — vencimento {{due_date}}',
      },
      isActive: {
        label: 'Ativo',
      },
    },
  },
  nfseTemplateVariables: {
    inputHint: 'Digite {{ para inserir variáveis da fatura.',
    openCatalog: 'Ver catálogo de variáveis',
    dialogTitle: 'Variáveis do template NFS-e',
    dialogDescription:
      'Use placeholders no formato {{variavel}}. Eles serão substituídos pelos dados da fatura na emissão.',
    searchPlaceholder: 'Buscar variável...',
    autocompleteHeading: 'Variáveis',
    autocompleteEmpty: 'Nenhuma variável encontrada.',
    empty: 'Nenhuma variável corresponde à busca.',
    categories: {
      invoice: 'Fatura',
      customer: 'Cliente',
      amounts: 'Valores',
      withholding: 'Retenções',
      service: 'Serviço',
    },
    fields: {
      invoiceNumber: {
        label: 'Número da fatura',
        description: 'Campo Fatura (invoice_number).',
      },
      description: {
        label: 'Descrição da fatura',
        description: 'Texto descritivo da fatura.',
      },
      issueDate: {
        label: 'Data de emissão',
        description: 'Data de emissão da fatura.',
      },
      dueDate: {
        label: 'Data de vencimento',
        description: 'Data de vencimento da fatura.',
      },
      managerialDate: {
        label: 'Data gerencial',
        description: 'Data gerencial da fatura.',
      },
      competencyDate: {
        label: 'Data de competência',
        description: 'Data de competência na emissão da NF.',
      },
      externalRef: {
        label: 'Referência externa',
        description: 'Identificador externo (ex.: Kamino).',
      },
      lineDescriptions: {
        label: 'Descrições dos itens',
        description: 'Descrições das linhas da fatura, separadas por ponto e vírgula.',
      },
      entityName: {
        label: 'Nome da entidade',
        description: 'Razão social da entidade emissora.',
      },
      customerName: {
        label: 'Nome do cliente',
        description: 'Nome fantasia ou razão social do cliente.',
      },
      customerLegalName: {
        label: 'Razão social do cliente',
        description: 'Razão social do cliente.',
      },
      customerTradeName: {
        label: 'Nome fantasia do cliente',
        description: 'Nome fantasia do cliente.',
      },
      customerCode: {
        label: 'Código do cliente',
        description: 'Código interno do cliente.',
      },
      customerTaxId: {
        label: 'CNPJ/CPF do cliente',
        description: 'Documento fiscal do cliente.',
      },
      customerEmail: {
        label: 'E-mail do cliente',
        description: 'E-mail principal do cliente.',
      },
      grossAmount: {
        label: 'Valor bruto',
        description: 'Valor total bruto da fatura.',
      },
      netAmount: {
        label: 'Valor líquido',
        description: 'Valor líquido após retenções.',
      },
      withholdingTotal: {
        label: 'Total de retenções',
        description: 'Soma das retenções na fonte.',
      },
      withholdingIrrf: {
        label: 'Retenção IRRF',
        description: 'Valor retido de IRRF.',
      },
      withholdingPis: {
        label: 'Retenção PIS',
        description: 'Valor retido de PIS.',
      },
      withholdingCofins: {
        label: 'Retenção COFINS',
        description: 'Valor retido de COFINS.',
      },
      withholdingCsll: {
        label: 'Retenção CSLL',
        description: 'Valor retido de CSLL.',
      },
      withholdingCsrf: {
        label: 'Retenção CSRF (PIS + COFINS + CSLL)',
        description: 'Soma das retenções federais PIS, COFINS e CSLL (Lei 10.833).',
      },
      withholdingInss: {
        label: 'Retenção INSS',
        description: 'Valor retido de INSS.',
      },
      withholdingIss: {
        label: 'Retenção ISS',
        description: 'Valor retido de ISS.',
      },
      serviceCode: {
        label: 'Código do serviço',
        description: 'Código do serviço de faturamento vinculado.',
      },
    },
  },
  contractTemplates: {
    list: {
      title: 'Templates de contrato',
      subtitle: 'Pacotes reutilizáveis de serviços com termos padrão',
      searchPlaceholder: 'Buscar templates...',
      createButton: 'Novo template',
      tabs: {
        overview: 'Visão geral',
        active: 'Ativos',
        inactive: 'Inativos',
      },
      stats: {
        templates: 'templates',
      },
      loading: 'Carregando...',
      loadError: 'Não foi possível carregar os templates.',
      empty: 'Nenhum template encontrado.',
      itemCount: '{{count}} itens',
      activeFilters: 'Filtros ativos',
      clearAllFilters: 'Limpar filtros',
      createdAt: 'Criado em {{date}}',
      columns: {
        template: 'Template',
        frequency: 'Frequência',
        items: 'Itens',
        renewal: 'Renovação',
        paymentTerms: 'Pagamento',
        status: 'Status',
        currency: 'Moeda',
      },
      filterLabels: {
        search: 'Busca',
        status: 'Status',
      },
      status: {
        active: 'Ativo',
        inactive: 'Inativo',
      },
      duration: {
        months: '{{count}} mês/meses',
        openEnded: 'Sem duração padrão',
      },
      renewal: {
        manual: 'Manual',
        fixedPercentage: '{{value}}%',
        indexBased: 'Indexado por {{index}}',
      },
      paymentTerms: {
        days: '{{count}} dia(s)',
        defaultValue: 'Padrão',
      },
      actions: {
        edit: 'Editar template',
        more: 'Ações do template',
      },
      detail: {
        summary: 'Resumo',
        currency: 'Moeda',
        paymentTerms: 'Prazo de pagamento',
        autoRenewal: 'Renovação automática',
        renewalSection: 'Renovação',
        renewalDuration: 'Duração da renovação',
        renewalAdjustment: 'Reajuste',
        cancellationNotice: 'Aviso prévio',
        defaultValue: 'Padrão',
        items: 'Itens',
        serviceDefault: 'Padrão do serviço',
        editButton: 'Editar template',
        noDescription: 'Sem descrição',
        days: '{{count}} dia(s)',
        months: '{{count}} mês/meses',
      },
    },
    actions: {
      duplicate: {
        trigger: 'Duplicar',
        title: 'Duplicar template de contrato',
        description:
          'Crie uma cópia de "{{name}}" com os mesmos termos, serviços e padrões. O código será gerado automaticamente.',
        nameLabel: 'Novo nome',
        namePlaceholder: 'Ex: Template enterprise - variação',
        nameRequired: 'Informe um nome para o novo template.',
        confirm: 'Criar cópia',
        creating: 'Criando...',
        success: 'Template duplicado com sucesso',
      },
      inactivate: {
        trigger: 'Inativar',
        title: 'Inativar "{{name}}"?',
        description:
          'Este template ficará indisponível para contratos futuros. Contratos existentes criados a partir dele não serão alterados.',
        confirm: 'Inativar template',
        success: 'Template inativado com sucesso',
      },
      reactivate: {
        trigger: 'Reativar',
        success: 'Template reativado com sucesso',
      },
    },
    form: {
      titleNew: 'Novo template de contrato',
      titleEdit: 'Editar template de contrato',
      breadcrumbNew: 'Novo template',
      breadcrumbEdit: 'Editar template',
      steps: {
        info: 'Informações',
        terms: 'Termos & Renovação',
        services: 'Serviços',
      },
      inactiveBanner: {
        title: 'Template inativo',
        description:
          'Este template não está disponível para novos contratos. Contratos existentes criados a partir dele não são afetados.',
      },
      sections: {
        templateInfo: {
          title: 'Informações do template',
          subtitle: 'Cabeçalho e escopo',
        },
        defaultTerms: {
          title: 'Termos padrão',
          subtitle: 'Configurações comerciais padrão',
        },
        renewal: {
          title: 'Renovação',
          subtitle: 'Configurações de renovação automática e reajuste',
        },
        itemDefaults: {
          title: 'Padrões dos itens',
          subtitle: 'Política de reconhecimento aplicada aos itens criados a partir deste template',
        },
        serviceItems: {
          title: 'Itens de serviço',
          subtitle: 'Pacote de serviços do template',
        },
      },
      fields: {
        entity: 'Entidade',
        name: 'Nome',
        code: 'Código',
        codeHint: 'Deixe vazio para gerar automaticamente',
        description: 'Descrição interna',
        billingFrequency: 'Frequência de faturamento',
        revenueRecognitionModel: 'Reconhecimento de receita',
        settlementModel: 'Modelo de liquidação',
        billingDayOfMonth: 'Dia de faturamento',
        billingDayAnchor: 'Âncora do dia',
        billingDayAnchorHint:
          'Dia fixo usa o dia de faturamento; último dia e último dia útil ignoram-no. A agregadora e a fatura comum seguem a mesma âncora.',
        autoApproveTransactions: 'Aprovação das transações',
        autoApproveTransactionsHint:
          'Transações projetadas do relatório de liquidação nascem em rascunho. Por regra, são aprovadas na criação (reconhecidas e abertas) com a regra registrada no histórico.',
        itemInvoicing: 'Faturamento do item',
        itemInvoicingHint:
          'Herda do serviço por padrão. Individual: cada ocorrência vira fatura própria. Agregado: acumula em transações e a agregadora do ciclo as fatura.',
        defaultDurationMonths: 'Duração padrão (meses)',
        currency: 'Moeda',
        currencyHint: 'Moeda comercial padrão do template. Todos os seus itens usam esta moeda.',
        currencyNoEntity: 'Selecione uma entidade para ver a moeda',
        currencyUnresolved: 'Selecione uma moeda comercial suportada para o contrato.',
        paymentMethod: 'Método de pagamento',
        installmentCount: 'Número de parcelas',
        installmentCountHint:
          'Em quantas parcelas a fatura gerada será dividida. Deixe em branco (ou 1) para parcela única.',
        installmentIntervalDays: 'Intervalo entre parcelas (dias)',
        installmentIntervalDaysHint:
          'Dias entre os vencimentos de cada parcela. Só é considerado quando há mais de uma parcela.',
        paymentTermsDays: 'Prazo de pagamento (dias)',
        paymentTermsDaysHint:
          'Dias entre a emissão e o vencimento. Deixe em branco (ou 0) para vencer na data de emissão.',
        autoRenewal: 'Renovação automática',
        renewalDurationMonths: 'Duração da renovação (meses)',
        renewalAdjustmentType: 'Tipo de reajuste',
        renewalAdjustmentValue: 'Valor do reajuste (%)',
        renewalAdjustmentPercentage: 'Percentual do reajuste',
        renewalAdjustmentValueHint: 'Percentual a ser aplicado sobre o valor vigente',
        renewalIndex: 'Índice de reajuste',
        renewalIndexHint:
          'Selecione o índice previsto em contrato. Os valores oficiais são sincronizados diariamente do BCB (SGS).',
        renewalIndexOptions: {
          igpm: 'IGP-M',
          ipca: 'IPCA',
          inpc: 'INPC',
          selic: 'SELIC',
        },
        renewalAccumulatedVariationWindowMonths: 'Janela var. acumulada',
        renewalAccumulatedVariationWindowMonthsHint:
          'Quantidade de meses usados para calcular a variação acumulada do índice. Ex.: 12 considera os últimos 12 meses.',
        renewalPublicationLagDays: 'Defasagem índice',
        renewalPublicationLagDaysHint:
          'Dias entre a publicação do índice e sua aplicação na renovação',
        renewalFloorAtZero: 'Piso de reajuste em zero',
        cancellationNoticeDays: 'Aviso prévio de cancelamento (dias)',
        cancellationNoticeDaysHint: 'Dias de antecedência para notificação de cancelamento',
        defaultBillingTiming: 'Cobrança padrão',
        defaultBillingTimingHint:
          'Define se os itens criados por este template serão cobrados antes ou depois do período de serviço. Se ficar no padrão do serviço, cada serviço mantém sua própria configuração.',
        billingTimingOptions: {
          advance: 'Antecipado',
          arrears: 'Pós-pago',
        },
        defaultServicePeriodRule: 'Período de serviço',
        defaultServicePeriodRuleHint:
          'Define como o período de serviço será calculado nos itens do contrato: por mês calendário ou pela data de aniversário do contrato. Use "Não definido" para manter a regra do serviço.',
        servicePeriodRuleOptions: {
          calendarMonth: 'Mês calendário',
          anniversaryDate: 'Data de aniversário',
        },
        defaultRecognitionMethod: 'Reconhecimento de receita',
        defaultRecognitionMethodHint:
          'Define como a receita dos itens será reconhecida no contrato. Se usar o padrão do serviço, o método configurado em cada serviço prevalece.',
        recognitionMethodOptions: {
          pointInTimeAtDelivery: 'No momento da entrega',
          straightLineOverTime: 'Linear ao longo do tempo',
          usageAsConsumed: 'Conforme consumido',
          progressMilestones: 'Por marcos de progresso',
        },
        items: 'Itens',
        itemLabel: 'Item {{index}}',
        service: 'Serviço',
        serviceLabel: 'Serviço',
        serviceLabelTooltip: 'Serviço de faturamento a ser incluído no contrato',
        priceLabel: 'Preço',
        priceLabelTooltip: 'Preço unitário do serviço neste item',
        recurrenceLabel: 'Recorrência',
        recurrenceLabelTooltip: 'Período em que o serviço será incluído (Sempre = todos os meses)',
        defaultPricePlaceholder: 'Preço padrão',
        baseFeeHint: 'Taxa base: {{price}}',
        introductoryPrice: 'Preço introdutório',
        introductoryPriceToggle: 'Preço introdutório?',
        introductoryMonths: 'Meses introdutórios',
        includeForMonths: 'Incluir nos primeiros X meses',
        includeForMonthsPlaceholder: 'Sempre',
        recurrenceOptions: {
          always: 'Sempre',
          months1: '1 mês',
          months2: '2 meses',
          months3: '3 meses',
          months4: '4 meses',
          months5: '5 meses',
          months6: '6 meses',
          months7: '7 meses',
          months8: '8 meses',
          months9: '9 meses',
          months10: '10 meses',
          months11: '11 meses',
          months12: '12 meses',
        },
        quantityPlaceholder: 'Qtd',
      },
      placeholders: {
        name: 'Ex: Plano SaaS Anual',
        code: 'CTT-001',
        description: 'Termos padrão para contratos corporativos anuais.',
        select: 'Selecionar',
        durationMonths: '12',
        billingDayOfMonth: 'Ex: 10',
        currency: 'BRL',
        paymentMethod: 'Selecionar',
        installmentCount: 'Ex: 3',
        installmentIntervalDays: 'Ex: 30',
        paymentTermsDays: '10',
        renewalDurationMonths: 'Ex: 12',
        renewalAdjustmentValue: 'Ex: 5.00',
        renewalIndex: 'Ex: IGPM',
        renewalAccumulatedVariationWindowMonths: 'Ex: 12',
        renewalPublicationLagDays: 'Ex: 30',
        cancellationNoticeDays: 'Ex: 30',
        service: 'Selecione um serviço',
      },
      options: {
        monthly: 'Mensal',
        quarterly: 'Trimestral',
        semiannual: 'Semestral',
        annual: 'Anual',
        onFiscalDocument: 'Na emissão do documento fiscal',
        onSettlement: 'Na liquidação (relatório do adquirente)',
        settlementDirect: 'Direto',
        settlementCustodial: 'Custodial (bruto em trânsito, repasse como obrigação)',
        anchorFixedDay: 'Dia fixo',
        anchorLastDay: 'Último dia do mês',
        anchorLastBusinessDay: 'Último dia útil do mês',
        autoApproveByRule: 'Por regra, na criação',
        autoApproveManual: 'Manual (ficam em rascunho)',
        invoicingIndividual: 'Individual — fatura própria',
        invoicingPeriodAggregate: 'Agregado — transação da agregadora',
        useCustomerDefault: 'Usar padrão do cliente',
        useServiceDefault: 'Usar padrão do serviço',
        unset: 'Não definido',
        noService: 'Selecionar',
        renewalYes: 'Sim — renovar automaticamente',
        renewalNo: 'Não — renovação manual',
        adjustmentNone: 'Sem reajuste',
        adjustmentFixed: 'Percentual fixo',
        adjustmentIndex: 'Indexado (IGPM / IPCA)',
        floorAtZeroYes: 'Sim — travar reajuste negativo em zero',
        floorAtZeroNo: 'Não — permitir reajuste negativo',
      },
      actions: {
        addItem: 'Adicionar item',
        back: 'Voltar',
        continueTo: 'Continuar: {{step}}',
        save: 'Salvar',
        create: 'Criar template',
      },
      emptyItems: 'Nenhum item adicionado.',
      validation: {
        entityRequired: 'Entidade é obrigatória',
        nameRequired: 'Nome é obrigatório',
        atLeastOneItem: 'É necessário ao menos um item de serviço',
        allItemsNeedService: 'Todos os itens devem ter um serviço selecionado',
        introductoryTermsRequired:
          'Preencha o preço introdutório e os meses introdutórios para cada item com preço introdutório ativo',
        durationRequired: 'Duração padrão é obrigatória',
        durationTwoDigits: 'Duração padrão deve ser um número com até 2 dígitos',
        billingDayRequired: 'Dia de faturamento é obrigatório',
        billingDayTwoDigits: 'Dia de faturamento deve ser um número com até 2 dígitos',
        billingDayInvalid: 'Dia de faturamento deve estar entre 1 e 28',
        paymentMethodRequired: 'Método de pagamento é obrigatório',
        paymentTermsRequired: 'Prazo de pagamento é obrigatório',
        paymentTermsThreeDigits: 'Prazo de pagamento deve ser um número com até 3 dígitos',
        installmentCountInvalid: 'Número de parcelas deve ser um número inteiro positivo',
        installmentIntervalDaysInvalid:
          'Intervalo entre parcelas deve ser um número inteiro positivo',
        currencyUnresolved: 'Selecione uma moeda comercial suportada antes de continuar.',
        renewalDurationRequired:
          'Duração da renovação é obrigatória quando a renovação automática está ativa',
        renewalAdjustmentValueRequired:
          'Percentual do reajuste é obrigatório para o tipo "Percentual fixo"',
        renewalIndexRequired: 'Índice de reajuste é obrigatório para o tipo "Indexado"',
        cancellationNoticeDaysRequired: 'Aviso prévio de cancelamento é obrigatório',
      },
    },
  },
  contracts: {
    list: {
      title: 'Contratos',
      subtitle: 'Contratos comerciais com clientes',
      statusViews: 'Status dos contratos',
      searchLabel: 'Buscar contratos',
      searchPlaceholder: 'Buscar contratos...',
      createButton: 'Novo contrato',
      stats: { contracts: 'contratos' },
      tabs: {
        all: 'Todos',
        ACTIVE: 'Ativos',
        DRAFT: 'Rascunhos',
        SUSPENDED: 'Suspensos',
        EXPIRED: 'Expirados',
        CANCELLED: 'Cancelados',
      },
      filters: {
        customer: 'Filtrar cliente',
        contract: 'Filtrar contrato',
        startDate: 'Filtrar início',
        endDate: 'Filtrar fim',
        currency: 'Filtrar moeda',
        status: 'Filtrar por status',
        renewalMonths: 'Filtrar meses até a renovação',
        renewalMinMonths: 'Mínimo de meses',
        renewalMaxMonths: 'Máximo de meses',
        apply: 'Aplicar',
      },
      activeFilters: 'Filtros ativos:',
      clearAllFilters: 'Limpar todos',
      filterLabels: {
        search: 'Busca',
        customer: 'Cliente',
        contract: 'Contrato',
        startDate: 'Início',
        endDate: 'Fim',
        renewal: 'Vigência',
        status: 'Status',
        currency: 'Moeda',
      },
      columns: {
        customer: 'Cliente',
        contract: 'Contrato',
        startDate: 'Início',
        endDate: 'Fim',
        validity: 'Vigência',
        status: 'Status',
        renewal: 'Renovação',
        currency: 'Moeda',
      },
      // Shown wherever an end date would be. A contract without a determinable term is
      // missing information, never open-ended (ALC-1417).
      termUndetermined: 'Prazo não definido',
      validity: { start: 'Início:' },
      sort: { label: 'Ordenar por', startDate: 'Início da vigência' },
      confirmation: {
        activate:
          'O contrato {{code}} passa a vigorar e começa a gerar cobranças e reconhecimento de receita nos próximos ciclos.',
        suspend:
          'A cobrança do contrato {{code}} para e os lançamentos de receita ainda não incorridos são cancelados. Os cronogramas permanecem ativos e a suspensão pode ser revertida com a reativação.',
        cancel:
          'O cancelamento do contrato {{code}} é definitivo. Confirme pela tela de detalhes do contrato, onde as consequências contábeis são apresentadas integralmente.',
        reactivate:
          'O contrato {{code}} volta a vigorar, os lançamentos ainda não incorridos são retomados e os ciclos passam a ser gerados novamente.',
      },
      confirmationTitle: {
        activate: 'Ativar contrato',
        suspend: 'Suspender contrato',
        cancel: 'Cancelar contrato',
        reactivate: 'Reativar contrato',
      },
      confirmationAction: {
        activate: 'Ativar',
        suspend: 'Suspender',
        cancel: 'Confirmar cancelamento',
        reactivate: 'Reativar',
      },
      renewal: {
        automatic: 'Renovação automática',
        manual: 'Renovação manual',
        months: 'meses',
        monthsLeft_zero: '{{count}} meses',
        monthsLeft_one: '{{count}} mês',
        monthsLeft_other: '{{count}} meses',
      },
      createdAt: 'Criado em {{date}}',
      pagination: {
        summary: '{{from}}–{{to}} de {{total}} contratos',
        previous: 'Anterior',
        next: 'Próxima',
      },
      loading: 'Carregando...',
      empty: 'Nenhum contrato encontrado.',
      emptyDescription: 'Os novos contratos aparecerão aqui após serem criados.',
      status: {
        DRAFT: 'Rascunho',
        ACTIVE: 'Ativo',
        SUSPENDED: 'Suspenso',
        CANCELLED: 'Cancelado',
        EXPIRED: 'Expirado',
      },
      detail: {
        startDate: 'Data de início',
        currency: 'Moeda',
        items: 'Itens',
        draftItems: 'Itens (rascunho)',
        currentVersion: 'Versão vigente',
        noVersion: 'Nenhuma versão vigente ainda',
        versionNumber: 'Versão {{number}}',
        effectiveFrom: 'Vigente desde {{date}}',
      },
      actions: {
        open: 'Abrir contrato',
        actionsFor: 'Ações para o contrato {{code}}',
        activate: 'Ativar',
        suspend: 'Suspender',
        reactivate: 'Reativar',
        cancel: 'Cancelar contrato',
        edit: 'Editar',
        confirmCancel: 'Tem certeza que deseja cancelar este contrato?',
      },
      syncNow: {
        triggerButton: 'Faturar',
        confirmTitle: 'Faturar contratos agora',
        confirmDescription:
          'Executa agora, sob demanda, a mesma geração de ciclos de faturamento que roda automaticamente às 04:00 para todos os contratos ativos. É uma operação em lote — pode levar alguns instantes e pode alterar várias faturas em rascunho.',
        running: 'Faturando…',
        confirm: 'Faturar',
        close: 'Fechar',
        runAgain: 'Executar novamente',
        summary: {
          evaluated: 'Contratos avaliados',
          generated: 'Gerados',
          generatedNote: '{{staled}} de {{generated}} marcado(s) como STALE',
          generatedIdempotentNote:
            'Os demais {{count}} contrato(s) já estavam em dia — geração idempotente, nenhuma ação necessária.',
          blocked: 'Bloqueados',
          staledSectionTitle: 'Faturas marcadas como STALE',
          staledWarning:
            'Estas faturas em rascunho já haviam sido revisadas e agora precisam de nova revisão — o dado de origem mudou desde a última geração.',
          staledDifferences: '{{count}} diferença(s)',
          blockedSectionTitle: 'Bloqueados por motivo',
          blockedReasonPendingUsage: 'Uso não finalizado (PENDING_USAGE)',
          contractLink: 'Abrir contrato',
          invoiceLink: 'Abrir fatura',
          empty: 'Nenhum ciclo novo gerado — todos os contratos ativos já estavam em dia.',
        },
      },
      revenueRecognitionRun: {
        triggerButton: 'Reconhecer receita',
        title: 'Rodar reconhecimento de receita em lote',
        description:
          'Reconhece, para a competência selecionada, toda entrada pendente cujo cronograma esteja ativo, em todos os contratos do tenant. Falhas são registradas por entrada — a execução nunca é interrompida por uma falha isolada.',
        monthLabel: 'Competência (mês)',
        monthFutureError: 'Não é possível selecionar uma competência futura.',
        run: 'Executar reconhecimento',
        running: 'Executando…',
        close: 'Fechar',
        runAgain: 'Executar novamente',
        summary: {
          total: 'Total processado',
          posted: 'Contabilizados',
          skipped: 'Ignorados',
          failed: 'Com falha',
          failedSectionTitle: 'Falhas por motivo',
          errorCodes: {
            CONTRACT_REVENUE_NO_SNAPSHOT:
              'Aguardando fatura ou uso finalizado: a receita só pode ser reconhecida depois que a fatura do período for contabilizada (ou, no faturamento por uso, o uso for finalizado)',
            CONTRACT_REVENUE_CURRENCY_UNSUPPORTED: 'Moeda não suportada',
            CONTRACT_REVENUE_POSTING_DATE_BEFORE_COMPETENCY:
              'Data de lançamento anterior à competência',
            CONTRACT_REVENUE_NO_BOOK_COVERAGE: 'Sem cobertura de livro contábil',
            CONTRACT_REVENUE_COA_VERSION_TRANSITION_UNSUPPORTED:
              'Transição de plano de contas não suportada',
            CONTRACT_REVENUE_COA_ASSIGNMENT_CHAIN_UNRESOLVED:
              'Histórico de atribuições do plano de contas não identifica a transição com segurança; revise as atribuições da entidade e do livro',
            CONTRACT_REVENUE_ROOT_CROSSWALK_UNMAPPED:
              'Conta raiz sem correspondência no mapa de versões do plano de contas',
            CONTRACT_REVENUE_ACCOUNT_CROSSWALK_UNMAPPED:
              'Conta de receita sem correspondência no mapa de versões do plano de contas',
            CONTRACT_REVENUE_ROOT_CROSSWALK_SLOT_CONFLICT:
              'Mapa de versões e slot do sistema divergem sobre a conta raiz',
            CONTRACT_REVENUE_ROOT_CROSSWALK_NATURAL_BALANCE_MISMATCH:
              'Natureza da conta muda entre as versões do plano de contas',
            CONTRACT_REVENUE_MULTIPLE_BALANCE_CARRIERS:
              'Mais de uma versão do plano de contas mantém o saldo do contrato; reconcilie as aberturas transportadas duplicadas',
            CONTRACT_REVENUE_COA_SCOPE_UNRESOLVED:
              'Não foi possível resolver o escopo contábil da data',
            CONTRACT_REVENUE_NO_ACTIVE_COA_VERSION:
              'Nenhuma versão de plano de contas vigente na data',
            CONTRACT_REVENUE_ACCOUNT_MAPPING_MISSING:
              'Contas de Ativo/Passivo de Contrato não configuradas',
            CONTRACT_REVENUE_USAGE_PERIOD_NOT_ELAPSED: 'Uso ainda não finalizado para o período',
            CONTRACT_REVENUE_ZERO_AMOUNT: 'Valor de reconhecimento zerado',
            CONTRACT_REVENUE_SCHEDULE_NOT_ACTIVE: 'Cronograma não está ativo',
            CONTRACT_REVENUE_ENTRY_NOT_PENDING: 'Entrada não está mais pendente',
            CONTRACT_REVENUE_ENTRY_NOT_FOUND: 'Entrada não encontrada',
            CONTRACT_ACCOUNT_CONTRACT_NOT_FOUND:
              'Contrato da conta de Ativo/Passivo de Contrato não encontrado; a conta não pode ser criada sem o código do contrato e o cliente',
            NO_OPEN_PERIOD: 'Sem período contábil aberto na data de lançamento',
            UNKNOWN: 'Motivo não informado',
          },
          errorCodeFallback: 'Código: {{code}}',
          contractLink: 'Abrir contrato',
          empty: 'Nenhuma entrada pendente para esta competência.',
        },
      },
    },
    details: {
      back: 'Voltar para contratos',
      startDate: 'Data de início',
      createdAt: 'Criado em',
      current: 'Vigente',
      historical: 'Histórica',
      loadingSection: 'Carregando...',
      openCustomerInNewTab: 'Abrir cliente em nova aba',
      openTemplateInNewTab: 'Abrir template em nova aba',
      openServiceInNewTab: 'Abrir serviço em nova aba',
      days: '{{count}} dia(s)',
      months: '{{count}} mês(es)',
      singleInstallment: 'Parcela única',
      installments: '{{count}} parcelas',
      installmentsWithInterval: '{{count}} parcelas a cada {{days}} dia(s)',
      versionItemCount: '{{count}} item(ns)',
      sections: {
        summary: 'Resumo',
        terms: 'Termos',
        renewalPreview: 'Prévia do reajuste',
        versionHistory: 'Histórico de versões',
        pricingExceptions: 'Exceções de preço',
        milestones: 'Marcos',
        usage: 'Uso',
        revenueSchedule: 'Reconhecimento de receita',
        recognitionEvidence: 'Evidências de conclusão',
      },
      fields: {
        customerCode: 'Código do cliente',
        template: 'Template de origem',
        updatedAt: 'Atualizado em',
        termDuration: 'Duração do prazo',
        endDate: 'Fim da vigência',
        nextRenewalDate: 'Próxima renovação',
        renewalAdjustment: 'Reajuste',
        renewalWindow: 'Janela do índice',
        renewalLag: 'Defasagem do índice',
        renewalFloorAtZero: 'Piso em zero',
        cancellationNotice: 'Aviso de cancelamento',
        billingFrequency: 'Frequência',
        billingDay: 'Dia de cobrança',
        revenueRecognition: 'Reconhecimento de receita',
        invoiceOrigin: 'Origem das faturas',
        settlementModel: 'Modelo de liquidação',
        autoApproveTransactions: 'Aprovação das transações',
        proRataFirstCycle: 'Pró-rata no 1º ciclo',
        paymentMethod: 'Método de pagamento',
        installments: 'Parcelamento',
        renewalPreviewAsOf: 'Data do próximo reajuste',
        renewalPreviewApplied: 'Reajuste aplicado',
        renewalPreviewAccumulated: 'Variação acumulada',
        renewalPreviewFloorApplied: 'Piso aplicado',
        renewalPreviewBlocked: 'Reajuste bloqueado',
        renewalPreviewBlockReason: 'Motivo',
        renewalPreviewIndexFallback: 'índice selecionado',
        renewalPreviewMissingObservationLabel: 'Dados do índice indisponíveis',
        renewalPreviewMissingObservationDescription:
          'O índice {{index}} não possui observações disponíveis para todo o período necessário ao cálculo do reajuste. Aguarde a publicação dos dados e tente novamente.',
        renewalPreviewNoEligibleObservationLabel: 'Dados do índice ainda não elegíveis',
        renewalPreviewNoEligibleObservationDescription:
          'Ainda não há uma observação do índice {{index}} elegível para o cálculo, considerando a defasagem configurada. Aguarde a publicação dos dados e tente novamente.',
        renewalPreviewMissingAdjustmentValueLabel: 'Configuração do reajuste incompleta',
        renewalPreviewMissingAdjustmentValueDescription:
          'Informe o percentual de reajuste do contrato e tente novamente.',
        renewalPreviewInvalidInputLabel: 'Configuração do reajuste inválida',
        renewalPreviewInvalidInputDescription:
          'Revise a configuração do reajuste e tente novamente.',
        renewalPreviewUnknownBlockReason:
          'Não foi possível calcular o reajuste com a configuração atual. Revise os dados e tente novamente.',
        renewalPreviewObservations: 'Observações usadas',
        renewalPreviewFormula: 'Fórmula',
        renewalPreviewLoading: 'Calculando reajuste…',
        renewalPreviewError: 'Não foi possível calcular o reajuste',
        renewalPreviewNone: 'Sem reajuste contratual',
        renewalPreviewObservationDate: 'Data de referência',
        renewalPreviewObservationValue: 'Valor (%)',
      },
      itemColumns: {
        service: 'Serviço',
        billablePrice: 'Preço cobrado',
        ssp: 'Preço independente',
        allocatedAmount: 'Valor alocado',
        billingTiming: 'Cobrança',
        servicePeriod: 'Período',
        recognition: 'Reconhecimento',
        actions: 'Ações',
      },
      versionColumns: {
        version: 'Versão',
        effectiveFrom: 'Vigente desde',
        createdAt: 'Criada em',
        items: 'Itens',
        state: 'Estado',
      },
      related: {
        service: 'Serviço',
        requestedPrice: 'Preço solicitado',
        floorPrice: 'Piso',
        reason: 'Motivo',
        approvedAt: 'Aprovado em',
        name: 'Nome',
        amount: 'Valor',
        status: 'Status',
        billedAt: 'Faturado em',
        period: 'Período',
        quantity: 'Quantidade',
        source: 'Origem',
        createdAt: 'Criado em',
        recognitionAmount: 'Valor a reconhecer',
        recognizedAt: 'Reconhecido em',
        finalizedAt: 'Finalizado em',
      },
      empty: {
        items: 'Nenhum item encontrado.',
        pricingExceptions: 'Nenhuma exceção de preço registrada.',
        milestones: 'Nenhum marco registrado.',
        usage: 'Nenhum uso registrado.',
        revenueSchedule: 'Nenhum cronograma de reconhecimento gerado.',
        recognitionEvidence: 'Nenhuma evidência de conclusão registrada.',
      },
      recognitionEvidence: {
        deliveryTitle: 'Registrar entrega/aceite',
        milestoneTitle: 'Registrar conclusão do marco',
        dialogDescription:
          'Registrar a evidência reconhece a receita de {{obligation}}. Informe quando a ' +
          'performance ocorreu e qual comprovação a sustenta.',
        evidenceDate: 'Data efetiva',
        evidenceDateHint:
          'Quando a entrega/conclusão ocorreu de fato — define a competência do reconhecimento. ' +
          'Pode ser retroativa, nunca futura.',
        reference: 'Comprovação',
        referencePlaceholder: 'Termo de aceite, relatório assinado, chamado…',
        referenceHint:
          'Obrigatória: um evento de receita sem comprovação verificável não é evidência.',
        notes: 'Observações',
        immutabilityHint:
          'A evidência é imutável. Uma correção exige estornar o reconhecimento e registrar ' +
          'nova evidência — o histórico nunca é editado.',
        submit: 'Registrar evidência',
        submitting: 'Registrando…',
        cancel: 'Cancelar',
        recordDelivery: 'Registrar entrega',
        recordCompletion: 'Registrar conclusão',
        delivered: 'Entrega registrada',
        completed: 'Concluído',
        recordSuccess: 'Evidência registrada e receita reconhecida.',
        recordedAt: 'Registrado em',
        kind: 'Tipo',
        kinds: {
          DELIVERY_ACCEPTANCE: 'Entrega/aceite',
          MILESTONE_COMPLETION: 'Conclusão de marco',
        },
      },
      revenueSchedule: {
        generate: 'Gerar cronograma',
        generating: 'Gerando…',
        generateSuccess: 'Cronograma de reconhecimento gerado.',
        total: 'Total',
        competency: 'Competência',
        issProvisioned: 'ISS provisionado',
        issProvisionedTooltip:
          'ISS provisionado sobre a receita já reconhecida (ganha) mas ainda não faturada (ALC-1259).',
        issProvisionedNone: 'Sem ISS provisionado nesta competência.',
        entryStatus: {
          PENDING: 'Pendente',
          POSTED: 'Contabilizado',
          REVERSED: 'Estornado',
          CANCELLED: 'Cancelado',
        },
        glPostingDate: 'Contabilizado em {{date}}',
        journals: 'Lançamentos',
        journalOriginal: 'Original',
        journalReversal: 'Estorno',
        journalReversed: 'Estornado',
        correctionOf: 'Correção da competência estornada',
        correctedBy: 'Corrigido por nova entrada',
        reversedAt: 'Estornado em {{date}}',
        actions: {
          recognize: 'Reconhecer',
          reverse: 'Estornar',
          correct: 'Corrigir',
        },
        awaiting: {
          invoice: 'Aguardando fatura',
          invoiceHint:
            'A receita deste período só pode ser reconhecida depois que a fatura for aprovada e contabilizada.',
          usage: 'Aguardando uso finalizado',
          usageHint:
            'A receita deste período só pode ser reconhecida depois que o uso do período for finalizado.',
        },
        recognize: {
          success: 'Receita reconhecida.',
        },
        reverse: {
          title: 'Estornar reconhecimento',
          description:
            'Um lançamento de estorno é gerado em cada livro do lançamento original. O histórico contabilizado é preservado — nada é apagado.',
          reasonLabel: 'Motivo do estorno',
          reasonPlaceholder: 'Ex.: serviço não entregue na competência',
          dateLabel: 'Data do estorno',
          dateHint:
            'Se o período estiver fechado, o estorno é deslocado para o próximo período aberto.',
          confirm: 'Estornar',
          success: 'Reconhecimento estornado em {{date}}.',
        },
        correct: {
          title: 'Corrigir reconhecimento estornado',
          description:
            'Cria uma nova entrada pendente para a mesma competência. Reconheça-a para contabilizar o valor corrigido.',
          amountLabel: 'Valor corrigido',
          amountHint: 'Use 1.200,00 ou 1200.00. Sem separador de milhar isolado (ex.: 1.200).',
          amountInvalid:
            'Valor inválido. Informe no formato 1.200,00 ou 1200.00, com no máximo 2 casas decimais.',
          reasonLabel: 'Motivo da correção',
          reasonPlaceholder: 'Ex.: valor conforme aditivo assinado',
          confirm: 'Agendar correção',
          success: 'Correção agendada para a competência {{period}}.',
        },
      },
      renewal: {
        fixedPercentage: '{{value}}% fixo',
        indexBased: 'Indexado por {{index}}',
      },
      billingTiming: {
        ADVANCE: 'Antecipado',
        ARREARS: 'Pós-pago',
      },
      servicePeriodRule: {
        CALENDAR_MONTH: 'Mês calendário',
        ANNIVERSARY_DATE: 'Data de aniversário',
      },
      recognitionMethod: {
        POINT_IN_TIME_AT_DELIVERY: 'Na entrega',
        STRAIGHT_LINE_OVER_TIME: 'Linear ao longo do tempo',
        USAGE_AS_CONSUMED: 'Conforme consumido',
        PROGRESS_MILESTONES: 'Por marcos',
      },
      milestoneStatus: {
        PENDING: 'Pendente',
        READY_TO_BILL: 'Pronto para faturar',
        BILLED: 'Faturado',
        BLOCKED: 'Bloqueado',
        CANCELLED: 'Cancelado',
      },
      milestones: {
        createButton: 'Novo marco',
        form: {
          createTitle: 'Novo marco',
          createDescription:
            'Marcos controlam faturamento e/ou reconhecimento de receita de forma independente ' +
            '— um marco pode faturar, reconhecer, ambos, ou nenhum dos dois.',
          name: 'Nome',
          namePlaceholder: 'Ex.: Entrega da fase 1',
          item: 'Item do contrato',
          itemNone: 'Nenhum item (marco de nível de contrato)',
          itemIneligibleSuffix: '(não reconhece por marco)',
          noItemsAvailable:
            'Este contrato ainda não tem itens vigentes — ative o contrato para vincular o ' +
            'marco a um item.',
          bills: 'Fatura ao concluir',
          billsHint:
            'Quando desativado, este marco nunca gera fatura — use para acompanhamento ou ' +
            'reconhecimento apenas.',
          amount: 'Valor de faturamento',
          recognitionAmount: 'Valor a reconhecer',
          recognitionEmptyHint:
            'Deixe em branco para um marco que não reconhece receita — não use zero.',
          recognitionNeedsItemHint: 'Selecione um item do contrato para poder reconhecer receita.',
          recognitionIneligibleHint:
            'Este item não reconhece receita marco a marco — sua receita já é reconhecida pelo ' +
            'próprio cronograma do item.',
          submit: 'Criar marco',
          submitting: 'Criando…',
          cancel: 'Cancelar',
          successCreate: 'Marco "{{name}}" criado.',
        },
        errors: {
          nonBillingAmount: 'Um marco que não fatura deve ter valor zero.',
          recognitionNonPositive:
            'O valor a reconhecer deve ser maior que zero — deixe em branco para um marco que ' +
            'não reconhece receita, nunca use zero.',
          recognitionMethodMismatch:
            'Este item não é reconhecido por marcos — remova o valor a reconhecer ou escolha ' +
            'outro item.',
          recognitionRequired:
            'Este item reconhece receita por marco; um marco que fatura precisa informar o ' +
            'valor a reconhecer.',
          amountDivergenceUnsupported:
            'Quando o marco fatura, o valor a reconhecer deve ser igual ao valor de faturamento.',
          recognitionWithoutItem: 'Um valor a reconhecer exige um item do contrato vinculado.',
        },
        ready: {
          action: 'Marcar pronto para faturar',
          confirmTitle: 'Marcar "{{name}}" pronto para faturar?',
          confirmDescription:
            'O marco entra no próximo ciclo de faturamento (ou na próxima execução de ' +
            '"Faturar"). Para marcos, faturamento e reconhecimento de receita ' +
            'acontecem juntos — a evidência de conclusão registrada depois é o que efetivamente ' +
            'reconhece a receita vinculada.',
          confirm: 'Marcar pronto',
          success: '"{{name}}" pronto para faturar.',
          notBillable:
            'Este marco está configurado para não faturar — sua conclusão é registrada por ' +
            'evidência, não pelo fluxo de faturamento.',
          notBillableHint: 'Não fatura — conclusão registrada por evidência',
          needsItemHint: 'Sem item vinculado — corrija o item antes de marcar pronto',
          contractNotActive:
            'Só é possível marcar marcos como prontos para faturar em contratos ativos.',
          milestoneNotPending:
            'Este marco não está mais pendente — atualize a página para ver o status atual.',
          noItem:
            'Este marco não tem item vinculado, então não há classificação de receita para ' +
            'faturar — corrija o item primeiro.',
        },
        itemCorrection: {
          action: 'Corrigir item',
          dialogTitle: 'Corrigir item de "{{name}}"',
          description:
            'Corrigir o item move o marco de volta para Pendente. Você precisará marcá-lo ' +
            'como pronto para faturar novamente — essa reautorização não acontece ' +
            'automaticamente.',
          submit: 'Corrigir item',
          success: 'Item de "{{name}}" corrigido — marco voltou para Pendente.',
          historyPrefix: 'Corrigido anteriormente de',
          reauthorizeHint:
            'Após salvar, marque o marco como pronto para faturar novamente quando desejar.',
        },
        allocations: {
          action: 'Corrigir alocações',
          title: 'Corrigir alocações de "{{name}}"',
          description:
            'Corrige o valor de faturamento e/ou o valor a reconhecer antes que o marco tenha ' +
            'sido faturado ou reconhecido. Toda correção fica registrada no histórico de ' +
            'auditoria.',
          submit: 'Salvar correção',
          success: 'Alocações de "{{name}}" corrigidas.',
          nonBillingHint: 'Este marco não fatura — o valor de faturamento permanece zero.',
          noItemHint: 'Sem item vinculado — este marco não pode reconhecer receita.',
        },
        runningTotal: {
          title: 'Total alocado ao item',
          itemAllocated: 'Consideração alocada ao item',
          alreadyAllocated: 'Já alocado em outros marcos',
          thisMilestone: 'Este marco',
          matched:
            'O total bate com a alocação do item — pronto para reconhecer receita quando concluído.',
          remainingPositive: 'Faltam {{amount}} para completar a alocação do item.',
          remainingNegative: 'Excede em {{amount}} a alocação do item.',
          ineligibleNote:
            'Este item não reconhece receita marco a marco — o valor a reconhecer não se ' +
            'aplica aqui.',
        },
      },
      pricingExceptions: {
        recordButton: 'Registrar exceção de preço',
        recordNewButton: 'Registrar nova exceção de preço',
        consumption: {
          CONSUMED: 'Consumida',
          PENDING: 'Pendente',
          REJECTED: 'Aditamento rejeitado',
        },
        driftWarning:
          'Preço atual {{price}} diverge do valor aprovado — esta exceção não cobre mais o preço vigente.',
        activationBlocked: {
          missing:
            'O preço de {{services}} está abaixo do piso do serviço e precisa de uma exceção de ' +
            'preço aprovada antes de ativar.',
          selfApproved:
            'A exceção de preço de {{services}} foi aprovada por você. Por segregação de funções, ' +
            'outro usuário autorizado precisa ativar o contrato.',
          priceDrift:
            'O preço de {{services}} mudou desde que a exceção de preço foi aprovada. Registre uma ' +
            'nova exceção para o preço atual.',
        },
        dialog: {
          title: 'Registrar exceção de preço',
          description:
            'Aprova um preço abaixo do piso comercial do serviço. Fica registrado em seu nome ' +
            'como aprovador(a).',
          contextLabel: 'Aplicar a',
          contextActivation: 'Ativação do contrato (itens em rascunho)',
          contextAmendment: 'Aditamento pendente — vigência {{date}}',
          noContextHint:
            'Não há itens em rascunho nem aditamentos pendentes para registrar uma exceção de preço.',
          serviceLabel: 'Serviço',
          serviceEmptyHint: 'Nenhum item deste contexto está abaixo do piso no momento.',
          requestedPriceLabel: 'Preço solicitado',
          floorPriceLabel: 'Piso do serviço',
          gapLabel: 'Abaixo do piso em',
          authorizationWarning:
            'Você está autorizando um preço comercial abaixo do piso do serviço. Esta aprovação ' +
            'fica registrada em seu nome e poderá ser usada para confirmar o preço na ativação ' +
            'ou na aprovação do aditamento.',
          reasonLabel: 'Motivo',
          reasonPlaceholder: 'Justifique por que este preço abaixo do piso deve ser autorizado…',
          submit: 'Aprovar exceção',
          submitting: 'Registrando…',
          cancel: 'Cancelar',
          success: 'Exceção de preço registrada para {{service}} ({{price}}).',
          errors: {
            duplicate:
              'Já existe uma exceção aprovada para este serviço neste contexto — não é possível ' +
              'registrar outra. Se o preço mudou, ajuste-o de volta ao valor aprovado ou peça a ' +
              'um administrador para revisar a exceção existente.',
            amendmentNotPending:
              'Este aditamento não está mais pendente — atualize a página para ver o status atual.',
            notBelowFloor:
              'Este item não está mais abaixo do piso do serviço — nenhuma exceção é necessária.',
          },
        },
      },
      usageSource: {
        MANUAL: 'Manual',
        CSV: 'CSV',
        API: 'API',
      },
      usageStatus: {
        OPEN: 'Aberto',
        FINALIZED: 'Finalizado',
        SUPERSEDED: 'Substituído',
      },
      usage: {
        createButton: 'Novo registro',
        chain: {
          finalizedAt: 'Finalizado em {{date}}',
          supersededAt: 'Substituído em {{date}}',
          correctsRecord: 'Corrige o registro {{id}}…',
        },
        form: {
          createTitle: 'Registrar uso',
          createDescription:
            'Registra manualmente o uso consumido em um item cobrado em ARREARS. O registro ' +
            'entra como Aberto — finalize-o para que ele passe a valer para o faturamento.',
          correctTitle: 'Corrigir uso',
          correctDescription:
            'Cria um novo registro de uso para o mesmo item e início de período. Ao finalizá-lo, ' +
            'ele substitui automaticamente o registro finalizado anterior — é assim que uma ' +
            'correção de uso funciona.',
          item: 'Item do contrato',
          itemPlaceholder: 'Selecione um item',
          itemIneligibleSuffix: '(não é cobrado em ARREARS)',
          noItemsAvailable:
            'Este contrato ainda não tem itens vigentes — ative o contrato para poder lançar uso.',
          noEligibleItems:
            'Nenhum item deste contrato é cobrado em ARREARS — uso só pode ser lançado para ' +
            'itens com esse momento de faturamento.',
          periodStart: 'Início do período',
          periodEnd: 'Fim do período',
          periodOrderInvalid: 'O fim do período não pode ser anterior ao início.',
          quantity: 'Quantidade',
          source: 'Origem',
          sourceManualHint:
            'O consumo lançado nesta tela é sempre registrado como manual. A origem de integração ' +
            'só pode ser atribuída pela própria integração autenticada, para que a trilha de ' +
            'auditoria não declare uma procedência que o sistema não comprovou.',
          externalId: 'ID externo',
          externalIdPlaceholder: 'Opcional — ex.: número da fatura de origem',
          externalIdHint:
            'Chave de idempotência única por (tenant, origem) — vale para todo o tenant, não só ' +
            'para este contrato. Reenviar o mesmo ID externo para este contrato é ignorado sem ' +
            'erro; para um contrato diferente, a API rejeita com conflito. Se você reaproveitou ' +
            'um número (ex.: de fatura) em mais de um contrato, use um ID diferente para cada um.',
          cancel: 'Cancelar',
          submit: 'Registrar uso',
          submitCorrection: 'Registrar correção',
          submitting: 'Enviando…',
          success: 'Uso registrado — está Aberto e ainda precisa ser finalizado.',
          errors: {
            externalIdConflict:
              'Este ID externo já foi usado para outro contrato — a chave de idempotência vale ' +
              'para todo o tenant, não só para este contrato. Use um ID diferente.',
            itemNotFound: 'Este item do contrato não foi encontrado — atualize a página.',
            notArrears: 'Uso só pode ser lançado para itens cobrados em ARREARS.',
            periodOrder: 'O fim do período não pode ser anterior ao início.',
          },
        },
        correction: {
          action: 'Corrigir',
          success: 'Correção registrada — finalize o novo registro para que ele passe a valer.',
        },
        finalize: {
          action: 'Finalizar uso',
          confirmTitle: 'Finalizar este registro de uso?',
          confirmDescription:
            'Finalizar substitui qualquer registro finalizado anterior do mesmo item e início de ' +
            'período (que passa a Substituído), dispara o reconhecimento de Contract Asset para ' +
            'obrigações reconhecidas conforme consumido, e reavalia o status de desatualização de ' +
            'faturas em rascunho afetadas — isso pode mover uma fatura que você esteja vendo.',
          confirm: 'Finalizar',
          success: 'Uso finalizado.',
          errors: {
            notFound: 'Este registro de uso não foi encontrado — atualize a página.',
            superseded:
              'Este registro já foi substituído por uma finalização mais recente e não pode ser ' +
              'finalizado novamente.',
            conflict:
              'Outra finalização concorrente para o mesmo período já está em andamento. ' +
              'Atualize a página e tente novamente.',
          },
        },
        import: {
          openButton: 'Importar CSV',
          title: 'Importar registros de uso via CSV',
          description:
            'Envia um arquivo CSV com uso em lote para itens cobrados em ARREARS. Linhas com ' +
            'problema não interrompem o restante — o resultado mostra o total importado e o que ' +
            'falhou, linha a linha.',
          warningHeader:
            'O cabeçalho deve ser exatamente: contractItemId,periodStart,periodEnd,quantity,externalId',
          warningNoQuoting:
            'O leitor do arquivo divide cada linha por vírgula sem suporte a aspas ou escape — ' +
            'uma vírgula dentro de um campo (ex.: em um texto livre) corrompe a linha inteira.',
          warningPartial:
            'A importação nunca é interrompida por uma linha com erro — cada linha é processada ' +
            'de forma independente.',
          downloadTemplate: 'Baixar modelo CSV',
          dropHint: 'Selecione um arquivo CSV para importar',
          selectFile: 'Selecionar arquivo',
          removeFile: 'Remover arquivo',
          submit: 'Importar',
          submitting: 'Importando…',
          cancel: 'Cancelar',
          close: 'Fechar',
          tryAnother: 'Importar outro arquivo',
          importedCount: 'Importados',
          failedCount: 'Com falha',
          errorsTitle: 'Linhas com falha',
          errorRow: 'Linha {{row}} do arquivo:',
        },
        pagination: {
          summary: '{{shown}} de {{total}} registros',
          loadMore: 'Carregar mais',
          loadingMore: 'Carregando…',
        },
      },
      actions: {
        activateTitle: 'Ativar contrato',
        activateDescription:
          'A ativação cria a primeira versão imutável a partir dos itens em rascunho.',
        activateSuccess: 'Contrato ativado.',
        activationFailure: {
          title: 'Não foi possível ativar o contrato',
          description: 'Corrija a configuração informada e tente ativar novamente.',
          stay: 'Continuar nesta tela',
          backToList: 'Voltar para a lista',
        },
        previewLoading: 'Calculando competências afetadas...',
        backdatedBillingPreview:
          '{{count}} ciclo(s) de faturamento já vencido(s) serão gerados após a confirmação.',
        backdatedRecognitionPreview:
          'Como a data de início do contrato já passou, temos {{count}} reconhecimento(s) de receita já vencido(s), serão processados pelo sistema após a confirmação.',
        previewBillingDate: 'Data de faturamento',
        previewCompetency: 'Competência',
        previewServicePeriod: 'Período de serviço',
        suspendTitle: 'Suspender contrato',
        suspendDescription: 'A suspensão pausa o contrato até que ele seja reativado.',
        suspendSuccess: 'Contrato suspenso.',
        reactivateTitle: 'Reativar contrato',
        reactivateDescription: 'A reativação move o contrato de volta para ativo.',
        reactivateSuccess: 'Contrato reativado.',
        cancelTitle: 'Cancelar contrato',
        cancelDescription: 'O cancelamento é definitivo e não pode ser desfeito.',
        cancelConsequenceRecognize:
          'Toda receita já ganha em competências encerradas, mas ainda pendente de contabilização, é reconhecida automaticamente antes do cancelamento — depois de cancelado não há mais como recuperá-la.',
        cancelConsequenceFuture:
          'A receita futura ainda não incorrida é cancelada e os cronogramas de reconhecimento são encerrados definitivamente — nada mais será gerado a partir deste contrato.',
        cancelConsequenceBilling: 'A cobrança deste contrato para imediatamente.',
        cancelSuspendContrast:
          'Diferente da suspensão — que cancela apenas os lançamentos ainda não incorridos, mantém os cronogramas ativos e pode ser revertida com a reativação —, o cancelamento é terminal.',
        cancelConfirm: 'Confirmar cancelamento',
        cancelSuccess:
          'Contrato cancelado. A receita já ganha foi reconhecida e a receita futura foi cancelada.',
        cancelUsageGuard: {
          description_one:
            'Há uso pendente em 1 serviço com base em uso. Será registrado zero para este:',
          description_other:
            'Há uso pendente em {{count}} serviços com base em uso. Será registrado zero para estes:',
          periodLabel: '{{start}} – {{end}}',
          viewUsageButton: 'Ver registros de uso',
        },
        cancelResolution: {
          title: 'Resolver pendências e cancelar',
          description:
            'Revise os ajustes abaixo. O uso será registrado como zero e o contrato será cancelado em seguida.',
          zeroUsageTitle: 'Registrar quantidade zero em {{count}} período(s) pendente(s)',
          zeroUsagePermission:
            'Você não possui permissão para registrar e finalizar o uso destes períodos.',
          reverseRevenueTitle: 'Estornar receita ainda não faturada:',
          reverseRevenueDescription:
            '{{count}} lançamento(s) sem qualquer valor reclassificado por fatura emitida. O estorno e o cancelamento serão feitos na mesma transação.',
          reverseRevenuePermission:
            'Você não possui permissão para estornar receita. O contrato pode ser cancelado sem este ajuste.',
          noUnissuedRevenue: 'Não há receita contabilizada e ainda não faturada para estornar.',
          confirm: 'Registrar zero e cancelar',
          success: 'Pendências resolvidas e contrato cancelado.',
        },
      },
    },
    activationErrors: {
      customerEntityMismatch:
        'O cliente selecionado não pertence à entidade do contrato. Escolha um cliente vinculado a esta entidade.',
      templateEntityMismatch:
        'O template selecionado não pertence à entidade do contrato. Escolha um template desta entidade.',
      backdatedConfirmation:
        'A ativação tem competências anteriores pendentes e precisa da sua confirmação explícita.',
      revenueAccountMappingMissing:
        'Não é possível ativar o contrato porque a versão ativa do Plano de Contas não possui as contas de sistema Ativo de Contrato e Passivo de Contrato. Configure as duas contas e tente novamente.',
      revenueAccountMappingAction: 'Configurar contas de sistema',
      custodialPayableUnmapped:
        'Um contrato custodial precisa da conta de sistema "Repasses a parceiros por pagar" mapeada no plano de contas da entidade. Mapeie-a antes de ativar.',
      custodialPayableAction: 'Mapear a conta de repasses',
      standaloneSellingPriceRequired:
        'A versão inicial do contrato precisa alocar a contraprestação por Preço de venda avulsa (SSP). Configure um SSP positivo para o serviço {{service}} antes de ativar.',
      meteredTemporaryItem:
        'Existe uma condição temporária em outro item deste contrato. No serviço medido {{service}}, mantenha Recorrência como Sempre se desejar, defina Cobrança padrão como Postecipado e configure o Reconhecimento de receita padrão do serviço como Conforme consumido.',
      draftOnly: 'Somente contratos em rascunho podem ser ativados.',
      startDateRequired: 'Informe a data de início antes de ativar o contrato.',
      termDurationRequired:
        'Informe uma duração válida para o prazo inicial antes de ativar o contrato.',
      itemsRequired: 'Adicione pelo menos um serviço ao contrato antes de ativá-lo.',
      functionalCurrencyUnresolved:
        'Não foi possível determinar a moeda funcional da entidade. Configure o livro contábil da entidade e tente novamente.',
      functionalCurrencyMismatch:
        'A moeda do contrato deve ser igual à moeda funcional da entidade.',
      serviceNotFound:
        'Um dos serviços do contrato não foi encontrado. Revise os itens e tente novamente.',
      serviceInactive:
        'Um dos serviços do contrato está inativo. Ative-o ou substitua-o antes de ativar o contrato.',
      serviceCurrencyMismatch: 'A moeda de um dos serviços não corresponde à moeda do contrato.',
      serviceFiscalMappingMissing:
        'Um dos serviços não possui classificação fiscal ou conta contábil de receita. Complete essa configuração antes de ativar.',
      meteredAdvanceBilling:
        'Um serviço medido não pode ser cobrado antecipadamente, pois o consumo só é conhecido após o período. Use cobrança pós-paga.',
      termEndRequired:
        'Não foi possível calcular o fim do prazo contratual. Revise a data de início e a duração.',
      timedServiceRecognition:
        'Itens com condição temporária exigem reconhecimento de receita linear ao longo do tempo.',
      timedTransitionInPast:
        'As condições temporárias gerariam uma transição anterior à data de ativação. Revise as durações ou a data de início.',
      introductoryTermsPairRequired:
        'Informe juntos o preço introdutório e a quantidade de meses introdutórios.',
      timedTermsExceedTerm:
        'A duração de uma condição temporária deve terminar antes do fim do prazo inicial do contrato.',
      emptyTimedVersion:
        'As condições temporárias não podem deixar uma versão do contrato sem serviços.',
      timedTermsMonthlyOnly:
        'Condições temporárias são permitidas apenas em contratos com cobrança mensal.',
      regularPriceInvalid: 'O preço regular de um serviço deve ser um valor não negativo.',
      introductoryPriceInvalid:
        'O preço introdutório deve ser não negativo e menor que o preço regular do serviço.',
      timedDurationInvalid:
        'A duração de uma condição temporária deve ser um número inteiro positivo de meses.',
      introductoryDurationInvalid:
        'A duração introdutória deve ser menor que a duração de inclusão do serviço.',
      pricingPermissionRequired:
        'Você não tem permissão para ativar contratos que exigem validação de exceção de preço.',
      billablePriceInvalid:
        'O preço cobrado de um dos serviços é inválido. Revise o valor e tente novamente.',
      pricingExceptionRequired:
        'Um preço está abaixo do piso do serviço e precisa de uma exceção de preço aprovada antes da ativação.',
      pricingExceptionSelfApproval:
        'A exceção de preço foi aprovada por você. Outro usuário autorizado deve ativar o contrato.',
      pricingExceptionPriceDrift:
        'O preço atual está abaixo do valor aprovado na exceção. Ajuste o preço ou solicite uma nova exceção.',
      mixedBillingTiming:
        'A alocação por SSP ainda não é compatível com serviços agrupados que têm momentos de cobrança diferentes.',
      mixedRecognition:
        'A alocação por SSP ainda não é compatível com serviços agrupados que misturam reconhecimento diferido e não diferido.',
      contractNotFound: 'Este contrato não foi encontrado. Atualize a página e tente novamente.',
      concurrentActivation:
        'O contrato foi alterado durante a ativação. Atualize a página para verificar o status antes de tentar novamente.',
      unknown: 'Não foi possível ativar o contrato. Revise os dados e tente novamente.',
    },
    form: {
      titleCreate: 'Novo contrato',
      // Proposed copy: the toast after a page save.
      proposedToasts: {
        createdAndActivated: 'Contrato criado e ativado com sucesso',
        draftSaved: 'Contrato salvo como rascunho',
        updated: 'Contrato atualizado com sucesso',
      },
      titleEdit: 'Editar contrato',
      fields: {
        entity: 'Entidade',
        customer: 'Cliente',
        responsibleUser: 'Responsável',
        responsibleUserPlaceholder: 'Selecione o responsável',
        code: 'Código',
        codeHint: 'Deixe vazio para gerar automaticamente',
        currency: 'Moeda',
        currencyHint: 'Moeda comercial do contrato. Todos os serviços adicionados usam esta moeda.',
        currencyNoEntity: 'Selecione uma entidade para ver a moeda',
        currencyUnresolved: 'Selecione uma moeda comercial suportada para o contrato.',
        startDate: 'Data de início',
        termDurationMonths: 'Duração (meses)',
        termDurationMonthsHint:
          'Duração do prazo contratual inicial. Obrigatória para ativar o contrato. Não confundir com a duração da renovação, que define o prazo seguinte.',
        paymentTermsDays: 'Prazo de pagamento (dias)',
        paymentTermsDaysHint:
          'Dias entre a emissão e o vencimento. Deixe em branco (ou 0) para vencer na data de emissão.',
        paymentTermsDaysPlaceholder: 'Ex: 30',
        billingFrequency: 'Frequência de faturamento',
        billingDayOfMonth: 'Dia de faturamento',
        billingDayAnchor: 'Âncora do dia',
        billingDayFromStartDate: 'Conforme data de início',
        paymentMethod: 'Método de pagamento',
        installmentCount: 'Número de parcelas',
        installmentCountHint: 'Cada fatura gerada é dividida neste número de parcelas.',
        installmentIntervalDays: 'Intervalo entre parcelas (dias)',
        installmentIntervalDaysHint:
          'Dias entre os vencimentos das parcelas. Só se aplica quando há mais de uma parcela.',
        notApplicable: 'Não se aplica',
        items: 'Itens',
        service: 'Serviço',
        billablePrice: 'Preço cobrado',
        regularPricePlaceholder: 'Preço regular',
        introductoryPricePlaceholder: 'Preço introdutório',
        introductoryMonthsPlaceholder: 'Meses introdutórios',
        includeForMonthsPlaceholder: 'Incluir por X meses',
        timedTermsHint:
          'As durações são períodos mensais de cobrança; um primeiro período proporcional conta como o período 1.',
      },
      termsDescription: 'Configurações de cobrança registradas para este contrato.',
      billingSummary: {
        title: 'Resumo de faturamento',
        fields: {
          frequency: 'Frequência',
          billingDay: 'Dia de faturamento',
          paymentMethod: 'Método de pagamento',
          dueDate: 'Vencimento',
          installments: 'Parcelamento',
        },
        values: {
          customerDefault: 'Padrão do cliente',
          billingDay: 'Dia {{day}}',
          startDate: 'Conforme data de início',
          paymentOnIssueDate: 'Na data de emissão',
          paymentTerms: '{{count}} dias após a emissão',
          singleInstallment: 'Parcela única',
          installments: '{{count}} parcelas',
          installmentsWithInterval: '{{count}} parcelas a cada {{days}} dias',
        },
        messages: {
          singleInstallment: 'Cada fatura será cobrada em uma única parcela.',
          multipleInstallments: 'Cada fatura será dividida em {{count}} parcelas.',
        },
        sequence: {
          title: 'Sequência de cobrança',
          invoice: 'Fatura recorrente',
          invoiceDetail: 'Emitida {{frequency}}mente.',
          firstInstallment: '1ª parcela',
          remainingInstallments: '2ª à {{count}}ª parcela',
          remainingInstallmentsDetail: 'A cada {{days}} dias.',
        },
      },
      actions: {
        addItem: 'Adicionar item',
        create: 'Criar contrato',
        save: 'Salvar',
        continue: 'Continuar',
      },
      createDecision: {
        title: 'Como deseja criar o contrato?',
        description: 'Você pode deixá-lo como rascunho para revisar depois ou ativá-lo agora.',
        saveDraft: 'Salvar rascunho',
        activate: 'Criar e ativar',
        cancel: 'Cancelar',
      },
      saveFailure: {
        title: 'Não foi possível salvar o contrato',
        stay: 'Continuar editando',
        backToList: 'Voltar para a lista',
      },
      activationFailure: {
        title: 'Não foi possível ativar o contrato',
        description: 'Corrija a configuração do item e tente ativar novamente.',
        backdatedDescription:
          'A ativação gerará {{billing}} ciclo(s) de cobrança e tratará {{recognition}} competência(s) de reconhecimento já iniciados. Confirme somente se deseja registrar esses efeitos agora.',
        stay: 'Continuar editando',
        backToList: 'Voltar para a lista',
        confirmBackdated: 'Confirmar ativação retroativa',
      },
      activationSuccess: {
        title: 'Contrato ativado',
        description: 'A primeira versão do contrato foi criada com sucesso.',
        backToList: 'Voltar para a lista',
        viewContract: 'Ver contrato',
      },
      steps: {
        info: 'Informações',
        services: 'Serviços',
        terms: 'Termos comerciais',
        renewal: 'Renovação',
      },
      emptyItems: 'Nenhum item adicionado.',
      validation: {
        entityRequired: 'Entidade é obrigatória',
        customerRequired: 'Cliente é obrigatório',
        atLeastOneItem: 'É necessário ao menos um item',
        allItemsNeedService: 'Todos os itens devem ter um serviço selecionado',
        introductoryTermsRequired:
          'Preencha o preço introdutório e os meses introdutórios para cada item com preço introdutório ativo',
        itemPriceUnresolved:
          'Não foi possível determinar o preço de um ou mais itens de serviço. Informe o preço manualmente antes de salvar.',
        currencyUnresolved: 'Selecione uma moeda comercial suportada antes de salvar.',
        paymentTermsInvalid: 'Prazo de pagamento deve ser um número de até 3 dígitos',
        billingDayInvalid: 'Dia de faturamento deve estar entre 1 e 28',
        installmentCountInvalid: 'Número de parcelas deve ser um número inteiro positivo',
        installmentIntervalInvalid: 'Intervalo entre parcelas deve ser um número inteiro positivo',
        installmentIntervalRequired:
          'Informe o intervalo entre parcelas quando houver mais de uma parcela',
        termDurationInvalid: 'Duração do prazo deve ser um número de até 3 dígitos',
        activationStartDateRequired: 'Data de início é obrigatória para ativar o contrato',
        activationTermDurationRequired: 'Duração (meses) é obrigatória para ativar o contrato',
        // Proposed copy, not yet approved by the client.
        proposedRequired: {
          entityId: 'Informe a entidade',
          customerId: 'Informe o cliente',
          startDate: 'Informe a data de início',
          termDurationMonths: 'Informe a duração',
          termDurationMonthsWithTemplate: 'Informe a duração quando o template não define uma',
          installmentIntervalDays:
            'Informe o intervalo entre parcelas quando há mais de uma parcela',
          renewalDurationMonths: 'Informe a duração da renovação',
          renewalAdjustmentValue: 'Informe o percentual do reajuste',
          renewalIndex: 'Informe o índice de reajuste',
          serviceId: 'Informe o serviço',
          introductoryPrice: 'Informe o preço introdutório',
          introductoryMonths: 'Informe os meses introdutórios',
        },
      },
      tooltips: {
        templateDerived: 'Derivado do template selecionado',
      },
      placeholders: {
        termDurationMonths: 'Ex: 12',
      },
    },
    fromTemplate: {
      title: 'Criar contrato a partir de template',
      subtitle: 'Selecione o cliente, o template e a data de início',
      createButton: 'Criar a partir de template',
      fields: {
        template: 'Template',
        templatePlaceholder: 'Selecione um template',
        templateOptionalPlaceholder: 'Nenhum template (contrato avulso)',
        customer: 'Cliente',
        startDate: 'Data de início',
        termDurationMonthsFromTemplate:
          'O template define {{count}} meses. Preencha apenas para usar um prazo diferente neste contrato.',
        termDurationMonthsRequired:
          'Este template não define uma duração padrão — informe o prazo, senão o contrato não poderá ser ativado.',
      },
      actions: {
        create: 'Criar contrato',
      },
      validation: {
        templateRequired: 'Template é obrigatório',
        customerRequired: 'Cliente é obrigatório',
        startDateRequired: 'Data de início é obrigatória',
        termDurationRequired:
          'Informe a duração do prazo: o template selecionado não define uma duração padrão.',
      },
    },
    amendments: {
      title: 'Alterações contratuais',
      newButton: 'Nova alteração',
      empty: 'Nenhuma alteração registrada.',
      dialogDescription:
        'Propõe uma alteração (upsell, downgrade, suspensão ou cancelamento) contra a versão vigente do contrato. A alteração só gera uma nova versão após aprovação.',
      status: {
        PENDING: 'Pendente',
        APPROVED: 'Aprovada',
        REJECTED: 'Rejeitada',
      },
      type: {
        UPSELL: 'Upsell',
        DOWNGRADE: 'Downgrade',
        SUSPENSION: 'Suspensão',
        CANCELLATION: 'Cancelamento',
        RENEWAL: 'Renovação',
      },
      fields: {
        amendmentType: 'Tipo de alteração',
        reason: 'Motivo',
        effectiveDate: 'Data de vigência',
        items: 'Itens propostos',
      },
      actions: {
        create: 'Propor alteração',
        cancel: 'Cancelar',
        approve: 'Aprovar',
        reject: 'Rejeitar',
      },
      confirmReject: 'Tem certeza que deseja rejeitar esta alteração?',
      propose: {
        title: 'Propor alteração contratual',
        reasonLabel: 'Motivo',
        reasonPlaceholder: 'Explique o motivo comercial desta alteração',
        reasonRequired: 'Informe o motivo da alteração.',
        effectiveDateLabel: 'Data de vigência',
        effectiveDateHint: 'Deve ser posterior à vigência da versão atual do contrato ({{date}}).',
        effectiveDateMustBeLater: 'A data de vigência deve ser posterior a {{date}}.',
        effectiveDateRequired: 'Informe a data de vigência.',
        itemsSectionTitle: 'Itens do contrato após a alteração',
        itemsSectionHint:
          'Esta lista substitui integralmente os itens do contrato — não é uma lista de diferenças. Remover uma linha remove esse serviço do contrato.',
        itemsRequired: 'A alteração precisa manter ao menos um item.',
        removedBadge: 'Removido',
        undoRemove: 'Desfazer remoção',
        removeItem: 'Remover',
        addItem: 'Adicionar item',
        addItemPlaceholder: 'Selecione um serviço',
        noAvailableServices: 'Não há outros serviços ativos disponíveis para adicionar.',
        priceLabel: 'Preço faturável',
        standaloneSellingPriceLabel: 'Preço de venda avulsa (SSP)',
        billingTimingLabel: 'Momento de faturamento',
        servicePeriodRuleLabel: 'Regra de período',
        recognitionMethodLabel: 'Método de reconhecimento',
        keepDefault: '(manter padrão)',
        priceRequired: 'Informe um preço válido (ex.: 1.200,00).',
        backdatedWarning:
          'Esta data de vigência já passou. A alteração retroagirá a receita para um período já fechado.',
        confirmBackdated: 'Confirmo a vigência retroativa desta alteração.',
        submit: 'Propor alteração',
        submitting: 'Enviando...',
        success: 'Alteração de {{type}} proposta com vigência em {{date}}. Aguardando aprovação.',
      },
      detail: {
        title: 'Detalhe da alteração',
        createdByLabel: 'Proposta por',
        createdAtLabel: 'Proposta em',
        decidedByLabel: 'Decidida por',
        decidedAtLabel: 'Decidida em',
        resultingVersionLabel: 'Nova versão do contrato',
        diffTitle: 'Comparação antes/depois',
        diffEmpty: 'Nenhuma diferença de itens registrada.',
        diffBefore: 'Antes',
        diffAfter: 'Depois',
        diffAdded: 'Item novo',
        diffRemoved: 'Item removido',
        diffChanged: 'Alterado',
        diffUnchanged: 'Sem alteração',
        notApplicable: 'Não existia',
        loadError: 'Não foi possível carregar o detalhe da alteração.',
        close: 'Fechar',
        back: 'Voltar',
      },
      approveConfirm: {
        title: 'Aprovar alteração contratual',
        description:
          'Ao aprovar, uma nova versão do contrato passa a valer a partir de {{date}}, e os cronogramas de receita são realocados prospectivamente a partir dessa data. Esta ação não pode ser desfeita.',
        backdatedWarning:
          'A data de vigência ({{date}}) já passou. A realocação de receita será retroativa a um período já encerrado.',
        confirmBackdated: 'Confirmo a aprovação com vigência retroativa.',
        confirm: 'Confirmar aprovação',
        submitting: 'Aprovando...',
        success: 'Alteração aprovada — nova versão vigente a partir de {{date}}.',
      },
      rejectConfirm: {
        title: 'Rejeitar alteração contratual',
        description:
          'Tem certeza que deseja rejeitar esta alteração? Nenhuma versão do contrato será criada.',
        confirm: 'Confirmar rejeição',
        submitting: 'Rejeitando...',
        success: 'Alteração rejeitada.',
      },
      errors: {
        selfApproval:
          'Esta alteração foi proposta por você. Por segregação de funções, outro usuário autorizado precisa aprová-la.',
        pricingPermissionRequired:
          'Para aprovar, o usuário também precisa da permissão de confirmar preços (ar:contract:pricing).',
        belowFloor:
          'O preço de {{services}} está abaixo do piso do serviço e precisa de uma isenção de preço aprovada.',
        selfApprovedException:
          'A isenção de preço de {{services}} foi aprovada por você. Por segregação de funções, outro usuário autorizado precisa aprovar essa isenção antes de confirmar o preço.',
        priceDrift:
          'O preço de {{services}} mudou desde que a isenção de preço foi aprovada. Registre uma nova isenção para o preço atual.',
        recordExceptionAction: 'Registrar exceção de preço',
        recordNewExceptionAction: 'Registrar nova exceção de preço',
        // Stands in for service names we cannot resolve (the services list only loads with
        // ar:service:read) — never fall back to the raw service id from the API message.
        unnamedServices: 'um ou mais serviços do contrato',
        staleStatus: 'Esta alteração já está com status "{{status}}". A lista foi atualizada.',
        cycleMidWindow:
          'A vigência em {{date}} cai dentro do período de faturamento {{start}}–{{end}}. Ajuste a data para o início de um ciclo de faturamento.',
        cyclePendingBilling:
          'Existe um ciclo de faturamento pendente ({{start}}–{{end}}) gerado pela versão anterior do contrato. Emita ou anule esse ciclo antes de aprovar.',
        backdatedConfirmationRequired: 'Confirme a vigência retroativa para prosseguir.',
        overFundedObligation:
          'Uma nota já emitida faturou o período de serviço que esta alteração encerraria, e a receita diferida dela ainda está no passivo de contrato. Estorne ou emita nota de crédito dessa fatura antes de alterar o contrato sobre esse período.',
        revenueAlreadyRecognized:
          'Parte da receita deste período de serviço já foi reconhecida no resultado. Reverta os reconhecimentos já lançados antes de prosseguir — cancelar agora retiraria receita de um período possivelmente fechado.',
        versionSuperseded:
          'O período de serviço pertence a outra versão do contrato: uma alteração passou a vigorar dentro dele. Um período dividido entre duas versões não pode ser faturado nem reconhecido como um só. Refature o ciclo pela versão vigente.',
        contractBusy:
          'Este contrato está sendo alterado ou reprocessado neste momento. Aguarde alguns instantes e tente novamente.',
        revenueAccountUnmapped:
          'As contas de Passivo de Contrato / Ativo de Contrato não estão mapeadas no plano de contas desta entidade, e sem elas a receita não pode ser diferida. Peça ao administrador do ERP para mapeá-las e repita a operação.',
        deferralLinkIncomplete:
          'Esta cobrança informa um ciclo do contrato cuja receita é reconhecida ao longo do período, mas os itens da fatura não estão vinculados a essa obrigação — emitir agora reconheceria tudo na data de emissão. Reenvie a cobrança para completar o vínculo e tente de novo.',
      },
    },
  },
  chat: {
    close: 'Fechar assistente',
    open: 'Abrir assistente',
    title: 'Assistente de serviços',
    thinking: 'Pensando...',
    ready: 'Online',
    placeholder: 'Descreva o serviço ou peça ajuda...',
    fieldsUpdated: 'Campos atualizados',
    fieldCount: '{{count}} campo(s)',
    quickPrompts: {
      saas: 'Cadastrar SaaS mensal',
      consulting: 'Consultoria por hora',
      usage: 'Precificação por uso',
      fiscalHelp: 'Ajuda com fiscal',
    },
  },
  summary: {
    title: 'Resumo do serviço',
    calculationMode: 'Modo de cálculo',
    entity: 'Entidade',
    baseFee: 'Taxa base',
    usageTiers: 'Faixas de uso',
    usageTiersCount: '{{count}} faixa(s)',
    usageTiersEmpty: 'Nenhuma faixa',
    issRate: 'ISS',
    issMunicipality: 'Município ISS',
    revenueAccount: 'Conta receita',
    nfsePreview: 'Prévia NFS-e',
  },
  simulator: {
    title: 'Simulador de preço',
    withQuantity: 'Simulação - {{quantity}} unidades',
    quantityLabel: 'Quantidade de uso',
    quantityPlaceholder: '0',
    enterQuantity: 'Informe uma quantidade para simular.',
    fixedFee: 'Valor fixo',
    usageFee: 'Valor variável',
    minimumApplied: 'Comprometimento mínimo aplicado',
    capApplied: 'Teto de cobrança aplicado',
    total: 'Total',
    tierApplied: 'Faixa aplicada: {{from}} - {{to}}',
    unlimited: 'Sem limite',
    noTier: 'Nenhuma faixa aplicável',
  },
  detail: {
    sections: {
      summary: 'Resumo',
      fiscal: 'Fiscal',
      accounting: 'Contábil',
      nfse: 'NFS-e',
    },
    nfse: {
      empty: 'Nenhuma descrição informada.',
    },
  },
  selectors: {
    municipality: 'Selecione um município',
    searchMunicipality: 'Buscar município...',
    searchMunicipalityHelp: 'Digite ao menos 2 caracteres para buscar na Focus NFe.',
    emptyMunicipalities: 'Nenhum município disponível',
    noMunicipalitiesFound: 'Nenhum município encontrado.',
    cnae: 'Selecione um CNAE',
    searchCnae: 'Digite para buscar...',
    noCnaesFound: 'Nenhum CNAE encontrado.',
    cnaeMinChars: 'Digite ao menos 2 caracteres para buscar.',
    municipalServiceCode: 'Selecione o código municipal',
    searchMunicipalServiceCode: 'Buscar por código ou descrição',
    municipalServiceCodeMinChars: 'Digite ao menos 2 caracteres para buscar.',
    noMunicipalServiceCodesFound: 'Nenhum código encontrado',
    loadMunicipalServiceCodesError: 'Erro ao carregar códigos municipais',
  },
  labels: {
    pricingShape: {
      fixed: 'Fixo',
      usage: 'Por uso',
      fixedPlusUsage: 'Base + uso',
      unconfigured: 'Não configurado',
    },
    calculationMode: {
      PER_UNIT: 'Por unidade',
      GRADUATED: 'Progressivo',
      VOLUME: 'Por volume',
      STAIRSTEP: 'Por faixa',
      PERCENT_OF_GTV: '% sobre GTV',
    },
    billingFrequency: {
      monthly: 'Mensal',
      quarterly: 'Trimestral',
      semiannual: 'Semestral',
      annual: 'Anual',
    },
    revenueRecognitionModel: {
      onFiscalDocument: 'Na emissão do documento fiscal',
      onSettlement: 'Na liquidação (relatório do adquirente)',
    },
    invoiceOrigin: {
      billingCycle: 'Uma fatura por ciclo, pela frequência',
      settlementAggregate:
        'Acumulado do período: transações → agregadora no pré-faturamento (sem fatura pela frequência)',
      mixed: 'Misto: itens individuais pelo ciclo, itens acumulados pela agregadora',
    },
    settlementModel: {
      direct: 'Direto',
      custodial: 'Custodial (bruto em trânsito, repasse como obrigação)',
    },
    billingDayAnchor: {
      fixedDay: 'Dia fixo',
      lastDay: 'Último dia do mês',
      lastBusinessDay: 'Último dia útil do mês',
    },
    autoApproveTransactions: {
      byRule: 'Por regra do contrato, na criação',
      manual: 'Manual — ficam em rascunho',
    },
    paymentMethod: {
      bolepix: 'Bolepix',
      pix: 'PIX',
      transfer: 'Transferência',
    },
    renewalAdjustmentType: {
      none: 'Sem reajuste',
      fixedPercentage: 'Percentual fixo',
      indexBased: 'Indexado',
    },
    yes: 'Sim',
    no: 'Não',
  },
  billingGroups: {
    list: {
      title: 'Grupos de cobrança',
      subtitle: 'Organize regras e preferências de cobrança',
      searchPlaceholder: 'Buscar grupos...',
      createButton: 'Novo grupo',
      loading: 'Carregando grupos...',
      errorTitle: 'Erro ao carregar grupos',
      errorDescription: 'Não foi possível carregar os grupos de cobrança.',
      retry: 'Tentar novamente',
      emptyTitle: 'Nenhum grupo encontrado',
      emptyDescription: 'Crie um grupo para definir regras de cobrança padrão.',
      noCode: 'Sem código',
      filters: {
        status: {
          label: 'Status',
          all: 'Todos',
          active: 'Ativos',
          inactive: 'Inativos',
        },
      },
      status: {
        active: 'Ativo',
        inactive: 'Inativo',
      },
    },
    form: {
      titleNew: 'Novo grupo de cobrança',
      titleEdit: 'Editar grupo de cobrança',
      subtitle: 'Defina regras e parâmetros de cobrança',
      sections: {
        general: {
          title: 'Informações gerais',
          description: 'Identificação e detalhes do grupo',
        },
        billing: {
          title: 'Regras de cobrança',
          description: 'Configurações padrão para faturamento',
        },
        status: {
          title: 'Status',
          description: 'Ative ou desative este grupo',
        },
      },
      fields: {
        name: {
          label: 'Nome',
          placeholder: 'Ex: Grupo padrão',
        },
        code: {
          label: 'Código',
          placeholder: 'BLG-001',
        },
        description: {
          label: 'Descrição',
          hint: 'Opcional',
          placeholder: 'Descreva quando usar este grupo...',
        },
        boletoInstructions: {
          label: 'Instruções de boleto',
          hint: 'Opcional',
          placeholder: 'Inclua instruções para pagamento...',
        },
        autoCollection: {
          label: 'Cobrança automática',
          hint: 'Ative para realizar cobranças automáticas.',
        },
        negativacao: {
          label: 'Negativação',
          hint: 'Habilita negativação automática.',
        },
        negativacaoDays: {
          label: 'Dias para negativação',
          hint: 'Número de dias após vencimento',
          placeholder: 'Ex: 30',
        },
        communicationChannels: {
          label: 'Canais de comunicação',
          hint: 'Separe por vírgula',
          placeholder: 'email, whatsapp',
        },
        isActive: {
          label: 'Grupo ativo',
          hint: 'Inativo deixa de aparecer nas seleções.',
        },
      },
    },
    detail: {
      empty: 'Selecione um grupo para ver os detalhes.',
      subtitle: 'Detalhes do grupo de cobrança',
      sections: {
        general: {
          title: 'Resumo',
          description: 'Informações principais do grupo',
        },
        billing: {
          title: 'Configuração de cobrança',
          description: 'Regras e canais configurados',
        },
      },
      fields: {
        code: 'Código',
        description: 'Descrição',
        boletoInstructions: 'Instruções de boleto',
        autoCollection: 'Cobrança automática',
        negativacao: 'Negativação',
        communicationChannels: 'Canais de comunicação',
      },
    },
    actions: {
      cancel: 'Cancelar',
      create: 'Criar grupo',
      saveChanges: 'Salvar alterações',
      edit: 'Editar',
      close: 'Fechar',
      deactivate: 'Desativar',
      deactivateConfirm: {
        title: 'Desativar "{{name}}"?',
        description:
          'O grupo deixa de aparecer nas seleções para novos clientes. Clientes já vinculados a ele permanecem associados.',
        confirm: 'Confirmar desativação',
      },
    },
    common: {
      notSet: 'Não definido',
      yes: 'Sim',
      no: 'Não',
    },
  },
  actions: {
    cancel: 'Cancelar',
    back: 'Voltar',
    create: 'Criar serviço',
    createActive: 'Criar e ativar',
    saveDraft: 'Salvar rascunho',
    saveChanges: 'Salvar alterações',
    activateService: 'Ativar serviço',
    edit: 'Editar',
    delete: 'Excluir',
    close: 'Fechar',
    addTier: 'Adicionar faixa',
    remove: 'Remover',
  },
  deactivate: {
    trigger: 'Desativar serviço',
    title: 'Desativar "{{name}}"?',
    description:
      'O serviço deixa de estar disponível para novos itens de contrato. Contratos que já usam este serviço não são afetados — isso é uma desativação, não uma exclusão permanente.',
    confirm: 'Confirmar desativação',
    success: 'Serviço desativado com sucesso.',
  },
  common: {
    saving: 'Salvando...',
    loading: 'Carregando...',
    noResults: 'Nenhum serviço encontrado.',
    notSet: 'Não definido',
    back: 'Voltar',
  },
  errors: {
    loadServices: 'Erro ao carregar serviços',
    loadMunicipalities: 'Erro ao carregar municípios',
    loadCnaes: 'Erro ao carregar CNAE',
  },
  saveErrorFields: {
    cnaeDescription: 'descrição do CNAE',
    municipalServiceDescription: 'descrição do código municipal',
    issMunicipalityName: 'nome do município do ISS',
    issMunicipalityState: 'UF do município do ISS',
    glRevenueAccountId: 'conta contábil de receita',
    managerialAccountId: 'conta gerencial',
    costCenterId: 'centro de custo',
    nfseDescriptionTemplate: 'descrição da NFS-e',
    segments: 'segmentos',
    status: 'status do serviço',
    classification: {
      root: 'classificação contábil',
      glAccounts: 'rateio das contas contábeis',
      departments: 'rateio dos departamentos',
      costCenters: 'rateio dos centros de custo',
      managerialAccounts: 'rateio das contas gerenciais',
      segments: 'rateio dos segmentos',
      customBreakdown: 'quebra personalizada',
    },
  },
  saveErrors: {
    unauthorized: 'Sua sessão expirou. Faça login novamente e tente salvar o serviço.',
    forbidden:
      'Você não tem permissão para salvar este serviço. Se precisar, peça acesso a um administrador.',
    notFound:
      'Este serviço não foi encontrado. Atualize a lista e tente novamente; ele pode ter sido removido ou alterado por outra pessoa.',
    genericConflict:
      'Encontramos um conflito ao salvar o serviço. Atualize a página, confira os dados e tente novamente.',
    genericInvalidRequest:
      'Não foi possível salvar o serviço. Revise os campos informados e tente novamente.',
    genericServerError:
      'Ocorreu um erro interno ao salvar o serviço. Tente novamente em alguns instantes; se continuar, acione o suporte.',
    genericServiceUnavailable:
      'O serviço está temporariamente indisponível. Aguarde alguns instantes e tente salvar novamente.',
    genericUnknown: 'Não foi possível salvar o serviço agora. Revise os dados e tente novamente.',
    duplicateCode:
      'Já existe um serviço com esse código. Use outro código ou deixe o campo em branco para gerar um automaticamente.',
    duplicateExternalProductId:
      'Esse ID externo já está vinculado a outro serviço da mesma entidade. Informe um ID diferente ou remova o valor.',
    entityNotFound:
      'A entidade selecionada não foi encontrada. Escolha outra entidade válida e tente novamente.',
    costCenterNotFound:
      'O centro de custo selecionado não pertence à entidade do serviço. Escolha um centro de custo da mesma entidade.',
    costCenterInactive:
      'O centro de custo selecionado está inativo. Escolha um centro de custo ativo e tente novamente.',
    managerialAccountNotFound:
      'A conta gerencial selecionada não pertence à entidade do serviço. Escolha uma conta gerencial da mesma entidade.',
    duplicateSegmentSelections:
      'Há segmentos repetidos na classificação. Remova a duplicidade e tente novamente.',
    segmentNotFound:
      'Um dos segmentos selecionados não foi encontrado para esta entidade. Revise a classificação e selecione um segmento válido.',
    segmentValueNotFound:
      'Um dos valores de segmento selecionados não foi encontrado. Revise a classificação e selecione um valor válido.',
    fiscalServiceTypeNotFound:
      'O serviço fiscal selecionado não existe mais ou está inativo. Escolha outro serviço fiscal ativo.',
    municipalityNotFound:
      'O município informado para o ISS não foi encontrado. Selecione novamente um município válido.',
    codeRequired:
      'O código do serviço não pode ficar vazio. Informe um código válido ou limpe o campo para gerar um automaticamente.',
    nameRequired: 'O nome do serviço é obrigatório. Preencha o nome e tente novamente.',
    invalidDecimal:
      'Um dos valores monetários informados está em formato inválido. Revise os números do serviço e tente novamente.',
    usageTiersRequireCalculationMode:
      'As faixas de uso precisam de um modo de cálculo. Escolha como o valor variável será calculado antes de salvar.',
    perUnitSingleTierOnly:
      'No modo por unidade, só é permitida uma única faixa. Remova as faixas extras e tente novamente.',
    usageTierRangeStartNegative:
      'A faixa de uso não pode começar com valor negativo. Corrija o início da faixa e tente novamente.',
    usageTierRangeEndInvalid:
      'O limite final de uma faixa precisa ser maior que o início. Ajuste os intervalos e tente novamente.',
    usageTierOnlyLastOpenEnded:
      'Somente a última faixa pode ficar sem limite final. Preencha os limites intermediários e tente novamente.',
    usageTierOverlap:
      'As faixas de uso estão sobrepostas. Ajuste os intervalos para que uma faixa não invada a outra.',
    stairstepRequiresFlatPrice:
      'Cada faixa do modo stair-step precisa de um preço fixo. Preencha o preço da faixa antes de salvar.',
    usageTierUnitPriceRequired:
      'As faixas do modo {{mode}} precisam de preço unitário. Preencha o valor por unidade em cada faixa.',
    usageTierFloorAbovePrice:
      'O preço piso de uma faixa não pode ser maior que o próprio preço da faixa. Ajuste os valores e tente novamente.',
    classificationGlRequired:
      'Informe pelo menos uma conta contábil na classificação do serviço antes de salvar.',
    classificationCustomBreakdownRequired:
      'A quebra personalizada precisa ter pelo menos uma linha preenchida. Complete a distribuição antes de salvar.',
    classificationSegmentPercentTotal:
      'Os percentuais dos segmentos precisam somar 100%. Ajuste a distribuição para fechar o total.',
    classificationPercentTotal:
      'Os percentuais de {{dimension}} precisam somar 100%. Ajuste a distribuição e tente novamente.',
    classificationGlNotFound:
      'Uma das contas contábeis selecionadas não foi encontrada. Escolha uma conta válida e tente novamente.',
    classificationGlInactive:
      'Uma das contas contábeis selecionadas está inativa. Troque por uma conta ativa para salvar o serviço.',
    classificationGlNotPostable:
      'A classificação usa uma conta contábil não postável. Selecione uma conta analítica/postável.',
    classificationGlCoaMismatch:
      'A conta contábil escolhida pertence a outro plano de contas. Selecione uma conta compatível com a entidade.',
    classificationGlNotEffective:
      'Uma das contas contábeis selecionadas ainda não está vigente para uso. Escolha outra conta ou ajuste a configuração.',
    classificationGlExpired:
      'Uma das contas contábeis selecionadas está fora de vigência. Escolha uma conta vigente para salvar o serviço.',
    classificationDepartmentNotFound:
      'Um dos departamentos da classificação não foi encontrado para esta entidade. Revise a seleção e tente novamente.',
    classificationCostCenterNotFound:
      'Um dos centros de custo da classificação não foi encontrado para esta entidade. Revise a seleção e tente novamente.',
    classificationDepartmentInactive:
      'Um dos departamentos da classificação está inativo. Escolha um departamento ativo e tente novamente.',
    classificationCostCenterInactive:
      'Um dos centros de custo da classificação está inativo. Escolha um centro de custo ativo e tente novamente.',
    classificationManagerialNotFound:
      'Uma das contas gerenciais selecionadas não foi encontrada. Escolha uma conta válida e tente novamente.',
    classificationManagerialWrongEntity:
      'Uma das contas gerenciais selecionadas pertence a outra entidade. Escolha uma conta da mesma entidade do serviço.',
    classificationManagerialInactive:
      'Uma das contas gerenciais selecionadas está inativa. Troque por uma conta ativa para salvar o serviço.',
    classificationManagerialNotLeaf:
      'A classificação usa uma conta gerencial sintética. Selecione uma conta gerencial de último nível.',
    classificationManagerialNotEffective:
      'Uma das contas gerenciais selecionadas ainda não está vigente. Escolha outra conta ou ajuste a configuração.',
    classificationManagerialExpired:
      'Uma das contas gerenciais selecionadas está fora de vigência. Escolha uma conta vigente para salvar o serviço.',
    classificationSegmentNotFound:
      'Um dos segmentos da classificação não foi encontrado. Revise a seleção e tente novamente.',
    classificationSegmentNotPredefined:
      'O segmento escolhido não aceita valores predefinidos. Revise a configuração do segmento antes de salvar.',
    classificationSegmentValueNotFound:
      'Um dos valores de segmento da classificação não foi encontrado. Escolha um valor válido e tente novamente.',
    invalidUuid:
      'O campo {{field}} está com um valor inválido. Selecione novamente uma opção válida antes de salvar.',
    required: 'Preencha o campo {{field}} antes de salvar o serviço.',
    maxLength:
      'O campo {{field}} aceita no máximo {{count}} caracteres. Ajuste o valor e tente novamente.',
    minLength:
      'O campo {{field}} precisa ter pelo menos {{count}} caracteres. Complete o valor e tente novamente.',
    enum: 'O campo {{field}} recebeu um valor inválido. Escolha uma das opções disponíveis e tente novamente.',
    string:
      'O campo {{field}} precisa ser um texto válido. Revise o valor informado e tente novamente.',
    number:
      'O campo {{field}} precisa ser um número válido. Revise o valor informado e tente novamente.',
    boolean: 'O campo {{field}} recebeu um valor inválido. Refaça a seleção e tente novamente.',
    array:
      'A lista de {{field}} está em formato inválido. Revise os itens informados e tente novamente.',
    pattern: 'O campo {{field}} está em formato inválido. Corrija o valor e tente novamente.',
    issRateMax:
      'A alíquota de ISS não pode ser maior que 100%. Ajuste o percentual e tente novamente.',
  },
  validation: {
    floorPriceHigherThanBaseFee: 'Preço piso não pode ser maior que a taxa base',
    // Proposed copy, not yet approved by the client.
    proposedRequired: {
      entityId: 'Informe a entidade',
      name: 'Informe o nome',
      baseFee: 'Informe a taxa base quando não há precificação por uso',
      claveProdServ: 'Informe a chave de produto/serviço (SAT)',
      serviceResultCountryCode: 'Informe um país estrangeiro para o resultado do serviço',
      cnaeCode: 'Informe o CNAE',
      issMunicipalityIbgeCode: 'Informe o município do ISS',
      municipalServiceCode: 'Informe o código municipal',
      issRate: 'Informe a alíquota do ISS',
      nbsCode: 'Informe a classificação do serviço (NBS)',
      cClassTrib: 'Informe a classificação tributária',
      cIndOp: 'Informe o tipo de operação',
      glRevenueAccount: 'Informe a conta de receita',
      managerialAccount: 'Informe a conta gerencial',
    },
    baseFeeRequired: 'Taxa base é obrigatória quando não há precificação por uso',
    gtvRateRequired: 'Informe o percentual sobre GTV (entre 0 e 100)',
    cnaeRequired: 'CNAE é obrigatório',
    issMunicipalityRequired: 'Município ISS é obrigatório',
    municipalServiceCodeRequired: 'Código municipal é obrigatório para emissão de NFS-e municipal',
    serviceCodeRequired: 'Serviço fiscal é obrigatório',
    fiscalServiceTypeRequired: 'Tipo fiscal do serviço é obrigatório',
    issRateRequired: 'Alíquota ISS é obrigatória',
    issRateMaxExceeded: 'Alíquota ISS não pode ser maior que 100%',
    serviceResultCountryRequired:
      'Selecione um país estrangeiro onde ocorreu o resultado do serviço.',
    glRevenueAccountRequired: 'Conta receita (GL) é obrigatória',
    managerialAccountRequired: 'Conta gerencial é obrigatória',
    nbsCodeRequired: 'Classificação do serviço (NBS) é obrigatória para emissão de NFS-e',
    claveProdServRequired:
      'A chave de produto/serviço (SAT) é obrigatória para emissão de CFDI (México)',
    nbsLeafCodeRequired:
      'Selecione um código NBS completo (formato 1.XXXX.XX.XX, ex.: 1.1302.21.00). Códigos intermediários como 1.1302.2 não são aceitos na NFS-e.',
    cClassTribRequired: 'Classificação tributária é obrigatória para emissão de NFS-e',
    cClassTribInvalid: 'Selecione uma classificação tributária IBS/CBS ativa da lista.',
    cIndOpRequired: 'Tipo de operação é obrigatório para emissão de NFS-e',
    cIndOpInvalid: 'Selecione um indicador de operação vigente da lista.',
    requiredFieldsTitle: 'Campos obrigatórios não preenchidos',
    requiredFieldsDescription:
      'Corrija os campos destacados em vermelho antes de salvar o serviço:',
    requiredFieldsConfirm: 'Entendi',
  },
  saasMetrics: {
    title: 'Métricas SaaS',
    subtitle:
      'MRR/ARR comprometido, previsão com uso projetado, bucket de MRR suspenso e movimentos do período',
    asOf: 'Referência',
    usageLookback: 'Janela de uso: {{count}} períodos',
    loading: 'Carregando métricas…',
    emDash: '—',
    multiCurrencyNote: 'Totais por moeda (contratos em mais de uma moeda)',
    thresholdLabel: 'Limiar suspensão → churn (dias)',
    thresholdHelp:
      'Contratos suspensos além deste limiar convertem o valor suspenso em churn nos movimentos do período.',
    saveThreshold: 'Salvar limiar',
    thresholdSaved: 'Limiar atualizado',
    cards: {
      committedMrr: 'MRR comprometido',
      committedArr: 'ARR comprometido',
      forecastMrr: 'MRR previsto',
      forecastArr: 'ARR previsto',
      forecastConfidence: 'Confiança do uso: {{confidence}}',
      suspendedMrr: 'MRR suspenso',
    },
    usageConfidence: {
      HIGH: 'Alta',
      LOW: 'Baixa',
      UNAVAILABLE: 'Indisponível',
    },
    empty: 'Nenhum valor recorrente ativo, suspenso ou com uso projetável.',
    period: {
      title: 'Movimentos do período',
      from: 'De',
      to: 'Até',
      loading: 'Carregando movimentos…',
      empty: 'Nenhum movimento no período.',
      expansion: 'Expansão',
      contraction: 'Contração',
      churn: 'Churn',
      suspension: 'Suspensão',
      reactivation: 'Reativação',
      committedImpact: 'Impacto no MRR comprometido',
      committedFrom: 'MRR no início',
      committedTo: 'MRR no fim',
      table: {
        date: 'Data',
        type: 'Tipo',
        cause: 'Causa',
        contract: 'Contrato',
        amount: 'Valor mensal',
        impact: 'Impacto',
        trace: 'Rastreio',
        openContract: 'Abrir',
      },
    },
    movementType: {
      EXPANSION: 'Expansão',
      CONTRACTION: 'Contração',
      CHURN: 'Churn',
      SUSPENSION: 'Suspensão',
      REACTIVATION: 'Reativação',
    },
    movementCause: {
      ACTIVATION: 'Ativação',
      AMENDMENT: 'Aditamento',
      RENEWAL: 'Renovação',
      STATUS_TRANSITION: 'Status',
      SUSPENSION_THRESHOLD: 'Limiar de suspensão',
    },
    sources: {
      title: 'Origem por contrato',
      contract: 'Contrato',
      version: 'Versão',
      status: 'Status',
      frequency: 'Frequência',
      periodAmount: 'Valor do ciclo (comprometido)',
      monthlyAmount: 'Mensal (comprometido)',
      projectedUsage: 'Uso projetado (mensal)',
      usageConfidence: 'Confiança do uso',
      usageObservations: 'Observações',
      suspensionDays: 'Dias suspenso',
      pastThreshold: 'Além do limiar',
      yes: 'Sim',
      no: 'Não',
      openContract: 'Abrir',
    },
    status: {
      ACTIVE: 'Ativo',
      SUSPENDED: 'Suspenso',
    },
  },
  arForecast: {
    title: 'Previsão de recebimentos',
    subtitle:
      'Recebimentos esperados por parcela em {{referenceDate}} — brutos e ajustados a risco. Previsão apenas: nenhum lançamento contábil é gerado.',
    loadError: 'Não foi possível carregar a previsão de recebimentos.',
    empty: 'Nenhuma parcela em aberto para prever.',
    totals: {
      gross: 'Bruto',
      riskAdjusted: 'Ajustado a risco',
      pastDue: 'Em atraso (bruto)',
      unspecifiedCurrency: 'Sem moeda definida',
    },
    tabs: {
      all: 'Todos',
      pastDue: 'Em atraso',
      future: 'A vencer',
    },
    emptyTab: 'Nenhuma parcela nesta aba.',
    filters: {
      entityPlaceholder: 'Todas as entidades',
      entityAll: 'Todas as entidades',
      customerPlaceholder: 'Todos os clientes',
      sourceLabel: 'Origem',
      sourceAll: 'Todas as origens',
      sourceExisting: 'AR existente',
      sourceProjected: 'Projeção de ciclo fixo',
      sourceRenewal: 'Renovação assumida',
      probabilityAll: 'Todas as probabilidades',
      probabilitySelected: '{{count}} faixas selecionadas',
      probability: {
        '0_25': '0% a 24%',
        '25_50': '25% a 49%',
        '50_75': '50% a 74%',
        '75_100': '75% a 100%',
      },
    },
    activeFilters: {
      label: 'Filtros ativos',
      clear: 'Limpar todos',
      entity: 'Entidade',
      customer: 'Cliente',
      source: 'Origem',
      probability: 'Probabilidade',
    },
    pagination: {
      showing: 'Mostrando {{from}}–{{to}} de {{total}}',
      page: 'Página {{page}} de {{totalPages}}',
      perPage: 'Itens por página',
      summary: 'Página {{page}} de {{totalPages}} · {{totalCount}} resultados',
      pageSizeLabel: 'Itens por página',
    },
    table: {
      customer: 'Cliente',
      installment: 'Parcela',
      contract: 'Contrato',
      projected: 'Projetado',
      renewalAssumed: 'Renovação assumida',
      fixedPortion: 'Fixo',
      usagePortion: 'Uso',
      usageHigh: 'Uso · alta confiança',
      usageLow: 'Uso · baixa confiança',
      usageUnavailable: 'Uso · indisponível',
      dueDate: 'Vencimento',
      expectedCashDate: 'Recebimento esperado',
      expectedCashHint: 'Estimado pelo comportamento de recebimento ({{source}}, {{lag}}d)',
      daysPastDue: '{{days}}d em atraso',
      gross: 'Bruto',
      riskAdjusted: 'Ajustado a risco',
      probability: 'Prob. recebimento',
      probabilityHint: {
        formula: '{{gross}} × {{percent}}% = {{riskAdjusted}} ajustado a risco.',
        basis: {
          CUSTOMER:
            'Histórico do próprio cliente: recebido ÷ faturado nas {{sampleSize}} faturas mais recentes vencidas há mais de {{maturityDays}} dias.',
          BILLING_GROUP:
            'Sem histórico próprio utilizável do cliente (mínimo {{minSamples}} faturas vencidas há mais de {{maturityDays}} dias). Base: grupo de faturamento, {{sampleSize}} faturas.',
          TENANT:
            'Sem histórico utilizável do cliente nem do grupo de faturamento. Base: entidade, {{sampleSize}} faturas.',
          NONE: 'Nenhuma fatura vencida há mais de {{maturityDays}} dias no histórico. Sem evidência para aplicar deságio — assume 100%.',
        },
        zero: '0% significa que nada foi recebido nessas faturas — não é ausência de histórico.',
        confidence: {
          HIGH: 'Confiança alta.',
          LOW: 'Confiança baixa.',
        },
      },
    },
  },
};

export const billingEn = {
  ...costServicesEn,
  ...costProvisionsEn,
  ...revenueCyclesEn,
  list: {
    title: 'Billing services',
    subtitle: 'Register and manage products and services for billing',
    searchPlaceholder: 'Search services...',
    createButton: 'New service',
    stats: {
      services: 'services',
    },
    price: {
      usage: 'Usage price',
    },
    filters: {
      status: {
        label: 'Status',
        all: 'All',
        active: 'Active',
        inactive: 'Inactive',
        draft: 'Draft',
      },
    },
    tabs: {
      overview: 'Overview',
      active: 'Active',
      inactive: 'Inactive',
      draft: 'Drafts',
    },
  },
  form: {
    titleCreate: 'New service',
    proposedToasts: {
      created: 'Service created successfully',
      updated: 'Service updated successfully',
      draftSaved: 'Service draft saved',
    },
    titleEdit: 'Edit service',
    sections: {
      entity: {
        title: 'Entity',
        subtitle: 'Select the service entity',
      },
      serviceInfo: {
        title: 'Service information',
        subtitle: 'Core service details',
      },
      pricing: {
        title: 'Pricing',
        subtitle: 'Model and pricing rules',
      },
      fixedPricing: {
        title: 'Base pricing',
        subtitle: 'Base contract values',
      },
      pricingLimits: {
        title: 'Billing limits',
        subtitle: 'Minimum and maximum billing guardrails',
      },
      billingRevenue: {
        title: 'Billing & revenue',
        subtitle: 'Billing timing and accounting recognition',
      },
      usagePricing: {
        title: 'Variable usage pricing',
        subtitle: 'Enable to charge per unit consumed in addition to (or instead of) the base fee',
        modeCards: {
          PER_UNIT: 'A single rate per unit',
          GRADUATED: 'Progressive tier rates',
          VOLUME: 'One rate for the entire volume',
          STAIRSTEP: 'One flat charge for the matched range',
          PERCENT_OF_GTV: 'A percentage of the gross transaction value',
        },
      },
      fiscal: {
        title: 'Fiscal configuration',
        subtitle: 'ISS and fiscal codes',
      },
      ibs: {
        title: 'IBS/CBS - Tax reform',
        subtitle: 'Codes required for NFSe emission (Reforma layout / NT-002)',
      },
      accounting: {
        title: 'Accounting classification',
        subtitle: 'GL accounts and managerial tags',
      },
      cfdi: {
        title: 'CFDI (Mexico)',
        subtitle: 'SAT keys used when stamping the CFDI 4.0',
        claveProdServ: 'Product/service key (SAT)',
        claveProdServHint: 'The SAT c_ClaveProdServ catalogue key, used on the CFDI.',
        satUnitCode: 'Unit key (SAT)',
        satUnitCodeHint: 'The SAT c_ClaveUnidad catalogue key. E.g. E48, service unit.',
      },
    },
    fields: {
      name: {
        label: 'Name',
        hint: 'Required field',
        placeholder: 'e.g. Monthly subscription',
      },
      code: {
        label: 'Code',
        hint: 'Leave empty to auto-generate',
        placeholder: 'SRV-001',
      },
      description: {
        label: 'Description',
        hint: 'Optional description for the service',
        placeholder: 'Describe the service and its use case...',
      },
      entity: {
        label: 'Entity',
        placeholder: 'Select entity',
      },
      currency: {
        label: 'Currency',
        placeholder: 'e.g. BRL',
        derivedHint:
          "The service's default commercial currency. It may differ from the entity's functional currency.",
        unresolvedHint: 'Select a supported commercial currency for the service.',
        noEntitySelected: 'Select an entity to see the currency',
        noEntitySelectedBlock: "Select the service's entity before saving.",
        unresolvedBlock: 'Select a supported commercial currency before saving.',
      },
      billingMode: {
        label: 'Billing mode',
        placeholder: 'Select mode',
        options: {
          recurring: 'Recurring',
          oneTime: 'One-time',
        },
      },
      invoicing: {
        label: 'Invoicing',
        hint: 'Individual: each charge is its own invoice with its own fiscal document. Period aggregate: charges become transactions and pre-billing gathers them into one aggregate per partner, currency and window.',
        options: {
          individual: 'Individual (one invoice per charge)',
          periodAggregate: 'Period aggregate (transactions → aggregate invoice)',
        },
      },
      usageUnitName: {
        label: 'Usage unit',
        hint: 'Name of the unit charged per use (e.g. "seat", "GB", "API call")',
        placeholder: 'e.g. seat',
      },
      baseFee: {
        label: 'Base fee',
        hint: 'Fixed amount charged every period, even with no usage. E.g. R$ 200/month subscription.',
      },
      includedUnits: {
        label: 'Included units',
        hint: 'Usage units included in the base fee before variable charges apply',
      },
      minimumCommitment: {
        label: 'Minimum commitment',
        hint: 'Minimum billing per period, regardless of consumption. E.g. at least R$ 500/month even with low usage.',
      },
      cap: {
        label: 'Cap',
        hint: 'Maximum limit per period. Usage above this is not charged. E.g. R$ 5,000/month cap.',
      },
      floorPrice: {
        label: 'Floor price',
        hint: 'Minimum unit price, even when a discount was negotiated. E.g. never charge less than R$ 2.00 per ticket.',
      },
      defaultStandaloneSellingPrice: {
        label: 'Default standalone selling price',
        hint: 'Reference price when the service is sold in a bundle. Used to allocate revenue across items.',
      },
      calculationMode: {
        label: 'Calculation mode',
        hint: 'How variable usage is calculated',
        placeholder: 'Select mode',
      },
      defaultBillingTiming: {
        label: 'Default billing timing',
        hint: 'Advance = bill at the start of the period; arrears = bill at the end, after consumption.',
        placeholder: 'Select timing',
        options: {
          advance: 'Advance',
          arrears: 'Arrears',
        },
      },
      defaultRecognitionMethod: {
        label: 'Default revenue recognition method',
        hint: 'When revenue is recognized under accrual accounting, regardless of when cash is received.',
        placeholder: 'Select method',
        options: {
          POINT_IN_TIME_AT_DELIVERY: 'At point of delivery',
          STRAIGHT_LINE_OVER_TIME: 'Straight-line over time',
          USAGE_AS_CONSUMED: 'As consumed',
          PROGRESS_MILESTONES: 'By progress milestones',
        },
        optionDescriptions: {
          POINT_IN_TIME_AT_DELIVERY: 'Revenue recognized once, at delivery.',
          STRAIGHT_LINE_OVER_TIME:
            'Spread evenly across the contract term. E.g. annual = 1/12 per month.',
          USAGE_AS_CONSUMED: 'Recognized as the customer uses the service.',
          PROGRESS_MILESTONES: 'Recognized when defined milestones are met.',
        },
      },
      gtvRate: {
        label: 'Percent of GTV',
        hint: 'The fee is this percent of each transaction’s gross transaction value (GTV). E.g. 4.23.',
      },
      usageTiers: {
        label: 'Usage tiers',
        empty: 'No tiers added',
        autoFillHint:
          'The "From" of each tier is automatically filled based on the previous tier. The last tier without an upper limit covers all excess.',
        headers: {
          from: 'From',
          to: 'To',
          unitPrice: 'Unit price',
          flatPrice: 'Tier price',
          floorPrice: 'Floor',
        },
        placeholders: {
          from: '0',
          to: 'No limit',
        },
      },
      cnaeCode: {
        label: 'CNAE',
        placeholder: 'Type to search',
      },
      serviceCode: {
        label: 'Fiscal service',
        placeholder: 'Type to search',
      },
      externalProductId: {
        label: 'External product ID',
        hint: 'Used to match external imports with this service',
        placeholder: 'E.g. PROD-123',
      },
      fiscalServiceTypeId: {
        label: 'Fiscal service (LC 116)',
        placeholder: 'Type to search',
        optionalHint: 'Optional. When selected, federal withholdings are prefilled automatically.',
        clearLabel: 'None',
        clearAria: 'Clear fiscal service selection',
      },
      municipalServiceCode: {
        label: 'Municipal code (NFSe)',
        placeholder: 'Search prefecture code',
        hint: 'Municipality proprietary code. ISS rate may be auto-filled.',
        // Proposed copy, not yet approved by the client.
        proposedSelectMunicipalityFirst: 'Select the ISS municipality first.',
      },
      irrfNature: {
        label: 'IRRF nature',
        placeholder: 'Select nature',
        derivedHint:
          'Always editable. The fiscal service (LC 116) only suggests a default when this field is empty.',
        options: {
          NONE: 'No IRRF withholding',
          PROFESSIONAL_1_5: 'Professional services (1.5%)',
          COMMISSION_1_5: 'Commissions and brokerage (1.5%)',
          CLEANING_SECURITY_1_0: 'Cleaning, maintenance and security (1.0%)',
        },
      },
      isLaborProvisionOverride: {
        label: 'Labor provision / empreitada',
        hint: 'When enabled, applies INSS withholding (11%) on invoices for this service.',
        defaultYes: 'Fiscal type default: Yes',
        defaultNo: 'Fiscal type default: No',
      },
      issRate: {
        label: 'ISS rate',
      },
      issMunicipalityIbgeCode: {
        label: 'ISS municipality',
        hint: 'Defaults from the entity fiscal config, but can be changed for this service.',
        placeholder: 'Select municipality',
      },
      issWithheldByDefault: {
        label: 'ISS withheld by default',
        placeholder: 'Select option',
      },
      nfseIssTaxation: {
        label: 'NFS-e ISS treatment',
        hint: 'Explicit fiscal classification. A zero rate does not characterize an export.',
        options: {
          TAXABLE: 'Taxable operation',
          IMMUNITY: 'Immunity',
          EXPORT: 'Service export',
          NON_INCIDENCE: 'Non-incidence',
        },
      },
      serviceResultCountryCode: {
        label: 'Service result country',
        hint: 'For municipal NFS-e, this country is also sent as the place of provision. Review the setup if those countries differ.',
        placeholder: 'Select country',
        loading: 'Loading countries...',
        unavailable: 'Countries could not be loaded. Try again.',
      },
      nbsCode: {
        label: 'Service classification',
        placeholder: '1.0401.10',
        tooltip:
          'NBS (Nomenclatura Brasileira de Serviços) classifies the type of service being sold, similar to NCM for products. E.g. 1.0401.10 - IT services',
      },
      cstIbsCbs: {
        label: 'Tax situation',
        placeholder: 'Select CST',
        tooltip:
          'Determines how IBS/CBS applies to this item — full tax, exempt, suspended, etc. E.g. 010 — Normal taxation',
        options: {
          '000': '000 - Full taxation',
          '010': '010 - Uniform sector rates',
          '011': '011 - Uniform reduced rates',
          '200': '200 - Reduced rate',
          '220': '220 - Fixed amount',
          '221': '221 - Proportional fixed rate',
          '222': '222 - Tax base reduction',
          '400': '400 - Exemption',
          '410': '410 - Immunity / non-incidence',
          '510': '510 - Deferral',
          '515': '515 - Deferral + reduction',
          '550': '550 - Suspension',
          '620': '620 - Single-stage taxation (monophase)',
          '800': '800 - Credit transfer',
          '810': '810 - ZFM IBS adjustment',
          '811': '811 - General adjustments',
          '820': '820 - Specific regime',
          '830': '830 - Base exclusion',
        },
      },
      cClassTrib: {
        label: 'Tax classification',
        placeholder: 'Enter classification',
        tooltip:
          'Groups items by tax treatment for the reform. Still being defined by RFB — advanced/optional field.',
      },
      cIndOp: {
        label: 'Operation type',
        placeholder: 'Select type',
        tooltip: 'Classifies the supply according to the current NFS-e operation catalogue.',
        inactiveOption: 'Obsolete code; select another',
        options: {
          '100501': '100501 - Intangible assets and rights, for consideration',
          '100502': '100502 - Intangible assets and rights, for consideration',
        },
      },
      ibsCbsBaseReductionPct: {
        label: 'Tax base reduction',
        hint: 'Applicable reduction percentage per LC 214/2025',
        tooltip: 'Percentage discount on the taxable base — some services get a reduced rate.',
      },
      glRevenueAccountId: {
        label: 'Revenue account (GL)',
        placeholder: 'Select account',
      },
      managerialAccountId: {
        label: 'Managerial account',
        placeholder: 'Select account',
      },
      costCenterId: {
        label: 'Cost center',
        placeholder: 'Select cost center',
      },
      segments: {
        label: 'Custom segments',
        hint: 'Select one value per segment',
        selectEntity: 'Select an entity to load segments.',
        empty: 'No segments available.',
        placeholder: 'Select a value',
        emptyValues: 'No values available',
      },
      nfseDescriptionTemplate: {
        label: 'NFS-e template',
        hint: 'Default text for service invoice',
        placeholder: 'E.g. Services for {{customer_name}} — due {{due_date}}',
      },
      isActive: {
        label: 'Active',
      },
    },
  },
  nfseTemplateVariables: {
    inputHint: 'Type {{ to insert invoice variables.',
    openCatalog: 'View variable catalog',
    dialogTitle: 'NFS-e template variables',
    dialogDescription:
      'Use placeholders like {{variable}}. They are replaced with invoice data when the NF is emitted.',
    searchPlaceholder: 'Search variable...',
    autocompleteHeading: 'Variables',
    autocompleteEmpty: 'No variables found.',
    empty: 'No variables match your search.',
    categories: {
      invoice: 'Invoice',
      customer: 'Customer',
      amounts: 'Amounts',
      withholding: 'Withholdings',
      service: 'Service',
    },
    fields: {
      invoiceNumber: {
        label: 'Invoice number',
        description: 'Fatura field (invoice_number).',
      },
      description: {
        label: 'Invoice description',
        description: 'Invoice descriptive text.',
      },
      issueDate: {
        label: 'Issue date',
        description: 'Invoice issue date.',
      },
      dueDate: {
        label: 'Due date',
        description: 'Invoice due date.',
      },
      managerialDate: {
        label: 'Managerial date',
        description: 'Invoice managerial date.',
      },
      competencyDate: {
        label: 'Competency date',
        description: 'Competency date used on NF emission.',
      },
      externalRef: {
        label: 'External reference',
        description: 'External identifier (e.g. Kamino).',
      },
      lineDescriptions: {
        label: 'Line descriptions',
        description: 'Invoice line descriptions joined with semicolons.',
      },
      entityName: {
        label: 'Entity name',
        description: 'Issuing entity legal name.',
      },
      customerName: {
        label: 'Customer name',
        description: 'Customer trade or legal name.',
      },
      customerLegalName: {
        label: 'Customer legal name',
        description: 'Customer legal name.',
      },
      customerTradeName: {
        label: 'Customer trade name',
        description: 'Customer trade name.',
      },
      customerCode: {
        label: 'Customer code',
        description: 'Internal customer code.',
      },
      customerTaxId: {
        label: 'Customer tax ID',
        description: 'Customer CNPJ/CPF.',
      },
      customerEmail: {
        label: 'Customer email',
        description: 'Primary customer email.',
      },
      grossAmount: {
        label: 'Gross amount',
        description: 'Invoice gross total.',
      },
      netAmount: {
        label: 'Net amount',
        description: 'Net amount after withholdings.',
      },
      withholdingTotal: {
        label: 'Total withholdings',
        description: 'Sum of source withholdings.',
      },
      withholdingIrrf: {
        label: 'IRRF withholding',
        description: 'IRRF withheld amount.',
      },
      withholdingPis: {
        label: 'PIS withholding',
        description: 'PIS withheld amount.',
      },
      withholdingCofins: {
        label: 'COFINS withholding',
        description: 'COFINS withheld amount.',
      },
      withholdingCsll: {
        label: 'CSLL withholding',
        description: 'CSLL withheld amount.',
      },
      withholdingCsrf: {
        label: 'CSRF withholding (PIS + COFINS + CSLL)',
        description: 'Combined federal PIS, COFINS, and CSLL withholdings (Lei 10.833).',
      },
      withholdingInss: {
        label: 'INSS withholding',
        description: 'INSS withheld amount.',
      },
      withholdingIss: {
        label: 'ISS withholding',
        description: 'ISS withheld amount.',
      },
      serviceCode: {
        label: 'Service code',
        description: 'Linked billing service code.',
      },
    },
  },
  contractTemplates: {
    list: {
      title: 'Contract templates',
      subtitle: 'Reusable service bundles with default terms',
      searchPlaceholder: 'Search templates...',
      createButton: 'New template',
      tabs: {
        overview: 'Overview',
        active: 'Active',
        inactive: 'Inactive',
      },
      stats: {
        templates: 'templates',
      },
      loading: 'Loading...',
      loadError: 'Could not load templates.',
      empty: 'No templates found.',
      itemCount: '{{count}} items',
      activeFilters: 'Active filters',
      clearAllFilters: 'Clear filters',
      createdAt: 'Created {{date}}',
      columns: {
        template: 'Template',
        frequency: 'Frequency',
        items: 'Items',
        renewal: 'Renewal',
        paymentTerms: 'Payment',
        status: 'Status',
        currency: 'Currency',
      },
      filterLabels: {
        search: 'Search',
        status: 'Status',
      },
      status: {
        active: 'Active',
        inactive: 'Inactive',
      },
      duration: {
        months: '{{count}} month(s)',
        openEnded: 'No default duration',
      },
      renewal: {
        manual: 'Manual',
        fixedPercentage: '{{value}}%',
        indexBased: 'Indexed by {{index}}',
      },
      paymentTerms: {
        days: '{{count}} day(s)',
        defaultValue: 'Default',
      },
      actions: {
        edit: 'Edit template',
        more: 'Template actions',
      },
      detail: {
        summary: 'Summary',
        currency: 'Currency',
        paymentTerms: 'Payment terms',
        autoRenewal: 'Auto renewal',
        renewalSection: 'Renewal',
        renewalDuration: 'Renewal duration',
        renewalAdjustment: 'Adjustment',
        cancellationNotice: 'Cancellation notice',
        defaultValue: 'Default',
        items: 'Items',
        serviceDefault: 'Service default',
        editButton: 'Edit template',
        noDescription: 'No description',
        days: '{{count}} day(s)',
        months: '{{count}} month(s)',
      },
    },
    actions: {
      duplicate: {
        trigger: 'Duplicate',
        title: 'Duplicate contract template',
        description:
          'Create a copy of "{{name}}" with the same terms, services, and defaults. The code will be generated automatically.',
        nameLabel: 'New name',
        namePlaceholder: 'Ex: Enterprise template - variation',
        nameRequired: 'Enter a name for the new template.',
        confirm: 'Create copy',
        creating: 'Creating...',
        success: 'Template duplicated successfully',
      },
      inactivate: {
        trigger: 'Inactivate',
        title: 'Inactivate "{{name}}"?',
        description:
          'This template will become unavailable for future contracts. Existing contracts created from it will not be changed.',
        confirm: 'Inactivate template',
        success: 'Template inactivated successfully',
      },
      reactivate: {
        trigger: 'Reactivate',
        success: 'Template reactivated successfully',
      },
    },
    form: {
      titleNew: 'New contract template',
      titleEdit: 'Edit contract template',
      breadcrumbNew: 'New template',
      breadcrumbEdit: 'Edit template',
      steps: {
        info: 'Information',
        terms: 'Terms & Renewal',
        services: 'Services',
      },
      inactiveBanner: {
        title: 'Inactive template',
        description:
          'This template is not available for new contracts. Existing contracts created from it are not affected.',
      },
      sections: {
        templateInfo: {
          title: 'Template Info',
          subtitle: 'Header and scope configuration',
        },
        defaultTerms: {
          title: 'Default Terms',
          subtitle: 'Commercial defaults for contracts',
        },
        renewal: {
          title: 'Renewal',
          subtitle: 'Auto-renewal and price adjustment settings',
        },
        itemDefaults: {
          title: 'Item Defaults',
          subtitle: 'Recognition policy applied to items created from this template',
        },
        serviceItems: {
          title: 'Service Items',
          subtitle: 'Service bundle for this template',
        },
      },
      fields: {
        entity: 'Entity',
        name: 'Name',
        code: 'Code',
        codeHint: 'Leave empty to auto-generate',
        description: 'Internal description',
        billingFrequency: 'Billing Frequency',
        revenueRecognitionModel: 'Revenue recognition',
        settlementModel: 'Settlement model',
        billingDayOfMonth: 'Billing day of month',
        billingDayAnchor: 'Day anchor',
        billingDayAnchorHint:
          'Fixed day reads the billing day; last day and last business day ignore it. The aggregate and the ordinary invoice follow the same anchor.',
        autoApproveTransactions: 'Transaction approval',
        autoApproveTransactionsHint:
          'Transactions projected from the settlement report are born as drafts. By rule, they are approved on creation (recognised and open) with the rule recorded on their history.',
        itemInvoicing: 'Item invoicing',
        itemInvoicingHint:
          "Inherits the service's default. Individual: each occurrence becomes its own invoice. Aggregate: accumulates as transactions the cycle's aggregate bills.",
        defaultDurationMonths: 'Default Duration (months)',
        currency: 'Currency',
        currencyHint: 'The template commercial currency. All template items use this currency.',
        currencyNoEntity: 'Select an entity to see the currency',
        currencyUnresolved: 'Select a supported commercial currency for the contract.',
        paymentMethod: 'Payment Method',
        installmentCount: 'Installment count',
        installmentCountHint:
          'How many installments a generated invoice is split into. Leave blank (or 1) for a single installment.',
        installmentIntervalDays: 'Installment interval (days)',
        installmentIntervalDaysHint:
          'Days between each installment due date. Only considered when there is more than one installment.',
        paymentTermsDays: 'Payment Terms (days)',
        paymentTermsDaysHint:
          'Days between issue and due date. Leave blank (or 0) to make it due on the issue date.',
        autoRenewal: 'Auto Renewal',
        renewalDurationMonths: 'Renewal Duration (months)',
        renewalAdjustmentType: 'Adjustment Type',
        renewalAdjustmentValue: 'Adjustment Value (%)',
        renewalAdjustmentPercentage: 'Adjustment percentage',
        renewalAdjustmentValueHint: 'Percentage applied on the current contract value',
        renewalIndex: 'Adjustment Index',
        renewalIndexHint:
          'Select the contractual index. Official values are synced daily from BCB (SGS).',
        renewalIndexOptions: {
          igpm: 'IGP-M',
          ipca: 'IPCA',
          inpc: 'INPC',
          selic: 'SELIC',
        },
        renewalAccumulatedVariationWindowMonths: 'Accum. var. window',
        renewalAccumulatedVariationWindowMonthsHint:
          'Number of months used to calculate accumulated index variation. Example: 12 uses the last 12 months.',
        renewalPublicationLagDays: 'Index lag',
        renewalPublicationLagDaysHint:
          'Days between the index publication and its application at renewal',
        renewalFloorAtZero: 'Floor Adjustment at Zero',
        cancellationNoticeDays: 'Cancellation Notice (days)',
        cancellationNoticeDaysHint: 'Days of advance notice required for cancellation',
        defaultBillingTiming: 'Default billing',
        defaultBillingTimingHint:
          'Defines whether items created from this template are billed before or after the service period. If left as the service default, each service keeps its own setting.',
        billingTimingOptions: {
          advance: 'Advance',
          arrears: 'Arrears',
        },
        defaultServicePeriodRule: 'Service period',
        defaultServicePeriodRuleHint:
          'Defines how the service period is calculated on contract items: by calendar month or contract anniversary date. Use "Unset" to keep the service rule.',
        servicePeriodRuleOptions: {
          calendarMonth: 'Calendar month',
          anniversaryDate: 'Anniversary date',
        },
        defaultRecognitionMethod: 'Revenue recognition',
        defaultRecognitionMethodHint:
          'Defines how revenue from the items is recognized in the contract. If left as the service default, each service configuration takes precedence.',
        recognitionMethodOptions: {
          pointInTimeAtDelivery: 'At point of delivery',
          straightLineOverTime: 'Straight-line over time',
          usageAsConsumed: 'As consumed',
          progressMilestones: 'By progress milestones',
        },
        items: 'Items',
        itemLabel: 'Item {{index}}',
        service: 'Service',
        serviceLabel: 'Service',
        serviceLabelTooltip: 'Billing service to include in the contract',
        priceLabel: 'Price',
        priceLabelTooltip: 'Unit price for this service item',
        recurrenceLabel: 'Recurrence',
        recurrenceLabelTooltip: 'Period the service is included (Always = every month)',
        defaultPricePlaceholder: 'Default price',
        baseFeeHint: 'Base fee: {{price}}',
        introductoryPrice: 'Introductory price',
        introductoryMonths: 'Introductory months',
        includeForMonths: 'Include for first X months',
        includeForMonthsPlaceholder: 'Always',
        recurrenceOptions: {
          always: 'Always',
          months1: '1 month',
          months2: '2 months',
          months3: '3 months',
          months4: '4 months',
          months5: '5 months',
          months6: '6 months',
          months7: '7 months',
          months8: '8 months',
          months9: '9 months',
          months10: '10 months',
          months11: '11 months',
          months12: '12 months',
        },
        quantityPlaceholder: 'Qty',
      },
      placeholders: {
        name: 'e.g. Standard SaaS Annual',
        code: 'CTT-001',
        description: 'Default annual terms for enterprise contracts.',
        select: 'Select',
        durationMonths: '12',
        billingDayOfMonth: 'e.g. 10',
        currency: 'BRL',
        paymentMethod: 'Select',
        installmentCount: 'e.g. 3',
        installmentIntervalDays: 'e.g. 30',
        paymentTermsDays: '10',
        renewalDurationMonths: 'e.g. 12',
        renewalAdjustmentValue: 'e.g. 5.00',
        renewalIndex: 'e.g. IGPM',
        renewalAccumulatedVariationWindowMonths: 'e.g. 12',
        renewalPublicationLagDays: 'e.g. 30',
        cancellationNoticeDays: 'e.g. 30',
        service: 'Select service',
      },
      options: {
        monthly: 'Monthly',
        quarterly: 'Quarterly',
        semiannual: 'Semiannual',
        annual: 'Annual',
        onFiscalDocument: 'On fiscal document',
        onSettlement: 'On settlement (acquirer report)',
        settlementDirect: 'Direct',
        settlementCustodial: 'Custodial (gross in transit, payout as a liability)',
        anchorFixedDay: 'Fixed day',
        anchorLastDay: 'Last day of the month',
        anchorLastBusinessDay: 'Last business day of the month',
        autoApproveByRule: 'By rule, on creation',
        autoApproveManual: 'Manual (they stay as drafts)',
        invoicingIndividual: 'Individual — its own invoice',
        invoicingPeriodAggregate: 'Aggregate — a transaction the aggregate bills',
        useCustomerDefault: 'Use customer default',
        useServiceDefault: 'Use service default',
        unset: 'Unset',
        noService: 'Select',
        renewalYes: 'Yes — auto-renew',
        renewalNo: 'No — manual renewal',
        adjustmentNone: 'No adjustment',
        adjustmentFixed: 'Fixed percentage',
        adjustmentIndex: 'Index-based (IGPM / IPCA)',
        floorAtZeroYes: 'Yes — floor negative adjustments at zero',
        floorAtZeroNo: 'No — allow negative adjustments',
      },
      actions: {
        addItem: 'Add Item',
        back: 'Back',
        continueTo: 'Continue: {{step}}',
        save: 'Save',
        create: 'Create template',
      },
      emptyItems: 'No items added yet.',
      validation: {
        entityRequired: 'Entity is required',
        nameRequired: 'Name is required',
        atLeastOneItem: 'At least one service item is required',
        allItemsNeedService: 'All service items must have a service selected',
        introductoryTermsRequired:
          'Enter the introductory price and introductory months for every item with introductory pricing enabled',
        durationRequired: 'Default duration is required',
        durationTwoDigits: 'Default duration must be a number with up to 2 digits',
        billingDayRequired: 'Billing day is required',
        billingDayTwoDigits: 'Billing day must be a number with up to 2 digits',
        billingDayInvalid: 'Billing day must be between 1 and 28',
        paymentMethodRequired: 'Payment method is required',
        paymentTermsRequired: 'Payment terms are required',
        paymentTermsThreeDigits: 'Payment terms must be a number with up to 3 digits',
        installmentCountInvalid: 'Installment count must be a positive whole number',
        installmentIntervalDaysInvalid: 'Installment interval must be a positive whole number',
        currencyUnresolved: 'Select a supported commercial currency before continuing.',
        renewalDurationRequired: 'Renewal duration is required when auto-renewal is enabled',
        renewalAdjustmentValueRequired:
          'Adjustment percentage is required for "Fixed percentage" type',
        renewalIndexRequired: 'Adjustment index is required for "Index-based" type',
        cancellationNoticeDaysRequired: 'Cancellation notice is required',
      },
    },
  },
  contracts: {
    list: {
      title: 'Contracts',
      subtitle: 'Commercial agreements with customers',
      statusViews: 'Contract statuses',
      searchLabel: 'Search contracts',
      searchPlaceholder: 'Search contracts...',
      createButton: 'New contract',
      stats: { contracts: 'contracts' },
      tabs: {
        all: 'All',
        ACTIVE: 'Active',
        DRAFT: 'Drafts',
        SUSPENDED: 'Suspended',
        EXPIRED: 'Expired',
        CANCELLED: 'Cancelled',
      },
      filters: {
        customer: 'Filter customer',
        contract: 'Filter contract',
        startDate: 'Filter start date',
        endDate: 'Filter end date',
        currency: 'Filter currency',
        status: 'Filter by status',
        renewalMonths: 'Filter months until renewal',
        renewalMinMonths: 'Minimum months',
        renewalMaxMonths: 'Maximum months',
        apply: 'Apply',
      },
      activeFilters: 'Active filters:',
      clearAllFilters: 'Clear all',
      filterLabels: {
        search: 'Search',
        customer: 'Customer',
        contract: 'Contract',
        startDate: 'Start',
        endDate: 'End',
        renewal: 'Validity',
        status: 'Status',
        currency: 'Currency',
      },
      columns: {
        customer: 'Customer',
        contract: 'Contract',
        startDate: 'Start',
        endDate: 'End',
        validity: 'Validity',
        status: 'Status',
        renewal: 'Renewal',
        currency: 'Currency',
      },
      termUndetermined: 'Term not set',
      validity: { start: 'Start:' },
      sort: { label: 'Sort by', startDate: 'Start date' },
      confirmation: {
        activate:
          'Contract {{code}} takes effect and starts generating billing and revenue recognition in the next cycles.',
        suspend:
          'Billing for contract {{code}} stops and revenue entries not yet incurred are cancelled. Schedules stay active and the suspension can be reverted by reactivating.',
        cancel:
          'Cancelling contract {{code}} is permanent. Confirm it from the contract details page, where the full accounting consequences are shown.',
        reactivate:
          'Contract {{code}} takes effect again, entries not yet incurred resume, and cycles start being generated once more.',
      },
      confirmationTitle: {
        activate: 'Activate contract',
        suspend: 'Suspend contract',
        cancel: 'Cancel contract',
        reactivate: 'Reactivate contract',
      },
      confirmationAction: {
        activate: 'Activate',
        suspend: 'Suspend',
        cancel: 'Confirm cancellation',
        reactivate: 'Reactivate',
      },
      renewal: {
        automatic: 'Auto-renewal',
        manual: 'Manual renewal',
        months: 'months',
        monthsLeft_zero: '{{count}} months',
        monthsLeft_one: '{{count}} month',
        monthsLeft_other: '{{count}} months',
      },
      createdAt: 'Created {{date}}',
      pagination: {
        summary: '{{from}}–{{to}} of {{total}} contracts',
        previous: 'Previous',
        next: 'Next',
      },
      loading: 'Loading...',
      empty: 'No contracts found.',
      emptyDescription: 'New contracts will appear here once they are created.',
      status: {
        DRAFT: 'Draft',
        ACTIVE: 'Active',
        SUSPENDED: 'Suspended',
        CANCELLED: 'Cancelled',
        EXPIRED: 'Expired',
      },
      detail: {
        startDate: 'Start date',
        currency: 'Currency',
        items: 'Items',
        draftItems: 'Items (draft)',
        currentVersion: 'Current version',
        noVersion: 'No effective version yet',
        versionNumber: 'Version {{number}}',
        effectiveFrom: 'Effective since {{date}}',
      },
      actions: {
        open: 'Open contract',
        actionsFor: 'Actions for contract {{code}}',
        activate: 'Activate',
        suspend: 'Suspend',
        reactivate: 'Reactivate',
        cancel: 'Cancel contract',
        edit: 'Edit',
        confirmCancel: 'Are you sure you want to cancel this contract?',
      },
      syncNow: {
        triggerButton: 'Bill',
        confirmTitle: 'Bill contracts now',
        confirmDescription:
          'Runs, on demand, the same Contract Billing Cycle generation that runs automatically at 04:00 for every ACTIVE contract. This is a tenant-wide batch operation — it can take a while and can change several draft invoices.',
        running: 'Billing…',
        confirm: 'Bill',
        close: 'Close',
        runAgain: 'Run again',
        summary: {
          evaluated: 'Contracts evaluated',
          generated: 'Generated',
          generatedNote: '{{staled}} of {{generated}} marked STALE',
          generatedIdempotentNote:
            'The remaining {{count}} contract(s) were already up to date — idempotent generation, no action needed.',
          blocked: 'Blocked',
          staledSectionTitle: 'Invoices marked STALE',
          staledWarning:
            'These draft invoices had already been reviewed and now need re-review — their source data changed since the last generation.',
          staledDifferences: '{{count}} difference(s)',
          blockedSectionTitle: 'Blocked by reason',
          blockedReasonPendingUsage: 'Usage not finalized (PENDING_USAGE)',
          contractLink: 'Open contract',
          invoiceLink: 'Open invoice',
          empty: 'No new cycle generated — every ACTIVE contract was already up to date.',
        },
      },
      revenueRecognitionRun: {
        triggerButton: 'Recognize revenue',
        title: 'Run batch revenue recognition',
        description:
          'Recognizes, for the selected competency, every due entry whose schedule is ACTIVE, across every contract in the tenant. Failures are recorded per entry — the run is never aborted by one isolated failure.',
        monthLabel: 'Competency (month)',
        monthFutureError: 'A future competency cannot be selected.',
        run: 'Run recognition',
        running: 'Running…',
        close: 'Close',
        runAgain: 'Run again',
        summary: {
          total: 'Total processed',
          posted: 'Posted',
          skipped: 'Skipped',
          failed: 'Failed',
          failedSectionTitle: 'Failures by reason',
          errorCodes: {
            CONTRACT_REVENUE_NO_SNAPSHOT:
              "Awaiting the invoice or finalized usage: revenue can only be recognized once the period's invoice posts to the GL (or, for usage billing, the usage is finalized)",
            CONTRACT_REVENUE_CURRENCY_UNSUPPORTED: 'Currency not supported',
            CONTRACT_REVENUE_POSTING_DATE_BEFORE_COMPETENCY: 'Posting date precedes the competency',
            CONTRACT_REVENUE_NO_BOOK_COVERAGE: 'No book coverage',
            CONTRACT_REVENUE_COA_VERSION_TRANSITION_UNSUPPORTED:
              'CoA version transition not supported',
            CONTRACT_REVENUE_COA_ASSIGNMENT_CHAIN_UNRESOLVED:
              'CoA assignment history cannot identify the transition safely; review the entity and book assignments',
            CONTRACT_REVENUE_ROOT_CROSSWALK_UNMAPPED:
              'Root account has no entry in the CoA version crosswalk',
            CONTRACT_REVENUE_ACCOUNT_CROSSWALK_UNMAPPED:
              'Revenue account has no entry in the CoA version crosswalk',
            CONTRACT_REVENUE_ROOT_CROSSWALK_SLOT_CONFLICT:
              'Version crosswalk and system-account slot disagree on the root account',
            CONTRACT_REVENUE_ROOT_CROSSWALK_NATURAL_BALANCE_MISMATCH:
              'Account natural balance changes between CoA versions',
            CONTRACT_REVENUE_MULTIPLE_BALANCE_CARRIERS:
              'More than one CoA version carries the contract balance; reconcile duplicated carried openings',
            CONTRACT_REVENUE_COA_SCOPE_UNRESOLVED:
              'Could not resolve the accounting scope for the date',
            CONTRACT_REVENUE_NO_ACTIVE_COA_VERSION: 'No CoA version is in force on the date',
            CONTRACT_REVENUE_ACCOUNT_MAPPING_MISSING:
              'Contract Asset / Liability accounts are not configured',
            CONTRACT_REVENUE_USAGE_PERIOD_NOT_ELAPSED: 'Usage period has not elapsed yet',
            CONTRACT_REVENUE_ZERO_AMOUNT: 'Recognized amount is zero',
            CONTRACT_REVENUE_SCHEDULE_NOT_ACTIVE: 'Schedule is not ACTIVE',
            CONTRACT_REVENUE_ENTRY_NOT_PENDING: 'Entry is no longer PENDING',
            CONTRACT_REVENUE_ENTRY_NOT_FOUND: 'Entry not found',
            CONTRACT_ACCOUNT_CONTRACT_NOT_FOUND:
              'Contract of the Contract Asset/Liability account not found; the account cannot be created without the contract code and customer',
            NO_OPEN_PERIOD: 'No OPEN GL period for the posting date',
            UNKNOWN: 'Reason not reported',
          },
          errorCodeFallback: 'Code: {{code}}',
          contractLink: 'Open contract',
          empty: 'No due entries for this competency.',
        },
      },
    },
    details: {
      back: 'Back to contracts',
      startDate: 'Start date',
      createdAt: 'Created at',
      current: 'Current',
      historical: 'Historical',
      loadingSection: 'Loading...',
      openCustomerInNewTab: 'Open customer in new tab',
      openTemplateInNewTab: 'Open template in new tab',
      openServiceInNewTab: 'Open service in new tab',
      days: '{{count}} day(s)',
      months: '{{count}} month(s)',
      singleInstallment: 'Single installment',
      installments: '{{count}} installments',
      installmentsWithInterval: '{{count}} installments every {{days}} day(s)',
      versionItemCount: '{{count}} item(s)',
      sections: {
        summary: 'Summary',
        terms: 'Terms',
        renewalPreview: 'Renewal adjustment preview',
        versionHistory: 'Version history',
        pricingExceptions: 'Pricing exceptions',
        milestones: 'Milestones',
        usage: 'Usage',
        revenueSchedule: 'Revenue recognition',
        recognitionEvidence: 'Completion evidence',
      },
      fields: {
        customerCode: 'Customer code',
        template: 'Source template',
        updatedAt: 'Updated at',
        termDuration: 'Term duration',
        endDate: 'Term end',
        nextRenewalDate: 'Next renewal',
        renewalAdjustment: 'Adjustment',
        renewalWindow: 'Index window',
        renewalLag: 'Index lag',
        renewalFloorAtZero: 'Floor at zero',
        cancellationNotice: 'Cancellation notice',
        billingFrequency: 'Frequency',
        billingDay: 'Billing day',
        revenueRecognition: 'Revenue recognition',
        invoiceOrigin: 'Invoice origin',
        settlementModel: 'Settlement model',
        autoApproveTransactions: 'Transaction approval',
        proRataFirstCycle: 'Pro-rata first cycle',
        paymentMethod: 'Payment method',
        installments: 'Installments',
        renewalPreviewAsOf: 'Next adjustment date',
        renewalPreviewApplied: 'Applied adjustment',
        renewalPreviewAccumulated: 'Accumulated variation',
        renewalPreviewFloorApplied: 'Floor applied',
        renewalPreviewBlocked: 'Adjustment blocked',
        renewalPreviewBlockReason: 'Reason',
        renewalPreviewIndexFallback: 'selected index',
        renewalPreviewMissingObservationLabel: 'Index data unavailable',
        renewalPreviewMissingObservationDescription:
          'Index {{index}} does not have observations for the entire period required to calculate the adjustment. Wait for the data to be published and try again.',
        renewalPreviewNoEligibleObservationLabel: 'Index data not yet eligible',
        renewalPreviewNoEligibleObservationDescription:
          'There is not yet an observation for index {{index}} that is eligible for this calculation, considering the configured lag. Wait for the data to be published and try again.',
        renewalPreviewMissingAdjustmentValueLabel: 'Incomplete adjustment setup',
        renewalPreviewMissingAdjustmentValueDescription:
          'Set the contract adjustment percentage and try again.',
        renewalPreviewInvalidInputLabel: 'Invalid adjustment setup',
        renewalPreviewInvalidInputDescription: 'Review the adjustment setup and try again.',
        renewalPreviewUnknownBlockReason:
          'The adjustment could not be calculated with the current setup. Review the data and try again.',
        renewalPreviewObservations: 'Observations used',
        renewalPreviewFormula: 'Formula',
        renewalPreviewLoading: 'Computing adjustment…',
        renewalPreviewError: 'Could not compute the adjustment',
        renewalPreviewNone: 'No contractual adjustment',
        renewalPreviewObservationDate: 'Reference date',
        renewalPreviewObservationValue: 'Value (%)',
      },
      itemColumns: {
        service: 'Service',
        billablePrice: 'Billable price',
        ssp: 'Standalone price',
        allocatedAmount: 'Allocated amount',
        billingTiming: 'Billing',
        servicePeriod: 'Period',
        recognition: 'Recognition',
        actions: 'Actions',
      },
      versionColumns: {
        version: 'Version',
        effectiveFrom: 'Effective from',
        createdAt: 'Created at',
        items: 'Items',
        state: 'State',
      },
      related: {
        service: 'Service',
        requestedPrice: 'Requested price',
        floorPrice: 'Floor',
        reason: 'Reason',
        approvedAt: 'Approved at',
        name: 'Name',
        amount: 'Amount',
        status: 'Status',
        billedAt: 'Billed at',
        period: 'Period',
        quantity: 'Quantity',
        source: 'Source',
        createdAt: 'Created at',
        recognitionAmount: 'Recognition amount',
        recognizedAt: 'Recognized at',
        finalizedAt: 'Finalized at',
      },
      empty: {
        items: 'No items found.',
        pricingExceptions: 'No pricing exceptions recorded.',
        milestones: 'No milestones recorded.',
        usage: 'No usage recorded.',
        revenueSchedule: 'No recognition schedule generated yet.',
        recognitionEvidence: 'No completion evidence recorded.',
      },
      recognitionEvidence: {
        deliveryTitle: 'Record delivery/acceptance',
        milestoneTitle: 'Record milestone completion',
        dialogDescription:
          'Recording this evidence recognizes the revenue of {{obligation}}. State when the ' +
          'performance happened and which proof supports it.',
        evidenceDate: 'Effective date',
        evidenceDateHint:
          'When the delivery/completion actually happened — this sets the recognition ' +
          'competency. May be backdated, never future.',
        reference: 'Proof',
        referencePlaceholder: 'Acceptance certificate, signed report, ticket…',
        referenceHint: 'Required: a revenue event with no verifiable support is not evidence.',
        notes: 'Notes',
        immutabilityHint:
          'Evidence is immutable. A correction means reversing the recognition and recording ' +
          'fresh evidence — history is never edited.',
        submit: 'Record evidence',
        submitting: 'Recording…',
        cancel: 'Cancel',
        recordDelivery: 'Record delivery',
        recordCompletion: 'Record completion',
        delivered: 'Delivery recorded',
        completed: 'Completed',
        recordSuccess: 'Evidence recorded and revenue recognized.',
        recordedAt: 'Recorded at',
        kind: 'Type',
        kinds: {
          DELIVERY_ACCEPTANCE: 'Delivery/acceptance',
          MILESTONE_COMPLETION: 'Milestone completion',
        },
      },
      revenueSchedule: {
        generate: 'Generate schedule',
        generating: 'Generating…',
        generateSuccess: 'Revenue-recognition schedule generated.',
        total: 'Total',
        competency: 'Competency',
        issProvisioned: 'ISS provisioned',
        issProvisionedTooltip:
          'ISS provisioned on revenue already recognized (earned) but not yet invoiced (ALC-1259).',
        issProvisionedNone: 'No ISS provisioned for this competency.',
        entryStatus: {
          PENDING: 'Pending',
          POSTED: 'Posted',
          REVERSED: 'Reversed',
          CANCELLED: 'Cancelled',
        },
        glPostingDate: 'Posted on {{date}}',
        journals: 'Journals',
        journalOriginal: 'Original',
        journalReversal: 'Reversal',
        journalReversed: 'Reversed',
        correctionOf: 'Correction of the reversed competency',
        correctedBy: 'Corrected by a new entry',
        reversedAt: 'Reversed on {{date}}',
        actions: {
          recognize: 'Recognize',
          reverse: 'Reverse',
          correct: 'Correct',
        },
        awaiting: {
          invoice: 'Awaiting invoice',
          invoiceHint:
            "This period's revenue can only be recognized once its invoice is approved and posted to the GL.",
          usage: 'Awaiting finalized usage',
          usageHint:
            "This period's revenue can only be recognized once the period's usage is finalized.",
        },
        recognize: {
          success: 'Revenue recognized.',
        },
        reverse: {
          title: 'Reverse recognition',
          description:
            'A counter-journal is posted in every book the original posted to. Posted history is preserved — nothing is deleted.',
          reasonLabel: 'Reversal reason',
          reasonPlaceholder: 'e.g. service not delivered in this competency',
          dateLabel: 'Reversal date',
          dateHint: 'If the period is closed, the reversal moves forward to the next open period.',
          confirm: 'Reverse',
          success: 'Recognition reversed on {{date}}.',
        },
        correct: {
          title: 'Correct reversed recognition',
          description:
            'Creates a new pending entry for the same competency. Recognize it to post the corrected amount.',
          amountLabel: 'Corrected amount',
          amountHint: 'Use 1.200,00 or 1200.00. A bare thousands separator (1.200) is ambiguous.',
          amountInvalid: 'Invalid amount. Use 1.200,00 or 1200.00, with at most 2 decimal places.',
          reasonLabel: 'Correction reason',
          reasonPlaceholder: 'e.g. amount per the signed amendment',
          confirm: 'Schedule correction',
          success: 'Correction scheduled for competency {{period}}.',
        },
      },
      renewal: {
        fixedPercentage: '{{value}}% fixed',
        indexBased: 'Indexed by {{index}}',
      },
      billingTiming: {
        ADVANCE: 'In advance',
        ARREARS: 'In arrears',
      },
      servicePeriodRule: {
        CALENDAR_MONTH: 'Calendar month',
        ANNIVERSARY_DATE: 'Anniversary date',
      },
      recognitionMethod: {
        POINT_IN_TIME_AT_DELIVERY: 'At delivery',
        STRAIGHT_LINE_OVER_TIME: 'Straight-line over time',
        USAGE_AS_CONSUMED: 'As consumed',
        PROGRESS_MILESTONES: 'By milestones',
      },
      milestoneStatus: {
        PENDING: 'Pending',
        READY_TO_BILL: 'Ready to bill',
        BILLED: 'Billed',
        BLOCKED: 'Blocked',
        CANCELLED: 'Cancelled',
      },
      milestones: {
        createButton: 'New milestone',
        form: {
          createTitle: 'New milestone',
          createDescription:
            'Milestones control billing and/or revenue recognition independently — a ' +
            'milestone can bill, recognize, both, or neither.',
          name: 'Name',
          namePlaceholder: 'e.g. Phase 1 delivery',
          item: 'Contract item',
          itemNone: 'No item (contract-level milestone)',
          itemIneligibleSuffix: '(does not recognize by milestone)',
          noItemsAvailable:
            'This contract has no current items yet — activate the contract to link the ' +
            'milestone to an item.',
          bills: 'Bills on completion',
          billsHint:
            'When off, this milestone never generates an invoice — use it for tracking or ' +
            'recognition only.',
          amount: 'Billing amount',
          recognitionAmount: 'Recognition amount',
          recognitionEmptyHint:
            'Leave blank for a milestone that does not recognize revenue — never use zero.',
          recognitionNeedsItemHint: 'Select a contract item to recognize revenue.',
          recognitionIneligibleHint:
            'This item does not recognize revenue milestone by milestone — its revenue is ' +
            "already recognized by the item's own schedule.",
          submit: 'Create milestone',
          submitting: 'Creating…',
          cancel: 'Cancel',
          successCreate: 'Milestone "{{name}}" created.',
        },
        errors: {
          nonBillingAmount: 'A milestone that does not bill must have amount zero.',
          recognitionNonPositive:
            'The recognition amount must be greater than zero — leave it blank for a ' +
            'milestone that does not recognize revenue, never use zero.',
          recognitionMethodMismatch:
            'This item is not recognized by milestones — remove the recognition amount or ' +
            'choose another item.',
          recognitionRequired:
            'This item recognizes revenue by milestone; a billing milestone must carry the ' +
            'recognition amount it earns.',
          amountDivergenceUnsupported:
            'When the milestone bills, the recognition amount must equal the billing amount.',
          recognitionWithoutItem: 'A recognition amount requires a linked contract item.',
        },
        ready: {
          action: 'Mark ready to bill',
          confirmTitle: 'Mark "{{name}}" ready to bill?',
          confirmDescription:
            'The milestone enters the next billing cycle (or the next "Bill" run). For ' +
            'milestones, billing and revenue recognition happen together — the completion ' +
            'evidence recorded afterward is what actually recognizes the linked revenue.',
          confirm: 'Mark ready',
          success: '"{{name}}" is ready to bill.',
          notBillable:
            'This milestone is configured not to bill — its completion is recorded through ' +
            'evidence, not the billing flow.',
          notBillableHint: 'Does not bill — completion recorded via evidence',
          needsItemHint: 'No linked item — correct the item before marking ready',
          contractNotActive: 'Milestones can only be marked ready to bill on an active contract.',
          milestoneNotPending:
            'This milestone is no longer pending — refresh to see its current status.',
          noItem:
            'This milestone has no linked item, so there is no revenue classification to ' +
            'bill against — correct the item first.',
        },
        itemCorrection: {
          action: 'Correct item',
          dialogTitle: 'Correct the item on "{{name}}"',
          description:
            'Correcting the item moves the milestone back to Pending. You will need to mark ' +
            'it ready to bill again — that re-authorization does not happen automatically.',
          submit: 'Correct item',
          success: 'Item on "{{name}}" corrected — milestone is back to Pending.',
          historyPrefix: 'Previously corrected from',
          reauthorizeHint:
            'After saving, mark the milestone ready to bill again whenever you want.',
        },
        allocations: {
          action: 'Correct allocations',
          title: 'Correct allocations for "{{name}}"',
          description:
            'Corrects the billing amount and/or the recognition amount before the milestone ' +
            'has billed or recognized. Every correction is recorded in the audit trail.',
          submit: 'Save correction',
          success: 'Allocations for "{{name}}" corrected.',
          nonBillingHint: 'This milestone does not bill — the billing amount stays zero.',
          noItemHint: 'No linked item — this milestone cannot recognize revenue.',
        },
        runningTotal: {
          title: 'Total allocated to the item',
          itemAllocated: "Item's allocated consideration",
          alreadyAllocated: 'Already allocated by other milestones',
          thisMilestone: 'This milestone',
          matched:
            "The total matches the item's allocation — ready to recognize revenue once completed.",
          remainingPositive: "{{amount}} still needed to complete the item's allocation.",
          remainingNegative: "Exceeds the item's allocation by {{amount}}.",
          ineligibleNote:
            'This item does not recognize revenue milestone by milestone — the recognition ' +
            'amount does not apply here.',
        },
      },
      pricingExceptions: {
        recordButton: 'Record pricing exception',
        recordNewButton: 'Record new pricing exception',
        consumption: {
          CONSUMED: 'Consumed',
          PENDING: 'Pending',
          REJECTED: 'Amendment rejected',
        },
        driftWarning:
          'Current price {{price}} no longer matches the approved value — this exception no ' +
          'longer covers the current price.',
        activationBlocked: {
          missing:
            'The price for {{services}} is below the service floor and needs an approved ' +
            'pricing exception before activation.',
          selfApproved:
            'The pricing exception for {{services}} was approved by you. Under segregation of ' +
            'duties, another authorized user must activate the contract.',
          priceDrift:
            'The price for {{services}} changed after the pricing exception was approved. ' +
            'Record a new exception for the current price.',
        },
        dialog: {
          title: 'Record pricing exception',
          description:
            'Approves a price below the service commercial floor. Recorded under your name as ' +
            'the approver.',
          contextLabel: 'Apply to',
          contextActivation: 'Contract activation (draft items)',
          contextAmendment: 'Pending amendment — effective {{date}}',
          noContextHint:
            'There are no draft items or pending amendments to record a pricing exception against.',
          serviceLabel: 'Service',
          serviceEmptyHint: 'No item in this context is currently below the floor.',
          requestedPriceLabel: 'Requested price',
          floorPriceLabel: 'Service floor',
          gapLabel: 'Below the floor by',
          authorizationWarning:
            'You are authorizing a commercial price below the service floor. This approval is ' +
            'recorded under your name and may be used to commit the price at activation or ' +
            'amendment approval.',
          reasonLabel: 'Reason',
          reasonPlaceholder: 'Justify why this below-floor price should be authorized…',
          submit: 'Approve exception',
          submitting: 'Recording…',
          cancel: 'Cancel',
          success: 'Pricing exception recorded for {{service}} ({{price}}).',
          errors: {
            duplicate:
              'An approved exception already exists for this service in this context — another ' +
              'cannot be recorded. If the price changed, set it back to the approved value or ' +
              'ask an administrator to review the existing exception.',
            amendmentNotPending:
              'This amendment is no longer pending — refresh the page to see its current status.',
            notBelowFloor:
              'This item is no longer below the service floor — no exception is needed.',
          },
        },
      },
      usageSource: {
        MANUAL: 'Manual',
        CSV: 'CSV',
        API: 'API',
      },
      usageStatus: {
        OPEN: 'Open',
        FINALIZED: 'Finalized',
        SUPERSEDED: 'Superseded',
      },
      usage: {
        createButton: 'New record',
        chain: {
          finalizedAt: 'Finalized at {{date}}',
          supersededAt: 'Superseded at {{date}}',
          correctsRecord: 'Corrects record {{id}}…',
        },
        form: {
          createTitle: 'Record usage',
          createDescription:
            'Manually records usage consumed against an ARREARS-billed item. The record starts ' +
            'as Open — finalize it so it counts toward billing.',
          correctTitle: 'Correct usage',
          correctDescription:
            'Creates a new usage record for the same item and period start. Finalizing it ' +
            'automatically supersedes the prior finalized record — that is how a usage ' +
            'correction works.',
          item: 'Contract item',
          itemPlaceholder: 'Select an item',
          itemIneligibleSuffix: '(not ARREARS-billed)',
          noItemsAvailable:
            'This contract has no current-version items yet — activate the contract to record usage.',
          noEligibleItems:
            'No item on this contract is ARREARS-billed — usage can only be recorded for items ' +
            'with that billing timing.',
          periodStart: 'Period start',
          periodEnd: 'Period end',
          periodOrderInvalid: 'Period end cannot be before period start.',
          quantity: 'Quantity',
          source: 'Source',
          sourceManualHint:
            'Usage entered on this screen is always recorded as manual. Integration provenance ' +
            'can only be set by the authenticated integration itself, so the audit trail never ' +
            'asserts an origin the system has not proven.',
          externalId: 'External ID',
          externalIdPlaceholder: 'Optional — e.g. source invoice number',
          externalIdHint:
            'Idempotency key, unique per (tenant, source) — tenant-wide, not scoped to this ' +
            'contract. Resubmitting the same external ID for this contract is a silent no-op; ' +
            'for a different contract, the API rejects it as a conflict. If you reuse a number ' +
            '(e.g. an invoice number) across contracts, use a distinct ID for each.',
          cancel: 'Cancel',
          submit: 'Record usage',
          submitCorrection: 'Record correction',
          submitting: 'Submitting…',
          success: 'Usage recorded — it is Open and still needs to be finalized.',
          errors: {
            externalIdConflict:
              'This external ID was already used for a different contract — the idempotency ' +
              'key is tenant-wide, not scoped to this contract. Use a distinct ID.',
            itemNotFound: 'This contract item was not found — refresh the page.',
            notArrears: 'Usage can only be recorded for ARREARS-billed items.',
            periodOrder: 'Period end cannot be before period start.',
          },
        },
        correction: {
          action: 'Correct',
          success: 'Correction recorded — finalize the new record so it takes effect.',
        },
        finalize: {
          action: 'Finalize usage',
          confirmTitle: 'Finalize this usage record?',
          confirmDescription:
            'Finalizing supersedes any prior finalized record for the same item and period ' +
            'start (which moves to Superseded), triggers Contract Asset recognition for ' +
            'usage-as-consumed obligations, and re-checks staleness on affected draft invoices ' +
            '— this can move an invoice you are currently viewing.',
          confirm: 'Finalize',
          success: 'Usage finalized.',
          errors: {
            notFound: 'This usage record was not found — refresh the page.',
            superseded:
              'This record was already superseded by a later finalization and cannot be finalized again.',
            conflict:
              'Another concurrent finalization for the same period is already in progress. ' +
              'Refresh the page and try again.',
          },
        },
        import: {
          openButton: 'Import CSV',
          title: 'Import usage records from CSV',
          description:
            'Uploads a CSV file with bulk usage for ARREARS-billed items. Rows with a problem ' +
            'do not stop the rest — the result shows the total imported and what failed, row ' +
            'by row.',
          warningHeader:
            'The header row must be exactly: contractItemId,periodStart,periodEnd,quantity,externalId',
          warningNoQuoting:
            'The file parser splits each row on commas with no quoting or escaping support — a ' +
            'comma inside a field (e.g. free text) corrupts the entire row.',
          warningPartial:
            'The import never aborts on a bad row — every row is processed independently.',
          downloadTemplate: 'Download CSV template',
          dropHint: 'Select a CSV file to import',
          selectFile: 'Select file',
          removeFile: 'Remove file',
          submit: 'Import',
          submitting: 'Importing…',
          cancel: 'Cancel',
          close: 'Close',
          tryAnother: 'Import another file',
          importedCount: 'Imported',
          failedCount: 'Failed',
          errorsTitle: 'Failed rows',
          errorRow: 'Row {{row}} of the file:',
        },
        pagination: {
          summary: '{{shown}} of {{total}} records',
          loadMore: 'Load more',
          loadingMore: 'Loading…',
        },
      },
      actions: {
        activateTitle: 'Activate contract',
        activateDescription: 'Activation creates the first immutable version from the draft items.',
        activateSuccess: 'Contract activated.',
        activationFailure: {
          title: 'Could not activate contract',
          description: 'Correct the configuration and try to activate it again.',
          stay: 'Stay on this page',
          backToList: 'Back to contracts',
        },
        previewLoading: 'Calculating affected competencies...',
        backdatedBillingPreview:
          '{{count}} already-due billing cycle(s) will be generated after confirmation.',
        backdatedRecognitionPreview:
          '{{count}} already-due recognition competencies will be handled by catch-up after confirmation.',
        previewBillingDate: 'Billing date',
        previewCompetency: 'Competency',
        previewServicePeriod: 'Service period',
        suspendTitle: 'Suspend contract',
        suspendDescription: 'Suspension pauses this contract until it is reactivated.',
        suspendSuccess: 'Contract suspended.',
        reactivateTitle: 'Reactivate contract',
        reactivateDescription: 'Reactivation moves this contract back to active.',
        reactivateSuccess: 'Contract reactivated.',
        cancelTitle: 'Cancel contract',
        cancelDescription: 'Cancellation is final and cannot be undone.',
        cancelConsequenceRecognize:
          'Any revenue already earned in closed competencies but still pending posting is automatically recognized before cancellation — once cancelled, it can never be recovered.',
        cancelConsequenceFuture:
          'Future revenue that has not yet accrued is cancelled and the recognition schedules are permanently retired — nothing more will be generated from this contract.',
        cancelConsequenceBilling: 'Billing for this contract stops immediately.',
        cancelSuspendContrast:
          'Unlike suspension — which only cancels not-yet-elapsed entries, keeps the schedules ACTIVE, and can be reversed by reactivating — cancellation is terminal.',
        cancelConfirm: 'Confirm cancellation',
        cancelSuccess:
          'Contract cancelled. Already-earned revenue was recognized and future revenue was cancelled.',
        cancelUsageGuard: {
          description_one:
            'There is pending usage for 1 usage based service. Zero will be recorded for it:',
          description_other:
            'There is pending usage for {{count}} usage based services. Zero will be recorded for them:',
          periodLabel: '{{start}} – {{end}}',
          viewUsageButton: 'View usage records',
        },
        cancelResolution: {
          title: 'Resolve pending items and cancel',
          description:
            'Review the adjustments below. Usage will be recorded as zero and the contract will then be cancelled.',
          zeroUsageTitle: 'Record zero quantity for {{count}} pending period(s)',
          zeroUsagePermission:
            'You do not have permission to record and finalize usage for these periods.',
          reverseRevenueTitle: 'Reverse revenue not yet invoiced:',
          reverseRevenueDescription:
            '{{count}} posting(s) with no amount reclassified by an issued invoice. Reversal and cancellation run in the same transaction.',
          reverseRevenuePermission:
            'You do not have permission to reverse revenue. The contract can be cancelled without this adjustment.',
          noUnissuedRevenue: 'There is no posted, unissued revenue to reverse.',
          confirm: 'Record zero and cancel',
          success: 'Pending items were resolved and the contract was cancelled.',
        },
      },
    },
    activationErrors: {
      customerEntityMismatch:
        'The selected customer does not belong to the contract entity. Pick a customer linked to this entity.',
      templateEntityMismatch:
        'The selected template does not belong to the contract entity. Pick a template of this entity.',
      backdatedConfirmation:
        'Activation has pending earlier periods and requires your explicit confirmation.',
      revenueAccountMappingMissing:
        'The contract cannot be activated because the active chart-of-accounts version does not have the Contract Asset and Contract Liability system accounts. Configure both accounts and try again.',
      revenueAccountMappingAction: 'Configure system accounts',
      custodialPayableUnmapped:
        'A custodial contract needs the "Custodial partner payable" system account mapped on the entity\'s chart of accounts. Map it before activating.',
      custodialPayableAction: 'Map the partner payable account',
      standaloneSellingPriceRequired:
        'The initial contract version needs to allocate consideration by standalone selling price (SSP). Configure a positive SSP for service {{service}} before activation.',
      meteredTemporaryItem:
        'Another item in this contract has a temporary condition. On metered service {{service}}, Recurrence may remain Always; set Default billing to Arrears and configure the service default Revenue recognition as As consumed.',
      draftOnly: 'Only draft contracts can be activated.',
      startDateRequired: 'Set a start date before activating the contract.',
      termDurationRequired:
        'Set a valid duration for the initial term before activating the contract.',
      itemsRequired: 'Add at least one service to the contract before activating it.',
      functionalCurrencyUnresolved:
        "The entity's functional currency could not be determined. Configure its accounting book and try again.",
      functionalCurrencyMismatch:
        'The contract currency must match the entity functional currency.',
      serviceNotFound: 'A contract service was not found. Review the items and try again.',
      serviceInactive:
        'A contract service is inactive. Activate or replace it before activating the contract.',
      serviceCurrencyMismatch: 'A contract service currency does not match the contract currency.',
      serviceFiscalMappingMissing:
        'A contract service has no fiscal classification or revenue account. Complete that configuration before activating.',
      meteredAdvanceBilling:
        'A metered service cannot be billed in advance because usage is known only after the period. Use arrears billing.',
      termEndRequired:
        'The contract term end could not be calculated. Review the start date and duration.',
      timedServiceRecognition:
        'Items with temporary terms require straight-line-over-time revenue recognition.',
      timedTransitionInPast:
        'The temporary terms would transition before the activation date. Review their durations or the start date.',
      introductoryTermsPairRequired:
        'Set both the introductory price and the number of introductory months.',
      timedTermsExceedTerm: 'A temporary term must end before the contract initial term ends.',
      emptyTimedVersion: 'Temporary terms cannot leave a contract version with no services.',
      timedTermsMonthlyOnly: 'Temporary terms are supported only on monthly contracts.',
      regularPriceInvalid: 'A service regular price must be a non-negative value.',
      introductoryPriceInvalid:
        'An introductory price must be non-negative and lower than the service regular price.',
      timedDurationInvalid: 'A temporary term duration must be a positive whole number of months.',
      introductoryDurationInvalid:
        'The introductory duration must be shorter than the service inclusion duration.',
      pricingPermissionRequired:
        'You do not have permission to activate contracts that require a pricing-exception check.',
      billablePriceInvalid: 'A service billable price is invalid. Review the value and try again.',
      pricingExceptionRequired:
        'A price is below the service floor and requires an approved pricing exception before activation.',
      pricingExceptionSelfApproval:
        'You approved this pricing exception. Another authorized user must activate the contract.',
      pricingExceptionPriceDrift:
        'The current price is below the approved exception value. Adjust the price or request a new exception.',
      mixedBillingTiming:
        'SSP allocation does not yet support bundled services with different billing timing.',
      mixedRecognition:
        'SSP allocation does not yet support bundled services that mix deferred and non-deferred recognition.',
      contractNotFound: 'This contract was not found. Refresh the page and try again.',
      concurrentActivation:
        'The contract changed during activation. Refresh the page to check its status before trying again.',
      unknown: 'The contract could not be activated. Review the data and try again.',
    },
    form: {
      titleCreate: 'New contract',
      proposedToasts: {
        createdAndActivated: 'Contract created and activated successfully',
        draftSaved: 'Contract saved as draft',
        updated: 'Contract updated successfully',
      },
      titleEdit: 'Edit contract',
      fields: {
        entity: 'Entity',
        customer: 'Customer',
        responsibleUser: 'Responsible',
        responsibleUserPlaceholder: 'Select responsible user',
        code: 'Code',
        codeHint: 'Leave empty to auto-generate',
        currency: 'Currency',
        currencyHint: 'The contract commercial currency. All added services use this currency.',
        currencyNoEntity: 'Select an entity to see the currency',
        currencyUnresolved: 'Select a supported commercial currency for the contract.',
        startDate: 'Start date',
        termDurationMonths: 'Duration (months)',
        termDurationMonthsHint:
          'Length of the initial contractual term. Required to activate the contract. Not the same as the renewal duration, which sizes the term after it.',
        paymentTermsDays: 'Payment terms (days)',
        paymentTermsDaysHint:
          'Days between issue and due date. Leave blank (or 0) to make it due on the issue date.',
        paymentTermsDaysPlaceholder: 'e.g. 30',
        billingFrequency: 'Billing frequency',
        billingDayOfMonth: 'Billing day',
        billingDayAnchor: 'Day anchor',
        billingDayFromStartDate: 'Based on start date',
        paymentMethod: 'Payment method',
        installmentCount: 'Installment count',
        installmentCountHint: 'Each generated invoice is split into this number of installments.',
        installmentIntervalDays: 'Days between installments',
        installmentIntervalDaysHint:
          'Days between installment due dates. Applies only when there is more than one installment.',
        notApplicable: 'Not applicable',
        items: 'Items',
        service: 'Service',
        billablePrice: 'Billable price',
        regularPricePlaceholder: 'Regular price',
        introductoryPricePlaceholder: 'Introductory price',
        introductoryMonthsPlaceholder: 'Introductory months',
        includeForMonthsPlaceholder: 'Include for X months',
        timedTermsHint:
          'Durations are monthly billing periods; a prorated first period counts as period 1.',
      },
      termsDescription: 'Billing settings recorded for this contract.',
      billingSummary: {
        title: 'Billing summary',
        fields: {
          frequency: 'Frequency',
          billingDay: 'Billing day',
          paymentMethod: 'Payment method',
          dueDate: 'Due date',
          installments: 'Installments',
        },
        values: {
          customerDefault: 'Customer default',
          billingDay: 'Day {{day}}',
          startDate: 'Based on start date',
          paymentOnIssueDate: 'On the issue date',
          paymentTerms: '{{count}} days after issue',
          singleInstallment: 'Single installment',
          installments: '{{count}} installments',
          installmentsWithInterval: '{{count}} installments every {{days}} days',
        },
        messages: {
          singleInstallment: 'Each invoice will be billed in one installment.',
          multipleInstallments: 'Each invoice will be split into {{count}} installments.',
        },
        sequence: {
          title: 'Billing sequence',
          invoice: 'Recurring invoice',
          invoiceDetail: 'Issued {{frequency}}.',
          firstInstallment: '1st installment',
          remainingInstallments: '2nd through {{count}}th installment',
          remainingInstallmentsDetail: 'Every {{days}} days.',
        },
      },
      actions: {
        addItem: 'Add item',
        create: 'Create contract',
        save: 'Save',
        continue: 'Continue',
      },
      createDecision: {
        title: 'How do you want to create the contract?',
        description: 'You can leave it as a draft to review later or activate it now.',
        saveDraft: 'Save draft',
        activate: 'Create and activate',
        cancel: 'Cancel',
      },
      saveFailure: {
        title: 'Could not save contract',
        stay: 'Keep editing',
        backToList: 'Back to contracts',
      },
      activationFailure: {
        title: 'Could not activate contract',
        description: 'Correct the item configuration and try activating again.',
        backdatedDescription:
          'Activation will generate {{billing}} billing cycle(s) and handle {{recognition}} already-started recognition period(s). Confirm only if you want to record these effects now.',
        stay: 'Keep editing',
        backToList: 'Back to contracts',
        confirmBackdated: 'Confirm backdated activation',
      },
      activationSuccess: {
        title: 'Contract activated',
        description: 'The first contract version was created successfully.',
        backToList: 'Back to contracts',
        viewContract: 'View contract',
      },
      steps: {
        info: 'Information',
        services: 'Services',
        terms: 'Commercial terms',
        renewal: 'Renewal',
      },
      emptyItems: 'No items added.',
      validation: {
        entityRequired: 'Entity is required',
        customerRequired: 'Customer is required',
        atLeastOneItem: 'At least one item is required',
        allItemsNeedService: 'All items must have a service selected',
        introductoryTermsRequired:
          'Enter the introductory price and introductory months for every item with introductory pricing enabled',
        itemPriceUnresolved:
          'Could not determine the price for one or more service items. Enter the price manually before saving.',
        currencyUnresolved: 'Select a supported commercial currency before saving.',
        paymentTermsInvalid: 'Payment terms must be a number with up to 3 digits',
        billingDayInvalid: 'Billing day must be between 1 and 28',
        installmentCountInvalid: 'Installment count must be a positive whole number',
        installmentIntervalInvalid: 'Days between installments must be a positive whole number',
        installmentIntervalRequired:
          'Enter the days between installments when there is more than one installment',
        termDurationInvalid: 'Term duration must be a number with up to 3 digits',
        activationStartDateRequired: 'Start date is required to activate the contract',
        activationTermDurationRequired: 'Duration (months) is required to activate the contract',
        // Proposed copy, not yet approved by the client.
        proposedRequired: {
          entityId: 'Enter the entity',
          customerId: 'Enter the customer',
          startDate: 'Enter the start date',
          termDurationMonths: 'Enter the duration',
          termDurationMonthsWithTemplate: 'Enter the duration when the template does not set one',
          installmentIntervalDays:
            'Enter the days between installments when there is more than one installment',
          renewalDurationMonths: 'Enter the renewal duration',
          renewalAdjustmentValue: 'Enter the adjustment percentage',
          renewalIndex: 'Enter the adjustment index',
          serviceId: 'Enter the service',
          introductoryPrice: 'Enter the introductory price',
          introductoryMonths: 'Enter the introductory months',
        },
      },
      tooltips: {
        templateDerived: 'Derived from the selected template',
      },
      placeholders: {
        termDurationMonths: 'e.g. 12',
      },
    },
    fromTemplate: {
      title: 'Create contract from template',
      subtitle: 'Select the customer, template, and start date',
      createButton: 'Create from template',
      fields: {
        template: 'Template',
        templatePlaceholder: 'Select a template',
        templateOptionalPlaceholder: 'No template (standalone contract)',
        customer: 'Customer',
        startDate: 'Start date',
        termDurationMonthsFromTemplate:
          'The template sets {{count}} months. Fill this in only to use a different term for this contract.',
        termDurationMonthsRequired:
          'This template sets no default duration — enter the term, or the contract cannot be activated.',
      },
      actions: {
        create: 'Create contract',
      },
      validation: {
        templateRequired: 'Template is required',
        customerRequired: 'Customer is required',
        startDateRequired: 'Start date is required',
        termDurationRequired:
          'Enter the term duration: the selected template sets no default duration.',
      },
    },
    amendments: {
      title: 'Contract amendments',
      newButton: 'New amendment',
      empty: 'No amendments recorded.',
      dialogDescription:
        "Proposes a change (upsell, downgrade, suspension, or cancellation) against the contract's current Version. Approval is required before it creates a new Version.",
      status: {
        PENDING: 'Pending',
        APPROVED: 'Approved',
        REJECTED: 'Rejected',
      },
      type: {
        UPSELL: 'Upsell',
        DOWNGRADE: 'Downgrade',
        SUSPENSION: 'Suspension',
        CANCELLATION: 'Cancellation',
        RENEWAL: 'Renewal',
      },
      fields: {
        amendmentType: 'Amendment type',
        reason: 'Reason',
        effectiveDate: 'Effective date',
        items: 'Proposed items',
      },
      actions: {
        create: 'Propose amendment',
        cancel: 'Cancel',
        approve: 'Approve',
        reject: 'Reject',
      },
      confirmReject: 'Are you sure you want to reject this amendment?',
      propose: {
        title: 'Propose contract amendment',
        reasonLabel: 'Reason',
        reasonPlaceholder: 'Explain the business reason for this amendment',
        reasonRequired: 'Enter the reason for the amendment.',
        effectiveDateLabel: 'Effective date',
        effectiveDateHint:
          "Must be later than the contract's current version effective date ({{date}}).",
        effectiveDateMustBeLater: 'The effective date must be later than {{date}}.',
        effectiveDateRequired: 'Enter the effective date.',
        itemsSectionTitle: 'Contract items after the amendment',
        itemsSectionHint:
          "This list fully replaces the contract's items — it is not a diff. Removing a row drops that service from the contract.",
        itemsRequired: 'The amendment must keep at least one item.',
        removedBadge: 'Removed',
        undoRemove: 'Undo removal',
        removeItem: 'Remove',
        addItem: 'Add item',
        addItemPlaceholder: 'Select a service',
        noAvailableServices: 'No other active services are available to add.',
        priceLabel: 'Billable price',
        standaloneSellingPriceLabel: 'Standalone selling price (SSP)',
        billingTimingLabel: 'Billing timing',
        servicePeriodRuleLabel: 'Service period rule',
        recognitionMethodLabel: 'Recognition method',
        keepDefault: '(keep default)',
        priceRequired: 'Enter a valid price (e.g. 1,200.00).',
        backdatedWarning:
          'This effective date is already in the past. The amendment will retroactively affect revenue for an already-closed period.',
        confirmBackdated: 'I confirm this amendment has a backdated effective date.',
        submit: 'Propose amendment',
        submitting: 'Submitting...',
        success: '{{type}} amendment proposed, effective {{date}}. Awaiting approval.',
      },
      detail: {
        title: 'Amendment detail',
        createdByLabel: 'Proposed by',
        createdAtLabel: 'Proposed at',
        decidedByLabel: 'Decided by',
        decidedAtLabel: 'Decided at',
        resultingVersionLabel: 'New contract version',
        diffTitle: 'Before/after comparison',
        diffEmpty: 'No item differences recorded.',
        diffBefore: 'Before',
        diffAfter: 'After',
        diffAdded: 'New item',
        diffRemoved: 'Removed item',
        diffChanged: 'Changed',
        diffUnchanged: 'Unchanged',
        notApplicable: 'Did not exist',
        loadError: 'Could not load the amendment detail.',
        close: 'Close',
        back: 'Back',
      },
      approveConfirm: {
        title: 'Approve contract amendment',
        description:
          'Approving creates a new contract version effective {{date}}, and revenue schedules are reallocated prospectively from that date. This action cannot be undone.',
        backdatedWarning:
          'The effective date ({{date}}) is already in the past. Revenue reallocation will be retroactive to an already-closed period.',
        confirmBackdated: 'I confirm approval with a backdated effective date.',
        confirm: 'Confirm approval',
        submitting: 'Approving...',
        success: 'Amendment approved — new version effective {{date}}.',
      },
      rejectConfirm: {
        title: 'Reject contract amendment',
        description:
          'Are you sure you want to reject this amendment? No contract version will be created.',
        confirm: 'Confirm rejection',
        submitting: 'Rejecting...',
        success: 'Amendment rejected.',
      },
      errors: {
        selfApproval:
          'This amendment was proposed by you. Under segregation of duties, another authorized user must approve it.',
        pricingPermissionRequired:
          'To approve, the user also needs the price-commit permission (ar:contract:pricing).',
        belowFloor:
          'The price for {{services}} is below the service floor and needs an approved pricing exception.',
        selfApprovedException:
          'The pricing exception for {{services}} was approved by you. Under segregation of duties, another authorized user must approve that exception before the price can be committed.',
        priceDrift:
          'The price for {{services}} changed after the pricing exception was approved. Record a new exception for the current price.',
        recordExceptionAction: 'Record pricing exception',
        recordNewExceptionAction: 'Record new pricing exception',
        unnamedServices: 'one or more contract services',
        staleStatus: 'This amendment is already "{{status}}". The list has been refreshed.',
        cycleMidWindow:
          'The effective date {{date}} falls inside the billing window {{start}}–{{end}}. Align the date with a billing-cycle boundary.',
        cyclePendingBilling:
          'There is a pending billing cycle ({{start}}–{{end}}) generated from the prior contract version. Void or issue it before approving.',
        backdatedConfirmationRequired: 'Confirm the backdated effective date to proceed.',
        overFundedObligation:
          'An already-issued invoice billed the service window this change would retire, and its deferred revenue is still sitting in contract liability. Reverse or credit-note that invoice before amending over the window.',
        revenueAlreadyRecognized:
          "Part of this service period's revenue has already been recognized in the P&L. Reverse those recognitions first — cancelling now would remove revenue from a period that may be closed.",
        versionSuperseded:
          'The service window belongs to a different contract version: an amendment took effect inside it. A window split across two versions cannot be billed or recognized as one. Re-bill the cycle under the current version.',
        contractBusy:
          'This contract is being amended or regenerated right now. Wait a moment and try again.',
        revenueAccountUnmapped:
          "The Contract Liability / Contract Asset accounts are not mapped on this entity's chart of accounts, and revenue cannot be deferred without them. Ask your ERP administrator to map them, then retry.",
        deferralLinkIncomplete:
          'This charge names a contract cycle whose revenue is recognized over time, but the invoice lines are not linked to that obligation — issuing now would recognize everything on the issue date. Re-send the charge to complete the link, then retry.',
      },
    },
  },
  chat: {
    close: 'Close assistant',
    open: 'Open assistant',
    title: 'Billing assistant',
    thinking: 'Thinking...',
    ready: 'Online',
    placeholder: 'Describe the service or ask for help...',
    fieldsUpdated: 'Fields updated',
    fieldCount: '{{count}} field(s)',
    quickPrompts: {
      saas: 'Register monthly SaaS',
      consulting: 'Hourly consulting',
      usage: 'Usage-based pricing',
      fiscalHelp: 'Fiscal help',
    },
  },
  summary: {
    title: 'Service summary',
    calculationMode: 'Calculation mode',
    entity: 'Entity',
    baseFee: 'Base fee',
    usageTiers: 'Usage tiers',
    usageTiersCount: '{{count}} tier(s)',
    usageTiersEmpty: 'No tiers',
    issRate: 'ISS',
    issMunicipality: 'ISS municipality',
    revenueAccount: 'Revenue account',
    nfsePreview: 'NFS-e preview',
  },
  simulator: {
    title: 'Pricing simulator',
    withQuantity: 'Simulation - {{quantity}} units',
    quantityLabel: 'Usage quantity',
    quantityPlaceholder: '0',
    enterQuantity: 'Enter a quantity to simulate.',
    fixedFee: 'Fixed fee',
    usageFee: 'Usage fee',
    minimumApplied: 'Minimum commitment applied',
    capApplied: 'Cap applied',
    total: 'Total',
    tierApplied: 'Applied tier: {{from}} - {{to}}',
    unlimited: 'No limit',
    noTier: 'No applicable tier',
  },
  detail: {
    sections: {
      summary: 'Summary',
      fiscal: 'Fiscal',
      accounting: 'Accounting',
      nfse: 'NFS-e',
    },
    nfse: {
      empty: 'No description provided.',
    },
  },
  selectors: {
    municipality: 'Select municipality',
    searchMunicipality: 'Search municipality...',
    searchMunicipalityHelp: 'Type at least 2 characters to search Focus NFe.',
    emptyMunicipalities: 'No municipalities available',
    noMunicipalitiesFound: 'No municipalities found.',
    cnae: 'Select CNAE',
    searchCnae: 'Type to search...',
    noCnaesFound: 'No CNAE codes found.',
    cnaeMinChars: 'Type at least 2 characters to search.',
    municipalServiceCode: 'Select municipal code',
    searchMunicipalServiceCode: 'Search by code or description',
    municipalServiceCodeMinChars: 'Type at least 2 characters to search.',
    noMunicipalServiceCodesFound: 'No codes found',
    loadMunicipalServiceCodesError: 'Failed to load municipal codes',
  },
  labels: {
    pricingShape: {
      fixed: 'Fixed',
      usage: 'Usage',
      fixedPlusUsage: 'Base + usage',
      unconfigured: 'Unconfigured',
    },
    calculationMode: {
      PER_UNIT: 'Per unit',
      GRADUATED: 'Graduated',
      VOLUME: 'Volume',
      STAIRSTEP: 'Stairstep',
      PERCENT_OF_GTV: '% of GTV',
    },
    billingFrequency: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semiannual: 'Semiannual',
      annual: 'Annual',
    },
    revenueRecognitionModel: {
      onFiscalDocument: 'On fiscal document',
      onSettlement: 'On settlement (acquirer report)',
    },
    invoiceOrigin: {
      billingCycle: 'Billing cycle by frequency',
      settlementAggregate:
        'Per-reservation receivables + pre-billing aggregate (no frequency invoice)',
      mixed: 'Mixed: individual items through the cycle, aggregated items through the aggregate',
    },
    settlementModel: {
      direct: 'Direct',
      custodial: 'Custodial (gross in transit, payout as a liability)',
    },
    billingDayAnchor: {
      fixedDay: 'Fixed day',
      lastDay: 'Last day of the month',
      lastBusinessDay: 'Last business day of the month',
    },
    autoApproveTransactions: {
      byRule: "By the contract's rule, on creation",
      manual: 'Manual — they stay as drafts',
    },
    paymentMethod: {
      bolepix: 'Bolepix',
      pix: 'PIX',
      transfer: 'Transfer',
    },
    renewalAdjustmentType: {
      none: 'No adjustment',
      fixedPercentage: 'Fixed percentage',
      indexBased: 'Index-based',
    },
    yes: 'Yes',
    no: 'No',
  },
  billingGroups: {
    list: {
      title: 'Billing groups',
      subtitle: 'Organize billing rules and preferences',
      searchPlaceholder: 'Search groups...',
      createButton: 'New group',
      loading: 'Loading groups...',
      errorTitle: 'Failed to load groups',
      errorDescription: 'We could not load billing groups.',
      retry: 'Try again',
      emptyTitle: 'No groups found',
      emptyDescription: 'Create a group to define default billing rules.',
      noCode: 'No code',
      filters: {
        status: {
          label: 'Status',
          all: 'All',
          active: 'Active',
          inactive: 'Inactive',
        },
      },
      status: {
        active: 'Active',
        inactive: 'Inactive',
      },
    },
    form: {
      titleNew: 'New billing group',
      titleEdit: 'Edit billing group',
      subtitle: 'Define billing rules and parameters',
      sections: {
        general: {
          title: 'General information',
          description: 'Identify and describe the group',
        },
        billing: {
          title: 'Billing rules',
          description: 'Default billing configuration',
        },
        status: {
          title: 'Status',
          description: 'Activate or deactivate this group',
        },
      },
      fields: {
        name: {
          label: 'Name',
          placeholder: 'e.g. Default group',
        },
        code: {
          label: 'Code',
          placeholder: 'BLG-001',
        },
        description: {
          label: 'Description',
          hint: 'Optional',
          placeholder: 'Describe when to use this group...',
        },
        boletoInstructions: {
          label: 'Boleto instructions',
          hint: 'Optional',
          placeholder: 'Add payment instructions...',
        },
        autoCollection: {
          label: 'Automatic collection',
          hint: 'Enable automatic charges.',
        },
        negativacao: {
          label: 'Negative listing',
          hint: 'Enable automatic negative listing.',
        },
        negativacaoDays: {
          label: 'Days to negative listing',
          hint: 'Days after due date',
          placeholder: 'e.g. 30',
        },
        communicationChannels: {
          label: 'Communication channels',
          hint: 'Comma separated',
          placeholder: 'email, whatsapp',
        },
        isActive: {
          label: 'Group active',
          hint: 'Inactive groups are hidden from selection.',
        },
      },
    },
    detail: {
      empty: 'Select a group to view details.',
      subtitle: 'Billing group details',
      sections: {
        general: {
          title: 'Summary',
          description: 'Main information about the group',
        },
        billing: {
          title: 'Billing configuration',
          description: 'Rules and channels configured',
        },
      },
      fields: {
        code: 'Code',
        description: 'Description',
        boletoInstructions: 'Boleto instructions',
        autoCollection: 'Automatic collection',
        negativacao: 'Negative listing',
        communicationChannels: 'Communication channels',
      },
    },
    actions: {
      cancel: 'Cancel',
      create: 'Create group',
      saveChanges: 'Save changes',
      edit: 'Edit',
      close: 'Close',
      deactivate: 'Deactivate',
      deactivateConfirm: {
        title: 'Deactivate "{{name}}"?',
        description:
          'The group stops appearing in selections for new customers. Customers already linked to it remain associated.',
        confirm: 'Confirm deactivation',
      },
    },
    common: {
      notSet: 'Not set',
      yes: 'Yes',
      no: 'No',
    },
  },
  actions: {
    cancel: 'Cancel',
    back: 'Back',
    create: 'Create service',
    createActive: 'Create and activate',
    saveDraft: 'Save draft',
    saveChanges: 'Save changes',
    activateService: 'Activate service',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    addTier: 'Add tier',
    remove: 'Remove',
  },
  deactivate: {
    trigger: 'Deactivate service',
    title: 'Deactivate "{{name}}"?',
    description:
      'The service becomes unavailable for new contract items. Contracts already using it are unaffected — this is a deactivation, not a permanent deletion.',
    confirm: 'Confirm deactivation',
    success: 'Service deactivated successfully.',
  },
  common: {
    saving: 'Saving...',
    loading: 'Loading...',
    noResults: 'No services found.',
    notSet: 'Not set',
    back: 'Back',
  },
  errors: {
    loadServices: 'Failed to load services',
    loadMunicipalities: 'Failed to load municipalities',
    loadCnaes: 'Failed to load CNAE codes',
  },
  saveErrorFields: {
    cnaeDescription: 'CNAE description',
    municipalServiceDescription: 'municipal service code description',
    issMunicipalityName: 'ISS municipality name',
    issMunicipalityState: 'ISS municipality state',
    glRevenueAccountId: 'revenue GL account',
    managerialAccountId: 'managerial account',
    costCenterId: 'cost center',
    nfseDescriptionTemplate: 'NFSe description',
    segments: 'segments',
    status: 'service status',
    classification: {
      root: 'accounting classification',
      glAccounts: 'GL account allocation',
      departments: 'department allocation',
      costCenters: 'cost center allocation',
      managerialAccounts: 'managerial account allocation',
      segments: 'segment allocation',
      customBreakdown: 'custom breakdown',
    },
  },
  saveErrors: {
    unauthorized: 'Your session has expired. Log in again and try saving the service.',
    forbidden:
      'You do not have permission to save this service. Ask an administrator for access if needed.',
    notFound:
      'This service was not found. Refresh the list and try again; it may have been removed or changed by someone else.',
    genericConflict:
      'We found a conflict while saving the service. Refresh the page, review the data, and try again.',
    genericInvalidRequest:
      'We could not save the service. Review the entered fields and try again.',
    genericServerError:
      'An internal error occurred while saving the service. Try again in a few moments; if it keeps happening, contact support.',
    genericServiceUnavailable:
      'The service is temporarily unavailable. Wait a moment and try saving again.',
    genericUnknown: 'We could not save the service right now. Review the data and try again.',
    duplicateCode:
      'A service with this code already exists. Use a different code or leave the field blank to generate one automatically.',
    duplicateExternalProductId:
      'This external ID is already linked to another service in the same entity. Enter a different ID or remove the value.',
    entityNotFound: 'The selected entity was not found. Choose another valid entity and try again.',
    costCenterNotFound:
      'The selected cost center does not belong to the service entity. Choose a cost center from the same entity.',
    costCenterInactive:
      'The selected cost center is inactive. Choose an active cost center and try again.',
    managerialAccountNotFound:
      'The selected managerial account does not belong to the service entity. Choose a managerial account from the same entity.',
    duplicateSegmentSelections:
      'There are duplicate segments in the classification. Remove the duplicate and try again.',
    segmentNotFound:
      'One of the selected segments was not found for this entity. Review the classification and choose a valid segment.',
    segmentValueNotFound:
      'One of the selected segment values was not found. Review the classification and choose a valid value.',
    fiscalServiceTypeNotFound:
      'The selected fiscal service no longer exists or is inactive. Choose another active fiscal service.',
    municipalityNotFound:
      'The selected ISS municipality was not found. Select a valid municipality again.',
    codeRequired:
      'The service code cannot be empty. Enter a valid code or clear the field to generate one automatically.',
    nameRequired: 'The service name is required. Fill it in and try again.',
    invalidDecimal:
      'One of the monetary values is in an invalid format. Review the numbers and try again.',
    usageTiersRequireCalculationMode:
      'Usage tiers require a calculation mode. Choose how variable pricing should be calculated before saving.',
    perUnitSingleTierOnly:
      'Per-unit pricing only allows a single tier. Remove the extra tiers and try again.',
    usageTierRangeStartNegative:
      'A usage tier cannot start with a negative value. Fix the tier start and try again.',
    usageTierRangeEndInvalid:
      'A tier end must be greater than its start. Adjust the ranges and try again.',
    usageTierOnlyLastOpenEnded:
      'Only the last tier can be open-ended. Fill in the intermediate limits and try again.',
    usageTierOverlap:
      'The usage tiers overlap. Adjust the ranges so one tier does not invade another.',
    stairstepRequiresFlatPrice:
      'Each stair-step tier needs a flat price. Fill in the tier price before saving.',
    usageTierUnitPriceRequired:
      '{{mode}} tiers require a unit price. Fill in the per-unit amount for each tier.',
    usageTierFloorAbovePrice:
      'A tier floor price cannot be greater than the tier price itself. Adjust the values and try again.',
    classificationGlRequired:
      'Add at least one GL account to the service classification before saving.',
    classificationCustomBreakdownRequired:
      'The custom breakdown must contain at least one filled row. Complete the distribution before saving.',
    classificationSegmentPercentTotal:
      'Segment percentages must total 100%. Adjust the distribution so the total closes.',
    classificationPercentTotal:
      '{{dimension}} percentages must total 100%. Adjust the distribution and try again.',
    classificationGlNotFound:
      'One of the selected GL accounts was not found. Choose a valid account and try again.',
    classificationGlInactive:
      'One of the selected GL accounts is inactive. Replace it with an active account to save the service.',
    classificationGlNotPostable:
      'The classification uses a non-postable GL account. Select a postable/detail account.',
    classificationGlCoaMismatch:
      'The selected GL account belongs to a different chart of accounts. Choose an account compatible with the entity.',
    classificationGlNotEffective:
      'One of the selected GL accounts is not yet effective for use. Choose another account or adjust the configuration.',
    classificationGlExpired:
      'One of the selected GL accounts is no longer effective. Choose a currently effective account to save the service.',
    classificationDepartmentNotFound:
      'One of the classification departments was not found for this entity. Review the selection and try again.',
    classificationCostCenterNotFound:
      'One of the classification cost centers was not found for this entity. Review the selection and try again.',
    classificationDepartmentInactive:
      'One of the classification departments is inactive. Choose an active department and try again.',
    classificationCostCenterInactive:
      'One of the classification cost centers is inactive. Choose an active cost center and try again.',
    classificationManagerialNotFound:
      'One of the selected managerial accounts was not found. Choose a valid account and try again.',
    classificationManagerialWrongEntity:
      'One of the selected managerial accounts belongs to a different entity. Choose one from the same service entity.',
    classificationManagerialInactive:
      'One of the selected managerial accounts is inactive. Replace it with an active account to save the service.',
    classificationManagerialNotLeaf:
      'The classification uses a summary managerial account. Select a leaf managerial account.',
    classificationManagerialNotEffective:
      'One of the selected managerial accounts is not yet effective. Choose another account or adjust the configuration.',
    classificationManagerialExpired:
      'One of the selected managerial accounts is no longer effective. Choose a currently effective account to save the service.',
    classificationSegmentNotFound:
      'One of the classification segments was not found. Review the selection and try again.',
    classificationSegmentNotPredefined:
      'The selected segment does not allow predefined values. Review the segment setup before saving.',
    classificationSegmentValueNotFound:
      'One of the classification segment values was not found. Choose a valid value and try again.',
    invalidUuid:
      'The {{field}} field has an invalid value. Select a valid option again before saving.',
    required: 'Fill in the {{field}} field before saving the service.',
    maxLength:
      'The {{field}} field allows at most {{count}} characters. Adjust the value and try again.',
    minLength:
      'The {{field}} field must have at least {{count}} characters. Complete the value and try again.',
    enum: 'The {{field}} field received an invalid value. Choose one of the available options and try again.',
    string: 'The {{field}} field must be valid text. Review the entered value and try again.',
    number: 'The {{field}} field must be a valid number. Review the entered value and try again.',
    boolean:
      'The {{field}} field received an invalid value. Make the selection again and try again.',
    array: 'The {{field}} list is in an invalid format. Review the entered items and try again.',
    pattern: 'The {{field}} field is in an invalid format. Correct the value and try again.',
    issRateMax: 'The ISS rate cannot be greater than 100%. Adjust the percentage and try again.',
  },
  validation: {
    floorPriceHigherThanBaseFee: 'Floor price cannot be higher than the base fee',
    // Proposed copy, not yet approved by the client.
    proposedRequired: {
      entityId: 'Enter the entity',
      name: 'Enter the name',
      baseFee: 'Enter the base fee when there is no usage-based pricing',
      claveProdServ: 'Enter the product/service key (SAT)',
      serviceResultCountryCode: 'Enter a foreign country for the service result',
      cnaeCode: 'Enter the CNAE',
      issMunicipalityIbgeCode: 'Enter the ISS municipality',
      municipalServiceCode: 'Enter the municipal code',
      issRate: 'Enter the ISS rate',
      nbsCode: 'Enter the service classification (NBS)',
      cClassTrib: 'Enter the tax classification',
      cIndOp: 'Enter the operation type',
      glRevenueAccount: 'Enter the revenue account',
      managerialAccount: 'Enter the managerial account',
    },
    baseFeeRequired: 'Base fee is required when there is no usage-based pricing',
    gtvRateRequired: 'Enter the percent of GTV (between 0 and 100)',
    cnaeRequired: 'CNAE is required',
    issMunicipalityRequired: 'ISS municipality is required',
    municipalServiceCodeRequired: 'Municipal service code is required for municipal NFSe emission',
    serviceCodeRequired: 'Fiscal service is required',
    fiscalServiceTypeRequired: 'Fiscal service type is required',
    issRateRequired: 'ISS rate is required',
    issRateMaxExceeded: 'ISS rate cannot exceed 100%',
    serviceResultCountryRequired: 'Select a foreign country where the service result occurred.',
    glRevenueAccountRequired: 'Revenue account (GL) is required',
    managerialAccountRequired: 'Managerial account is required',
    nbsCodeRequired: 'Service classification (NBS) is required for NFSe emission',
    claveProdServRequired: 'The product/service key (SAT) is required for CFDI emission (Mexico)',
    nbsLeafCodeRequired:
      'Select a full leaf NBS code (format 1.XXXX.XX.XX, e.g. 1.1302.21.00). Intermediate codes like 1.1302.2 are not accepted for NFSe.',
    cClassTribRequired: 'Tax classification is required for NFSe emission',
    cClassTribInvalid: 'Select an active IBS/CBS tax classification from the list.',
    cIndOpRequired: 'Operation type is required for NFSe emission',
    cIndOpInvalid: 'Select a current operation indicator from the list.',
    requiredFieldsTitle: 'Required fields are missing',
    requiredFieldsDescription: 'Fix the fields highlighted in red before saving the service:',
    requiredFieldsConfirm: 'Got it',
  },
  saasMetrics: {
    title: 'SaaS metrics',
    subtitle:
      'Committed MRR/ARR, forecast with projected usage, suspended-MRR bucket, and period movements',
    asOf: 'As of',
    usageLookback: 'Usage lookback: {{count}} periods',
    loading: 'Loading metrics…',
    emDash: '—',
    multiCurrencyNote: 'Totals by currency (contracts span more than one currency)',
    thresholdLabel: 'Suspension → churn threshold (days)',
    thresholdHelp:
      'Suspended contracts past this threshold convert suspended value to churn in period movements.',
    saveThreshold: 'Save threshold',
    thresholdSaved: 'Threshold updated',
    cards: {
      committedMrr: 'Committed MRR',
      committedArr: 'Committed ARR',
      forecastMrr: 'Forecast MRR',
      forecastArr: 'Forecast ARR',
      forecastConfidence: 'Usage confidence: {{confidence}}',
      suspendedMrr: 'Suspended MRR',
    },
    usageConfidence: {
      HIGH: 'High',
      LOW: 'Low',
      UNAVAILABLE: 'Unavailable',
    },
    empty: 'No active, suspended, or projected-usage recurring value.',
    period: {
      title: 'Period movements',
      from: 'From',
      to: 'To',
      loading: 'Loading movements…',
      empty: 'No movements in this period.',
      expansion: 'Expansion',
      contraction: 'Contraction',
      churn: 'Churn',
      suspension: 'Suspension',
      reactivation: 'Reactivation',
      committedImpact: 'Committed MRR impact',
      committedFrom: 'MRR at start',
      committedTo: 'MRR at end',
      table: {
        date: 'Date',
        type: 'Type',
        cause: 'Cause',
        contract: 'Contract',
        amount: 'Monthly amount',
        impact: 'Impact',
        trace: 'Trace',
        openContract: 'Open',
      },
    },
    movementType: {
      EXPANSION: 'Expansion',
      CONTRACTION: 'Contraction',
      CHURN: 'Churn',
      SUSPENSION: 'Suspension',
      REACTIVATION: 'Reactivation',
    },
    movementCause: {
      ACTIVATION: 'Activation',
      AMENDMENT: 'Amendment',
      RENEWAL: 'Renewal',
      STATUS_TRANSITION: 'Status',
      SUSPENSION_THRESHOLD: 'Suspension threshold',
    },
    sources: {
      title: 'Source by contract',
      contract: 'Contract',
      version: 'Version',
      status: 'Status',
      frequency: 'Frequency',
      periodAmount: 'Cycle amount (committed)',
      monthlyAmount: 'Monthly (committed)',
      projectedUsage: 'Projected usage (monthly)',
      usageConfidence: 'Usage confidence',
      usageObservations: 'Observations',
      suspensionDays: 'Days suspended',
      pastThreshold: 'Past threshold',
      yes: 'Yes',
      no: 'No',
      openContract: 'Open',
    },
    status: {
      ACTIVE: 'Active',
      SUSPENDED: 'Suspended',
    },
  },
  arForecast: {
    title: 'AR forecast',
    subtitle:
      'Expected receipts per installment as of {{referenceDate}} — gross and risk-adjusted. Forecast only: no accounting entries are created.',
    loadError: 'Could not load the AR forecast.',
    empty: 'No open installments to forecast.',
    totals: {
      gross: 'Gross',
      riskAdjusted: 'Risk-adjusted',
      pastDue: 'Past due (gross)',
      unspecifiedCurrency: 'No currency set',
    },
    tabs: {
      all: 'All',
      pastDue: 'Past due',
      future: 'Upcoming',
    },
    emptyTab: 'No installments in this tab.',
    filters: {
      entityPlaceholder: 'All entities',
      entityAll: 'All entities',
      customerPlaceholder: 'All customers',
      sourceLabel: 'Source',
      sourceAll: 'All sources',
      sourceExisting: 'Existing AR',
      sourceProjected: 'Fixed-cycle projection',
      sourceRenewal: 'Renewal assumed',
      probabilityAll: 'All probabilities',
      probabilitySelected: '{{count}} ranges selected',
      probability: {
        '0_25': '0% to 24%',
        '25_50': '25% to 49%',
        '50_75': '50% to 74%',
        '75_100': '75% to 100%',
      },
    },
    activeFilters: {
      label: 'Active filters',
      clear: 'Clear all',
      entity: 'Entity',
      customer: 'Customer',
      source: 'Source',
      probability: 'Probability',
    },
    pagination: {
      showing: 'Showing {{from}}–{{to}} of {{total}}',
      page: 'Page {{page}} of {{totalPages}}',
      perPage: 'Items per page',
      summary: 'Page {{page}} of {{totalPages}} · {{totalCount}} results',
      pageSizeLabel: 'Items per page',
    },
    table: {
      customer: 'Customer',
      installment: 'Installment',
      contract: 'Contract',
      projected: 'Projected',
      renewalAssumed: 'Renewal assumed',
      fixedPortion: 'Fixed',
      usagePortion: 'Usage',
      usageHigh: 'Usage · high confidence',
      usageLow: 'Usage · low confidence',
      usageUnavailable: 'Usage · unavailable',
      dueDate: 'Due date',
      expectedCashDate: 'Expected receipt',
      expectedCashHint: 'Estimated from collection behavior ({{source}}, {{lag}}d)',
      daysPastDue: '{{days}}d past due',
      gross: 'Gross',
      riskAdjusted: 'Risk-adjusted',
      probability: 'Collection prob.',
      probabilityHint: {
        formula: '{{gross}} × {{percent}}% = {{riskAdjusted}} risk-adjusted.',
        basis: {
          CUSTOMER:
            "The customer's own history: collected ÷ billed over the {{sampleSize}} most recent invoices due more than {{maturityDays}} days ago.",
          BILLING_GROUP:
            'No usable history for this customer alone (minimum {{minSamples}} invoices due more than {{maturityDays}} days ago). Basis: billing group, {{sampleSize}} invoices.',
          TENANT:
            'No usable history for the customer or the billing group. Basis: entity, {{sampleSize}} invoices.',
          NONE: 'No invoice due more than {{maturityDays}} days ago in history. No evidence for a haircut — assumes 100%.',
        },
        zero: '0% means nothing was collected on those invoices — not a lack of history.',
        confidence: {
          HIGH: 'High confidence.',
          LOW: 'Low confidence.',
        },
      },
    },
  },
};
